// Website Structure and Mock Data for Infotech.Works

import {
  // Industry Icons
  Zap,
  Stethoscope,
  Banknote,
  ShoppingBag,
  Car,
  ShoppingCart,
  Wrench,
  Truck,
  TrendingUp,
  GraduationCap,
  Leaf,
  Scale,
  Network,
  Trophy,
  Sun,
  Dumbbell,
  Heart,
  Shield,
  Briefcase,
  Brain,
} from "lucide-react";
import { TEAM_IMAGES } from "./teamImages";

export const COMPANY_INFO = {
  name: "Infotech.Works",
  tagline: "Transforming Ideas into Digital Reality",
  description:
    // "We develop AI, mobile and web applications for startups, SME and enterprise clients.",
    "A product engineering studio building AI-native mobile and web products for startups, growth companies and enterprise teams.",
  logo: "/images/itw-logo-white-bg.png",
  email: "contact@infotech.works",
  careersOpportunitiesEmail: "careers@infotech.works",
  phone: "+1 (555) 123-4567",
  address:
    "Infotech.Works, Lane 5A, Laxman Nagar, Baner, Pune, Maharashtra 411045",
  social: {
    linkedin: "https://www.linkedin.com/company/infotechworks",
    twitter: "https://twitter.com/infotechworks",
    facebook: "https://facebook.com/infotechworks",
    github: "https://github.com/infotechworks",
    instagram: "https://www.instagram.com/infotechworks/",
  },
};

export const HERO_SLIDES = [
  {
    id: 1,
    title: "We Build Products That Scale",
    subtitle:
      "A product engineering studio for founders, product teams and enterprises who need more than a dev shop.",
    image:
      "https://images.unsplash.com/photo-1677442135703-1787eea5ce01?w=1920&q=80",
    cta: "Explore AI Services",

    link: "/what-we-do",
  },
  {
    id: 2,
    title: "AI-Native From Day One",
    subtitle:
      "We don't bolt AI onto existing products. We design intelligence into everything we build.",
    image:
      "https://images.unsplash.com/photo-1610563166150-b34df4f3bcd6?w=1920&q=80",
    cta: "View Services",
    link: "/ai",
  },
  {
    id: 3,
    title: "Your Team. Your Outcomes.",
    subtitle:
      "We embed with your team as a long-term engineering partner — accountable to your goals, not just your backlog.",
    image:
      "https://images.unsplash.com/photo-1644325349124-d1756b79dd42?w=1920&q=80",
    cta: "Learn More",
    link: "/what-we-do/engagement-model",
  },
  {
    id: 4,
    title: "Mobile That Users Keep",
    subtitle:
      "Combining seamless performance, usability, robust engineering, intuitive design, and real-world usability that drives long-term engagement.",
    image:
      "https://images.unsplash.com/photo-1522071820081-009f0129c71c?w=1920&q=80",
    cta: "Get Started",
    link: "/what-we-do/mobile-app-development",
  },
  {
    id: 5,
    title: "Deep Industry Roots",
    subtitle:
      "Rooted in industry experience, we create meaningful technology solutions that solve complex challenges and drive sustainable growth.",
    image:
      "https://images.unsplash.com/photo-1523961131990-5ea7c61b2107?w=1920&q=80",
    cta: "See Industries",
    link: "/industries",
  },
];

// Updated AI Services - Only 4 items
export const AI_SERVICES = [
  {
    slug: "ai-consulting",
    title: "AI Consulting",
    shortDesc:
      "Strategic guidance to maximize your AI investments and drive transformation",
    description:
      "Navigate the complex AI landscape with our expert consulting services. We help organizations identify AI opportunities, develop implementation strategies, and build roadmaps that align with business objectives while maximizing ROI.",
    features: [
      "AI Strategy Development",
      "Technology Assessment",
      "ROI Analysis",
      "Implementation Planning",
      "Risk Assessment",
    ],
    icon: "Lightbulb",
    image:
      "https://images.unsplash.com/photo-1552664730-d307ca884978?w=400&q=80",
  },
  {
    slug: "agentic-ai",
    title: "Agentic AI",
    shortDesc:
      "Autonomous AI agents that act on your behalf and automate complex tasks",
    description:
      "Build autonomous AI agents capable of complex reasoning, planning, and action. Our agentic AI solutions can handle multi-step tasks, make decisions, and interact with various tools and APIs to achieve business objectives.",
    features: [
      "Multi-Agent Systems",
      "Tool Integration",
      "Autonomous Decision Making",
      "Task Planning",
      "Goal-Oriented Behavior",
    ],
    icon: "Bot",
    image:
      "https://images.unsplash.com/photo-1485827404703-89b55fcc595e?w=400&q=80",
  },
  {
    slug: "ai-products",
    title: "AI Products",
    shortDesc:
      "End-to-end AI product development from concept to market launch",
    description:
      "Transform your AI ideas into market-ready products. We handle the entire product development lifecycle, from ideation and prototyping to development, testing, and deployment of AI-powered products.",
    features: [
      "Product Strategy",
      "MVP Development",
      "Iterative Design",
      "Market Validation",
      "Go-to-Market Support",
    ],
    icon: "Package",
    image:
      "https://images.unsplash.com/photo-1559028012-481c04fa702d?w=400&q=80",
  },
  {
    slug: "smart-ai-chatbot",
    title: "Smart AI Chatbot",
    shortDesc:
      "Intelligent conversational AI for superior customer experiences",
    description:
      "Create sophisticated chatbots and virtual assistants powered by advanced NLP and machine learning. Our chatbots provide 24/7 customer support, handle complex queries, and seamlessly integrate with your existing systems.",
    features: [
      "Multi-language Support",
      "Intent Recognition",
      "Context Awareness",
      "CRM Integration",
      "Analytics Dashboard",
    ],
    icon: "MessageSquare",
    image:
      "https://images.unsplash.com/photo-1531746790731-6c087fecd65a?w=400&q=80",
  },
];

// Engagement Models for "What We Do" menu
export const ENGAGEMENT_MODELS = [
  {
    slug: "fixed-price",
    title: "Fixed Price",
    anchor: "fixed-price",
    shortDesc: "Predictable costs with well-defined scope and deliverables",
    description:
      "Ideal for projects with clear requirements and defined scope. Get budget certainty with milestone-based delivery.",
    image:
      "https://images.unsplash.com/photo-1554224155-6726b3ff858f?w=400&q=80",
  },
  {
    slug: "fixed-team",
    title: "Fixed Team",
    anchor: "fixed-team",
    shortDesc: "Dedicated team working exclusively on your project",
    description:
      "A committed team that becomes an extension of your organization, fully aligned with your goals and culture.",
    image:
      "https://images.unsplash.com/photo-1522071820081-009f0129c71c?w=400&q=80",
  },
  {
    slug: "time-and-material",
    title: "Time & Material",
    anchor: "time-and-material",
    shortDesc: "Flexible engagement for evolving project requirements",
    description:
      "Maximum flexibility for projects with changing scope. Pay for actual time and resources used.",
    image:
      "https://images.unsplash.com/photo-1553877522-43269d4ea984?w=400&q=80",
  },
];

// Service Bundles for "What We Do" menu
export const SERVICE_BUNDLES = [
  {
    slug: "ai-studio",
    title: "AI Studio",
    shortDesc:
      "Comprehensive platform for AI experimentation and rapid prototyping",
    description:
      "Access our AI Studio platform for rapid prototyping, model training, and experimentation. Collaborate with our AI experts to explore possibilities and validate concepts before full-scale development.",
    features: [
      "Jupyter Notebooks",
      "Pre-built Templates",
      "Model Training",
      "Visualization Tools",
      "Collaboration Features",
    ],
    icon: "FlaskConical",
    image:
      "https://images.unsplash.com/photo-1518770660439-4636190af475?w=400&q=80",
  },
  {
    slug: "dedicated-capability-centre",
    title: "Dedicated Capability Centre",
    shortDesc:
      "Your dedicated team extension working exclusively on your projects",
    description:
      "Establish a dedicated capability center with our expert team working exclusively on your projects. Get the benefits of an in-house team without the overhead of building one from scratch.",
    features: [
      "Dedicated Team",
      "Knowledge Transfer",
      "Continuous Improvement",
      "Scalable Resources",
      "24/7 Support",
    ],
    icon: "Building2",
    image:
      "https://images.unsplash.com/photo-1497366216548-37526070297c?w=400&q=80",
  },
  {
    slug: "startup-lab",
    title: "Startup Lab",
    shortDesc: "Innovation hub for startups with mentorship and resources",
    description:
      "Our Startup Lab provides emerging companies with access to expertise, resources, and mentorship. We help startups validate ideas, build MVPs, and scale products to market.",
    features: [
      "Mentorship Program",
      "Technical Support",
      "Funding Guidance",
      "Network Access",
      "Rapid Prototyping",
    ],
    icon: "Rocket",
    image:
      "https://images.unsplash.com/photo-1559136555-9303baea8ebd?w=400&q=80",
  },
  {
    slug: "product-engineering",
    title: "Product Engineering",
    shortDesc: "End-to-end product development from concept to market launch",
    description:
      "Complete product engineering services from ideation to deployment. We build scalable, maintainable products using modern technologies and best practices.",
    features: [
      "Full-Stack Development",
      "Cloud-Native Apps",
      "Microservices",
      "DevOps Integration",
      "Continuous Delivery",
    ],
    icon: "Cog",
    image:
      "https://images.unsplash.com/photo-1559028012-481c04fa702d?w=400&q=80",
  },
];

// Individual Services for "What We Do" menu
export const INDIVIDUAL_SERVICES = [
  {
    slug: "mobile-app-development",
    title: "Mobile App Development",
    shortDesc: "Native and cross-platform mobile excellence",
    description:
      "Create mobile applications that users love. Whether native iOS/Android or cross-platform solutions, we build performant, beautiful apps that deliver exceptional mobile experiences.",
    features: [
      "iOS Development",
      "Android Development",
      "React Native",
      "Flutter",
      "App Store Optimization",
    ],
    icon: "Smartphone",
    image:
      "https://images.unsplash.com/photo-1512941937669-90a1b58e7e9c?w=400&q=80",
  },
  {
    slug: "web-app-development",
    title: "Web App Development",
    shortDesc: "Modern web solutions that perform and scale",
    description:
      "Build powerful web applications and websites that engage users and drive results. Our web development services cover everything from responsive websites to complex web applications.",
    features: [
      "Frontend Development",
      "Backend Development",
      "CMS Integration",
      "E-commerce",
      "Progressive Web Apps",
    ],
    icon: "Globe",
    image:
      "https://images.unsplash.com/photo-1547658719-da2b51169166?w=400&q=80",
  },
  {
    slug: "api-development",
    title: "API Development",
    shortDesc: "Robust APIs that power your ecosystem",
    description:
      "Design and develop APIs that enable seamless communication between applications, services, and partners. Our APIs are secure, well-documented, and built for scalability.",
    features: [
      "REST & GraphQL",
      "API Gateway",
      "Authentication",
      "Rate Limiting",
      "Documentation",
    ],
    icon: "Share2",
    image:
      "https://images.unsplash.com/photo-1516321318423-f06f85e504b3?w=400&q=80",
  },
  {
    slug: "iot-app-development",
    title: "IoT App Development",
    shortDesc: "Connected solutions for the smart world",
    description:
      "Build applications that connect physical devices to the digital world. Our IoT development services cover device connectivity, data collection, cloud platforms, and intelligent analytics.",
    features: [
      "Device Integration",
      "Edge Computing",
      "Cloud Platforms",
      "Real-time Monitoring",
      "Predictive Maintenance",
    ],
    icon: "Wifi",
    image:
      "https://images.unsplash.com/photo-1518770660439-4636190af475?w=400&q=80",
  },
  {
    slug: "custom-integrations",
    title: "Custom Integrations",
    shortDesc: "Connect your systems seamlessly",
    description:
      "Break down data silos and enable seamless communication between your applications. Our integration experts connect disparate systems, APIs, and platforms to create unified, efficient workflows.",
    features: [
      "API Integration",
      "ETL Pipelines",
      "Middleware Development",
      "Real-time Sync",
      "Data Mapping",
    ],
    icon: "Link",
    image:
      "https://images.unsplash.com/photo-1558494949-ef010cbdcc31?w=400&q=80",
  },
  {
    slug: "it-support-services",
    title: "IT Support Services",
    shortDesc: "24/7 support to keep you running",
    description:
      "Ensure your IT systems run smoothly with our comprehensive support services. From helpdesk to infrastructure management, we provide reliable support that minimizes downtime and maximizes productivity.",
    features: [
      "24/7 Helpdesk",
      "Remote Support",
      "Infrastructure Management",
      "Security Monitoring",
      "Disaster Recovery",
    ],
    icon: "Headphones",
    image:
      "https://images.unsplash.com/photo-1553877522-43269d4ea984?w=400&q=80",
  },
  {
    slug: "product-management-advisory",
    title: "Product Management Advisory",
    shortDesc: "Strategic guidance for product success",
    description:
      "Get expert advice on product strategy, roadmap planning, and market positioning. Our product management consultants help you make data-driven decisions that drive product success.",
    features: [
      "Product Strategy",
      "Roadmap Planning",
      "Market Analysis",
      "Feature Prioritization",
      "Stakeholder Management",
    ],
    icon: "LineChart",
    image:
      "https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=400&q=80",
  },
  {
    slug: "experience-design",
    title: "Experience Design",
    shortDesc: "User-centered design that delights",
    description:
      "Create exceptional digital experiences through research-driven design. Our UX/UI designers craft intuitive interfaces that enhance user satisfaction, drive engagement, and support business goals.",
    features: [
      "User Research",
      "UI/UX Design",
      "Prototyping",
      "Usability Testing",
      "Design Systems",
    ],
    icon: "Palette",
    image:
      "https://images.unsplash.com/photo-1561070791-2526d30994b5?w=400&q=80",
  },
  {
    slug: "quality-assurance-testing",
    title: "Quality Assurance and Testing",
    shortDesc: "Ensure flawless software delivery",
    description:
      "Deliver bug-free software with our comprehensive QA and testing services. From manual testing to automated test suites, we ensure your applications meet the highest quality standards.",
    features: [
      "Manual Testing",
      "Automated Testing",
      "Performance Testing",
      "Security Testing",
      "Mobile Testing",
    ],
    icon: "CheckCircle",
    image:
      "https://images.unsplash.com/photo-1576091160550-2173dba999ef?w=400&q=80",
  },
];

// Updated Industries - 20 industries as per requirements
export const INDUSTRIES = [
  {
    slug: "energy",
    title: "Energy",
    description:
      "Energy management, smart grid solutions, and renewable energy platforms",
    icon: "Zap",
    image:
      "https://images.unsplash.com/photo-1473341304170-971dccb5ac1e?w=400&q=80",
  },
  {
    slug: "healthcare-medical",
    title: "Healthcare / Medical",
    description: "Health management, telemedicine, and medical record systems",
    icon: "Stethoscope",
    image:
      "https://images.unsplash.com/photo-1576091160399-112ba8d25d1d?w=400&q=80",
  },
  {
    slug: "fintech-finance",
    title: "FinTech / Finance",
    description:
      "Banking apps, payment solutions, and financial management platforms",
    icon: "DollarSign",
    image:
      "https://images.unsplash.com/photo-1563986768494-4dee2763ff3f?w=400&q=80",
  },
  {
    slug: "retail",
    title: "Retail",
    description: "Point of sale, inventory management, and retail analytics",
    icon: "Store",
    image:
      "https://images.unsplash.com/photo-1441986300917-64674bd600d8?w=400&q=80",
  },
  {
    slug: "transport",
    title: "Transport",
    description: "Transportation apps, ride-sharing, and fleet management",
    icon: "Car",
    image:
      "https://images.unsplash.com/photo-1449824913935-59a10b8d2000?w=400&q=80",
  },
  {
    slug: "artificial-intelligence",
    title: "Artificial Intelligence",
    description: "AI-native products and platforms for the AI industry",
    icon: "Brain",
    image:
      "https://images.unsplash.com/photo-1677691824304-279660ceece3?w=400&q=80",
  },
  {
    slug: "ecommerce-gig-commerce",
    title: "e-Commerce / Gig-commerce",
    description:
      "Online marketplaces, shopping platforms, and gig economy solutions",
    icon: "ShoppingCart",
    image:
      "https://images.unsplash.com/photo-1563013544-824ae1b704d3?w=400&q=80",
  },
  {
    slug: "utility",
    title: "Utility",
    description: "Utility management, smart metering, and service platforms",
    icon: "Wrench",
    image:
      "https://images.unsplash.com/photo-1581094794329-c8112c4e5190?w=400&q=80",
  },
  {
    slug: "logistics",
    title: "Logistics",
    description:
      "Supply chain management, fleet tracking, and delivery platforms",
    icon: "Truck",
    image:
      "https://images.unsplash.com/photo-1586528116311-ad8dd3c8310d?w=400&q=80",
  },
  {
    slug: "sales-marketing",
    title: "Sales & Marketing",
    description: "CRM systems, marketing automation, and sales enablement",
    icon: "TrendingUp",
    image:
      "https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=400&q=80",
  },
  {
    slug: "security",
    title: "Security",
    description: "Cybersecurity, surveillance systems, and access control",
    icon: "Shield",
    image:
      "https://images.unsplash.com/photo-1555949963-ff9fe0c870eb?w=400&q=80",
  },
  {
    slug: "edtech-education-training",
    title: "EdTech / Education / Training",
    description:
      "Learning management systems, e-learning platforms, and training solutions",
    icon: "GraduationCap",
    image:
      "https://images.unsplash.com/photo-1501504905252-473c47e087f8?w=400&q=80",
  },
  {
    slug: "agritech",
    title: "AgriTech",
    description:
      "Smart farming solutions, precision agriculture, and farm management systems",
    icon: "Leaf",
    image:
      "https://images.unsplash.com/photo-1625246333195-78d9c38ad449?w=400&q=80",
  },
  {
    slug: "jobs-hr-tech",
    title: "Jobs / HR Tech",
    description:
      "Job portals, recruitment platforms, and HR management systems",
    icon: "Briefcase",
    image:
      "https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?w=400&q=80",
  },
  {
    slug: "legal",
    title: "Legal",
    description: "Legal tech, case management, and contract automation",
    icon: "Scale",
    image:
      "https://images.unsplash.com/photo-1589829545856-d10d557cf95f?w=400&q=80",
  },
  {
    slug: "networking-business",
    title: "Networking & Business",
    description:
      "Professional networking, community platforms, and business connectivity",
    icon: "Network",
    image:
      "https://images.unsplash.com/photo-1557804506-669a67965ba0?w=400&q=80",
  },
  {
    slug: "sports",
    title: "Sports",
    description:
      "Sports analytics, team management, and fan engagement platforms",
    icon: "Trophy",
    image:
      "https://images.unsplash.com/photo-1461896836934-28f72d37c6f0?w=400&q=80",
  },
  {
    slug: "lifestyle",
    title: "Lifestyle",
    description:
      "Lifestyle apps, social platforms, and personal management tools",
    icon: "Sun",
    image:
      "https://images.unsplash.com/photo-1511988617509-a57c8a288659?w=400&q=80",
  },
  {
    slug: "fitness",
    title: "Fitness",
    description: "Fitness tracking, workout apps, and gym management systems",
    icon: "Dumbbell",
    image:
      "https://images.unsplash.com/photo-1534438327276-14e5300c3a48?w=400&q=80",
  },
  {
    slug: "charity-funding",
    title: "Charity / Funding",
    description:
      "Donation platforms, crowdfunding, and nonprofit management systems",
    icon: "Heart",
    image:
      "https://images.unsplash.com/photo-1532629345422-7515f3d16bb6?w=400&q=80",
  },
];


export const PRODUCTS_MENU = [
  {
    slug: "products",
    title: "About ScreenQube",
    shortDesc:
      "A centralised digital signage product for every industry",
    image: "/images/products/screenqube.jpg",
    path: "https://screenqube.com/",
  },
];

// Resources Menu (formerly Case Studies)
export const RESOURCES_MENU = [
  {
    slug: "case-studies",
    title: "Case Studies",
    shortDesc:
      "Explore our successful project implementations and client success stories",
    image:
      "https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=400&q=80",
    path: "/resources/case-studies",
  },
  {
    slug: "blogs",
    title: "Blogs",
    shortDesc: "Insights, trends, and thought leadership from our experts",
    image:
      "https://images.unsplash.com/photo-1499750310107-5fef28a66643?w=400&q=80",
    path: "/resources/blogs",
  },
];

// About Menu - Updated (removed Testimonials, added Life @ Infotech.Works)
export const ABOUT_MENU = [
  {
    slug: "company",
    title: "About Company",
    shortDesc:
      "Learn about our mission, vision, and the values that drive us forward",
    image:
      "https://images.unsplash.com/photo-1497366216548-37526070297c?w=400&q=80",
    path: "/about/company",
  },
  {
    slug: "leadership",
    title: "Leadership Team",
    shortDesc: "Meet the experienced leaders guiding our company to success",
    image:
      "https://images.unsplash.com/photo-1522071820081-009f0129c71c?w=400&q=80",
    path: "/about/leadership",
  },
  {
    slug: "partners",
    title: "Partner With Us",
    shortDesc: "Explore partnership opportunities and grow together with us",
    image:
      "https://images.unsplash.com/photo-1557804506-669a67965ba0?w=400&q=80",
    path: "/about/partners",
  },
  {
    slug: "life-at-infotech",
    title: "Life @ Infotech.Works",
    shortDesc:
      "Discover our culture, values, and what makes us a great place to work",
    image:
      "https://images.unsplash.com/photo-1522202176988-66273c2fd55f?w=400&q=80",
    path: "/about/life-at-infotech",
  },
];

export const CASE_STUDIES = [
  {
    id: 7,
    slug: "startup-review-ecosystem",
    title: "Startup Review Ecosystem",
    tagline: "Shark Tank on App - Democratizing Startup Reviews",
    clientType: "Startup Incubator / Venture Platform",
    industry: "Networking & Business",
    industrySlug: "networking-business",
    services: ["Mobile App Development", "Experience Design"],
    servicesSlugs: ["mobile-app-development", "experience-design"],
    timeline: "45 Days",
    technologies: [
      "React Native",
      "PHP",
      "Laravel",
      "AWS",
      "Firebase",
      "MySQL",
    ],
    challenge:
      "Create a platform to democratize startup reviews and make the investment process transparent and live for all audiences. Founders needed a way to submit pitches via video stories, while experts required tools to perform preliminary reviews and help founders perfect their pitch before meeting investors.",
    solution:
      "We developed a multi-role mobile application that ensures media, reviews, ratings, and rewards are well maintained with an intuitive UI. The platform connects founders, experts, investors, and viewers in a seamless ecosystem.",
    features: [
      "Multiple user roles (Founders, Experts, Investors, Viewers)",
      "Video pitch submission with story format",
      "Swipe feed for browsing startup pitches",
      "Expert review and rating system",
      "Push notifications for real-time updates",
      "Responsive animations and transitions",
      "Social networking features",
      "Reward and recognition system",
    ],
    results: [
      {
        value: "Scalable",
        label: "Architecture",
        description: "Supports large video file uploads",
      },
      {
        value: "1500+",
        label: "Startup Pitches",
        description: "Video pitches submitted on the platform",
      },
      {
        value: "2000+",
        label: "Expert Reviews",
        description: "Reviews completed by industry experts",
      },
      // {
      //   value: "85%",
      //   label: "User Engagement",
      //   description: "Daily active user retention rate",
      // },
      {
        value: "45",
        label: "Days to Launch",
        description: "From concept to app store deployment",
      },
      {
        value: "4.8/5",
        label: "Client Statisfaction",
        description: "Rated by founders and investors",
      },
    ],
    testimonial: {
      quote:
        "The exceptional experience with multi-role functionality and visually appealing design of the app reflects a deep understanding of user needs, making it both visually appealing and highly functional.",
      author: "Project Head",
      role: "Startup Incubator",
    },
    image:
      "https://images.unsplash.com/photo-1559136555-9303baea8ebd?w=800&q=80",
    heroImage:
      "https://images.unsplash.com/photo-1553877522-43269d4ea984?w=1920&q=80",
    featured: false,
  },
  {
    id: 1,
    slug: "ai-powered-healthcare-platform",
    title: "AI-Powered Healthcare Platform",
    tagline: "Transforming Diagnostics with Artificial Intelligence",
    clientType: "Healthcare Technology Company",
    industry: "Healthcare / Medical",
    industrySlug: "healthcare-medical",
    services: ["AI Consulting", "Web App Development"],
    servicesSlugs: ["ai-consulting", "web-app-development"],
    timeline: "6 Months",
    technologies: [
      "MCP",
      "LLM",
      "Vector DB",
      "Python",
      "TensorFlow",
      "React",
      "Node.js",
      "AWS",
      "PostgreSQL",
    ],
    challenge: "Needed to reduce diagnosis time and improve accuracy using AI",
    solution: "Developed an AI diagnostic assistant using deep learning models",
    features: [
      "AI-powered diagnostics",
      "Real-time analysis",
      "Patient management",
      "Integration with medical devices",
    ],
    results: [
      {
        value: "40%",
        label: "Faster Diagnosis",
        description: "Reduction in diagnosis time",
      },
      {
        value: "92%",
        label: "Accuracy Rate",
        description: "AI model prediction accuracy",
      },
      {
        value: "50%",
        label: "Cost Reduction",
        description: "Operational cost savings",
      },
    ],
    testimonial: {
      quote:
        "The AI solution has revolutionized how we approach diagnostics. It's faster, more accurate, and has significantly improved patient outcomes.",
      author: "Chief Medical Officer",
      role: "Healthcare Technology Company",
    },
    image:
      "https://images.unsplash.com/photo-1576091160399-112ba8d25d1d?w=800&q=80",
    heroImage:
      "https://images.unsplash.com/photo-1576091160399-112ba8d25d1d?w=1920&q=80",
    featured: true,
  },
  {
    id: 2,
    slug: "ecommerce-marketplace-transformation",
    title: "E-Commerce Marketplace Transformation",
    tagline: "Scaling Retail for the Digital Age",
    clientType: "Retail Corporation",
    industry: "e-Commerce / Gig-commerce",
    industrySlug: "ecommerce-gig-commerce",
    services: ["Web App Development", "API Development"],
    servicesSlugs: ["web-app-development", "api-development"],
    timeline: "8 Months",
    technologies: [
      "MCP",
      "LLM",
      "Vector DB",
      "React",
      "Node.js",
      "MongoDB",
      "Redis",
      "AWS",
      "Elasticsearch",
    ],
    challenge: "Legacy platform couldn't scale to meet growing demand",
    solution:
      "Built a modern microservices-based marketplace with AI recommendations",
    features: [
      "Microservices architecture",
      "AI recommendations",
      "Real-time inventory",
      "Multi-vendor support",
    ],
    results: [
      {
        value: "300%",
        label: "Traffic Capacity",
        description: "Increase in traffic handling",
      },
      {
        value: "25%",
        label: "Higher Conversion",
        description: "Improvement in sales conversion",
      },
      { value: "99.9%", label: "Uptime", description: "Platform availability" },
    ],
    testimonial: {
      quote:
        "The new platform handles 3x the traffic with better performance. Our conversion rates have never been higher.",
      author: "VP of Technology",
      role: "Retail Corporation",
    },
    image:
      "https://images.unsplash.com/photo-1563013544-824ae1b704d3?w=800&q=80",
    heroImage:
      "https://images.unsplash.com/photo-1563013544-824ae1b704d3?w=1920&q=80",
    featured: false,
  },
  {
    id: 3,
    slug: "smart-farming-iot-solution",
    title: "Smart Farming IoT Solution",
    tagline: "Precision Agriculture for Modern Farms",
    clientType: "Agricultural Enterprise",
    industry: "AgriTech",
    industrySlug: "agritech",
    services: ["IOT App Development", "Mobile App Development"],
    servicesSlugs: ["iot-app-development", "mobile-app-development"],
    timeline: "5 Months",
    technologies: [
      "React Native",
      "Node.js",
      "AWS IoT",
      "Python",
      "PostgreSQL",
      "MQTT",
    ],
    challenge: "Inefficient crop management and resource wastage",
    solution:
      "Implemented IoT sensors with AI-driven analytics for precision farming",
    features: [
      "IoT sensor integration",
      "Real-time monitoring",
      "Predictive analytics",
      "Automated irrigation",
    ],
    results: [
      {
        value: "30%",
        label: "Water Savings",
        description: "Reduction in water usage",
      },
      {
        value: "20%",
        label: "Yield Increase",
        description: "Improvement in crop yield",
      },
      {
        value: "24/7",
        label: "Monitoring",
        description: "Real-time farm visibility",
      },
    ],
    testimonial: {
      quote:
        "The IoT solution has transformed how we manage our farms. We've seen significant savings and better yields.",
      author: "Operations Director",
      role: "Agricultural Enterprise",
    },
    image:
      "https://images.unsplash.com/photo-1625246333195-78d9c38ad449?w=800&q=80",
    heroImage:
      "https://images.unsplash.com/photo-1625246333195-78d9c38ad449?w=1920&q=80",
    featured: false,
  },
  {
    id: 10,
    slug: "ticket-scanning-fansfirst",
    title: "NFC Ticket Scanning App",
    tagline: "Revolutionizing Stadium Entry with Secure NFC Technology",
    clientType: "Sports & Entertainment Company",
    industry: "Sports",
    industrySlug: "sports",
    services: ["Mobile App Development", "IOT App Development"],
    servicesSlugs: ["mobile-app-development", "iot-app-development"],
    timeline: "3 Months",
    technologies: [
      "React Native",
      "NFC",
      "Node.js",
      "AWS",
      "Firebase",
      "JANAM Scanner SDK",
    ],
    challenge:
      "Build an app to scan custom NFC tickets for Fans First games with real-time event stats for sales, capacity, attendance per section, and ticket plans at baseball venues. The upgrade to NFC tickets was needed to combat ticket misuse by introducing non-shareable, cloud-bound NFC tickets ensuring secure transfers.",
    solution:
      "Developed a React Native iOS and Android app built for the Fans First ticketing team with support for JANAM scanner devices, 1D/2D barcode scanning, QR code scanning through mobile camera, and secure, fast scanning capabilities. The app provides real-time event statistics and attendance tracking.",
    features: [
      "JANAM scanner device support with NFC",
      "1D/2D Barcode scanning",
      "QR code scanning via mobile camera",
      "Real-time event statistics",
      "Capacity and attendance tracking",
      "Section-wise attendance reports",
      "Secure cloud-bound tickets",
      "Non-shareable ticket validation",
    ],
    results: [
      {
        value: "99.9%",
        label: "Scan Accuracy",
        description: "Reliable ticket validation rate",
      },
      {
        value: "< 2 sec",
        label: "Scan Speed",
        description: "Average time per ticket scan",
      },
      {
        value: "95%",
        label: "Fraud Reduction",
        description: "Decrease in ticket misuse incidents",
      },
      {
        value: "5M+",
        label: "Tickets Scanned",
        description: "Per event ticket processing",
      },
    ],
    testimonial: {
      quote:
        "Scanning speed was great—very, very easy to scan. Thanks to the whole team for delivering a high-quality application in record time!",
      author: "Director, Customer Relationship",
      role: "Fans First",
    },
    image:
      "https://images.unsplash.com/photo-1574629810360-7efbbe195018?w=800&q=80",
    heroImage:
      "https://images.unsplash.com/photo-1540747913346-19e32dc3e97e?w=1920&q=80",
    featured: false,
  },
  {
    id: 4,
    slug: "fintech-mobile-banking-app",
    title: "FinTech Mobile Banking App",
    tagline: "Banking Reimagined for the Digital Generation",
    clientType: "Digital Banking Startup",
    industry: "FinTech / Finance",
    industrySlug: "fintech-finance",
    services: ["Mobile App Development", "Experience Design"],
    servicesSlugs: ["mobile-app-development", "experience-design"],
    timeline: "7 Months",
    technologies: [
      "React Native",
      "Node.js",
      "PostgreSQL",
      "AWS",
      "Plaid API",
      "Stripe",
    ],
    challenge: "Create a mobile-first banking experience for millennials",
    solution:
      "Developed a feature-rich mobile banking app with biometric authentication",
    features: [
      "Biometric authentication",
      "Instant transfers",
      "Budget tracking",
      "Investment integration",
    ],
    results: [
      {
        value: "500K+",
        label: "Downloads",
        description: "App downloads in first year",
      },
      {
        value: "4.8",
        label: "Star Rating",
        description: "Average app store rating",
      },
      {
        value: "60%",
        label: "Cost Reduction",
        description: "Customer acquisition cost savings",
      },
    ],
    testimonial: {
      quote:
        "The app has exceeded all our expectations. User adoption has been phenomenal and feedback is overwhelmingly positive.",
      author: "CEO",
      role: "Digital Banking Startup",
    },
    image:
      "https://images.unsplash.com/photo-1563986768494-4dee2763ff3f?w=800&q=80",
    heroImage:
      "https://images.unsplash.com/photo-1563986768494-4dee2763ff3f?w=1920&q=80",
    featured: true,
  },
  {
    id: 5,
    slug: "edtech-learning-platform",
    title: "EdTech Learning Platform",
    tagline: "Adaptive Learning for the Modern Student",
    clientType: "Education Technology Company",
    industry: "EdTech / Education / Training",
    industrySlug: "edtech-education-training",
    services: ["Web App Development"],
    servicesSlugs: ["web-app-development", "ai-consulting"],
    timeline: "9 Months",
    technologies: [
      "React",
      "Python",
      "Django",
      "TensorFlow",
      "AWS",
      "PostgreSQL",
    ],
    challenge: "Scale online education while maintaining engagement",
    solution:
      "Built an AI-powered adaptive learning platform with gamification",
    features: [
      "Adaptive learning paths",
      "Gamification",
      "Progress tracking",
      "Live sessions",
    ],
    results: [
      {
        value: "85%",
        label: "Completion Rate",
        description: "Course completion rate",
      },
      {
        value: "2M+",
        label: "Active Users",
        description: "Monthly active learners",
      },
      {
        value: "40%",
        label: "Better Outcomes",
        description: "Improvement in learning outcomes",
      },
    ],
    testimonial: {
      quote:
        "The platform has transformed online education. Student engagement and completion rates are at an all-time high.",
      author: "Chief Learning Officer",
      role: "Education Technology Company",
    },
    image:
      "https://images.unsplash.com/photo-1501504905252-473c47e087f8?w=800&q=80",
    heroImage:
      "https://images.unsplash.com/photo-1501504905252-473c47e087f8?w=1920&q=80",
    featured: false,
  },
  {
    id: 6,
    slug: "logistics-fleet-management",
    title: "Logistics Fleet Management System",
    tagline: "Intelligent Route Optimization for Logistics",
    clientType: "Logistics Company",
    industry: "Logistics",
    industrySlug: "logistics",
    services: ["Web App Development", "Mobile App Development"],
    servicesSlugs: ["web-app-development", "mobile-app-development"],
    timeline: "6 Months",
    technologies: [
      "React",
      "React Native",
      "Node.js",
      "MongoDB",
      "Google Maps API",
      "AWS",
    ],
    challenge: "Optimize delivery routes and reduce operational costs",
    solution:
      "Implemented AI-driven route optimization with real-time tracking",
    features: [
      "Route optimization",
      "Real-time tracking",
      "Driver management",
      "Analytics dashboard",
    ],
    results: [
      {
        value: "35%",
        label: "Fuel Savings",
        description: "Reduction in fuel costs",
      },
      {
        value: "25%",
        label: "Faster Deliveries",
        description: "Improvement in delivery time",
      },
      {
        value: "100%",
        label: "Visibility",
        description: "Real-time fleet visibility",
      },
    ],
    testimonial: {
      quote:
        "The fleet management system has dramatically reduced our operational costs while improving delivery times.",
      author: "Operations Manager",
      role: "Logistics Company",
    },
    image:
      "https://images.unsplash.com/photo-1586528116311-ad8dd3c8310d?w=800&q=80",
    heroImage:
      "https://images.unsplash.com/photo-1586528116311-ad8dd3c8310d?w=1920&q=80",
    featured: false,
  },
  {
    id: 8,
    slug: "build-operate-transfer-indian-isv",
    title: "Build-Operate-Transfer Engagement",
    tagline: "Strategic Partnership Driving Mobile & Web Innovation",
    clientType: "Indian ISV / Software Company",
    industry: "Networking & Business",
    industrySlug: "networking-business",
    services: ["Dedicated Capability Centre", "Product Engineering"],
    servicesSlugs: ["dedicated-capability-centre", "product-engineering"],
    timeline: "Ongoing",
    technologies: [
      "Microsoft Fabric",
      "React",
      "Node.js",
      "Azure",
      "AI Analytics",
      "Data Modernization",
    ],
    challenge:
      "An Indian ISV at the forefront of data modernization solutions and AI-driven analytics needed to expand their mobile and web application development capabilities while maintaining their focus on core platform innovations like Microsoft Fabric.",
    solution:
      "Infotech.Works established a dedicated capability center serving as a strategic partner. By combining their business processes with our technical expertise, vision, and market knowledge, we brought high-quality, innovative products to market faster and more efficiently. This collaboration strengthened their product development pipeline and enhanced agility.",
    features: [
      "Dedicated capability center model",
      "Mobile application development",
      "Web application development",
      "AI-driven analytics integration",
      "Data modernization solutions",
      "Microsoft Fabric expertise",
      "Accelerated product delivery",
      "Knowledge transfer programs",
    ],
    results: [
      {
        value: "40%",
        label: "Faster Delivery",
        description: "Reduction in time-to-market for new products",
      },
      {
        value: "3x",
        label: "Product Output",
        description: "Increase in product development capacity",
      },
      {
        value: "100%",
        label: "Quality Score",
        description: "Consistent delivery of quality work",
      },
      {
        value: "5+",
        label: "Years Partnership",
        description: "Long-term strategic collaboration",
      },
    ],
    testimonial: {
      quote:
        "Our partnership with Infotech.Works has been truly transformational for us. Their dynamic leadership, clear product vision, and collaborative spirit have inspired our team to push the boundaries of mobile and web app innovation. Together, we are building products that not only meet but exceed industry standards.",
      author: "CEO",
      role: "Indian ISV",
    },
    image:
      "https://images.unsplash.com/photo-1552664730-d307ca884978?w=800&q=80",
    heroImage:
      "https://images.unsplash.com/photo-1557804506-669a67965ba0?w=1920&q=80",
    featured: false,
  },
  {
    id: 9,
    slug: "dedicated-capability-center-us-company",
    title: "Dedicated Capability Center for US Company",
    tagline: "Transforming Software Development with Excellence",
    clientType: "US-based Software Company",
    industry: "Networking & Business",
    industrySlug: "networking-business",
    services: [
      "Dedicated Capability Centre",
      "Web App Development",
      "Mobile App Development",
    ],
    servicesSlugs: [
      "dedicated-capability-centre",
      "web-app-development",
      "mobile-app-development",
    ],
    timeline: "Ongoing",
    technologies: [
      "React",
      "React Native",
      "Node.js",
      "AWS",
      "PostgreSQL",
      "Docker",
    ],
    challenge:
      "A US strategic partner aimed to transform the world of software development by driving innovative solutions and incorporating top-notch expertise. They needed a reliable offshore capability center to scale their development capacity while maintaining quality excellence.",
    solution:
      "Infotech.Works serves as a dedicated capability center, delivering on four-fold values: Quality Excellence with timely delivery, Cross-Platform Development for inter-company solutions, Innovative Solutions from ideation to execution, and successful delivery of 330+ projects across various industries.",
    features: [
      "Quality excellence with consistent delivery",
      "Cross-platform software development",
      "Innovative solution design",
      "330+ successful project deliveries",
      "Multi-industry expertise",
      "Scalable team structure",
      "Agile development methodology",
      "24/7 development support",
    ],
    results: [
      {
        value: "150+",
        label: "Projects Delivered",
        description: "Successful projects across industries",
      },
      {
        value: "98%",
        label: "Client Satisfaction",
        description: "Consistent client happiness scores",
      },
      {
        value: "50%",
        label: "Cost Efficiency",
        description: "Reduction in development costs",
      },
      {
        value: "140+",
        label: "Happy Clients",
        description: "Satisfied customers worldwide",
      },
    ],
    testimonial: {
      quote:
        "Partnering with Infotech.Works has been one of the best strategic decisions we've made. Their professionalism, responsiveness, and commitment to excellence have consistently exceeded our expectations. Thanks to Infotech.Works, we were able to accelerate our growth initiatives and enhance our technological capabilities significantly.",
      author: "CEO",
      role: "US-based Strategic Partner",
    },
    image:
      "https://images.unsplash.com/photo-1497366216548-37526070297c?w=800&q=80",
    heroImage:
      "https://images.unsplash.com/photo-1497366811353-6dc744f64d28?w=1920&q=80",
    featured: false,
  },

  {
    id: 11,
    slug: "product-customization-dental-braces",
    title: "3D Product Customization Platform",
    tagline: "Interactive Smile Studio for Custom Dental Braces",
    clientType: "Healthcare / Dental Company",
    industry: "Healthcare / Medical",
    industrySlug: "healthcare-medical",
    services: ["Mobile App Development", "Experience Design"],
    servicesSlugs: ["mobile-app-development", "experience-design"],
    timeline: "4 Months",
    technologies: [
      "React Native",
      "Unity 3D",
      "ThreeJS",
      "Node.js",
      "AWS",
      "Firebase",
    ],
    challenge:
      "Company sells custom dental braces and needed to offer a mouth-designer or smile-studio where kids can choose which braces go on which tooth. The solution needed to be engaging, interactive, and guide patients through the entire brace lifecycle.",
    solution:
      "Developed an appointment management app that takes patients through the entire brace life-cycle, featuring a smile-studio built with Unity 3D. Integrated shopping, resources, and appointment management with an intuitive UI designed for young users.",
    features: [
      "ThreeJS 3D mouth renderer",
      "3D mouth model customizer",
      "Unity 3D smile studio",
      "Push notifications",
      "Milestone-based intuitive UI",
      "Appointment management",
      "Integrated shopping experience",
      "Educational resources",
    ],
    results: [
      {
        value: "85%",
        label: "User Engagement",
        description: "App engagement rate among patients",
      },
      {
        value: "60%",
        label: "Conversion Increase",
        description: "Improvement in brace selections",
      },
      {
        value: "3D",
        label: "Modelling",
        description: "3rd Party Integration",
      },
      {
        value: "40%",
        label: "Support Reduction",
        description: "Decrease in customer support calls",
      },
    ],
    testimonial: {
      quote:
        "We have received highly positive feedback from our clients who are using this app. The interactive design tool is intuitive, user-friendly, and highly engaging.",
      author: "Director, Customer Relationship",
      role: "Dental Braces Company",
    },
    image:
      "https://images.unsplash.com/photo-1588776814546-1ffcf47267a5?w=800&q=80",
    heroImage:
      "https://images.unsplash.com/photo-1606811841689-23dfddce3e95?w=1920&q=80",
    featured: false,
  },
  {
    id: 12,
    slug: "fleet-management-power-wash",
    title: "Fleet Management for Field Services",
    tagline: "Digitizing Power Wash Operations with Real-Time Tracking",
    clientType: "Field Services Company",
    industry: "Logistics",
    industrySlug: "logistics",
    services: ["Mobile App Development", "IOT App Development"],
    servicesSlugs: ["mobile-app-development", "iot-app-development"],
    timeline: "4 Months",
    technologies: [
      "Swift",
      "iOS",
      "Google Maps API",
      "Node.js",
      "AWS",
      "PostgreSQL",
    ],
    challenge:
      "Need an app to digitalize Power Wash services for on-the-go technicians, streamlining work order creation, service tracking, and customer interactions. Required capabilities to capture before/after images, track equipment and service lists, capture signatures, and manage routes with real-time GPS tracking.",
    solution:
      "Built a native iOS app with the ability for technicians to view work orders assigned to them for specific dates, allowing them to go through checklists, capture images, and efficiently complete jobs. Features include route planning, GPS tracking, real-time geo-fencing, timelogs, and push notifications.",
    features: [
      "Route planning and optimization",
      "Real-time GPS tracking",
      "Geo-fencing capabilities",
      "Work order management",
      "Before/after image capture",
      "Digital signature capture",
      "Equipment tracking",
      "Time logging and reporting",
    ],
    results: [
      {
        value: "35%",
        label: "Efficiency Gain",
        description: "Improvement in service completion rate",
      },
      {
        value: "50%",
        label: "Paperwork Reduction",
        description: "Decrease in manual documentation",
      },
      {
        value: "25%",
        label: "Fuel Savings",
        description: "Reduction through optimized routing",
      },
      {
        value: "100%",
        label: "Job Visibility",
        description: "Real-time tracking of all technicians",
      },
    ],
    testimonial: {
      quote:
        "Our Power Wash Services has proven reliable, flexible, and dedicated to our fleet's success. Their expertise and support have significantly improved our operational appearance.",
      author: "Head, Fleet Maintenance",
      role: "Power Wash Services",
    },
    image:
      "https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=800&q=80",
    heroImage:
      "https://images.unsplash.com/photo-1601584115197-04ecc0da31d7?w=1920&q=80",
    featured: false,
  },
  {
    id: 13,
    slug: "healthcare-iot-baby-language",
    title: "Healthcare IoT - Baby Language Learning",
    tagline: "BLE-Enabled Early Language Development Platform",
    clientType: "HealthTech Startup",
    industry: "Healthcare / Medical",
    industrySlug: "healthcare-medical",
    services: ["IOT App Development", "Mobile App Development"],
    servicesSlugs: ["iot-app-development", "mobile-app-development"],
    timeline: "6 Months",
    technologies: [
      "React Native",
      "BLE",
      "AWS IoT",
      "Node.js",
      "Firebase",
      "Audio Processing",
    ],
    challenge:
      "Create a subscription-based mobile service for teaching foreign languages to babies by utilizing pre-built libraries and also letting users record their own. The solution needed to ship with integrated BLE speaker and BLE pacifier hardware.",
    solution:
      "Partnered with a hardware development company for physical prototypes and firmware development. The mobile app was made able to upgrade firmware, set playlist files, and perform file transfers to the speaker. Features include BLE connectivity, in-app subscriptions, audio processing, and offline capabilities.",
    features: [
      "BLE and IoT hardware integration",
      "Firmware upgrade capability",
      "In-app subscriptions and purchases",
      "Audio processing and playback",
      "Consent processing (TOTP)",
      "Offline capabilities",
      "Storage management",
      "Custom audio recording",
    ],
    results: [
      {
        value: "2K+",
        label: "IoT Devices",
        description: "Sold to various customers",
      },
      {
        value: "95%",
        label: "Connectivity Rate",
        description: "Successful BLE pairing rate",
      },
      {
        value: "5/5",
        label: "User Rating",
        description: "User satisfaction score",
      },
      {
        value: "92%",
        label: "Retention Rate",
        description: "Monthly subscriber retention",
      },
    ],
    testimonial: {
      quote:
        "I'm incredibly proud to share our experience with this world-class product. This subscription-based mobile service that would revolutionize early language learning for babies, and this product has exceeded all expectations.",
      author: "Product Owner",
      role: "HealthTech Startup",
    },
    image:
      "https://images.unsplash.com/photo-1515488042361-ee00e0ddd4e4?w=800&q=80",
    heroImage:
      "https://images.unsplash.com/photo-1489710437720-ebb67ec84dd2?w=1920&q=80",
    featured: false,
  },
  {
    id: 14,
    slug: "classroom-management-tutoring",
    title: "Classroom Management Platform",
    tagline: "High-Impact Tutoring Program Management",
    clientType: "EdTech Company",
    industry: "EdTech / Education / Training",
    industrySlug: "edtech-education-training",
    services: ["Mobile App Development", "Custom Integrations"],
    servicesSlugs: ["mobile-app-development", "custom-integrations"],
    timeline: "3 Months",
    technologies: [
      "React Native",
      "AWS Cognito",
      "REST APIs",
      "Node.js",
      "PostgreSQL",
    ],
    challenge:
      "Designing and delivering successful high-impact tutoring programs while offering mobile apps to tutors for tracking presence and activity for their sessions. Needed seamless integration with existing backend systems.",
    solution:
      "Developed the mobile app along with interface to communicate with the client's existing backend system, pulling data from their existing APIs to render the app experience as planned. Features include SSO with Cognito, push notifications, and calendar integration.",
    features: [
      "SSO with AWS Cognito",
      "Push notifications",
      "Calendar integration",
      "Session tracking",
      "Attendance management",
      "Activity logging",
      "Existing API integration",
      "Real-time sync",
    ],
    results: [
      {
        value: "40%",
        label: "Admin Time Saved",
        description: "Reduction in manual tracking work",
      },
      {
        value: "98%",
        label: "Attendance Accuracy",
        description: "Improved tracking precision",
      },
      {
        value: "2x",
        label: "Tutor Productivity",
        description: "Increase in session efficiency",
      },
      {
        value: "85%",
        label: "User Adoption",
        description: "Tutor app adoption rate",
      },
    ],
    testimonial: {
      quote:
        "I am incredibly proud of the mobile app development team for developing a mobile app that has revolutionized the way we design and deliver high-impact tutoring programs.",
      author: "Chief Technology Officer",
      role: "EdTech Company",
    },
    image:
      "https://images.unsplash.com/photo-1523240795612-9a054b0db644?w=800&q=80",
    heroImage:
      "https://images.unsplash.com/photo-1524178232363-1fb2b075b655?w=1920&q=80",
    featured: false,
  },
  {
    id: 15,
    slug: "mobile-commerce-platform",
    title: "Mobile Commerce Platform",
    tagline: "Data-Driven M-Commerce for Business Growth",
    clientType: "Retail / E-Commerce Company",
    industry: "e-Commerce / Gig-commerce",
    industrySlug: "ecommerce-gig-commerce",
    services: ["Mobile App Development", "Experience Design"],
    servicesSlugs: ["mobile-app-development", "experience-design"],
    timeline: "5 Months",
    technologies: [
      "React Native",
      "Node.js",
      "MongoDB",
      "Stripe",
      "Firebase Analytics",
      "AWS",
    ],
    challenge:
      "To overcome stagnation by leveraging mobile commerce (m-commerce) to enhance customer engagement, streamline usability, and employ data-driven strategies for growth. Existing channels were insufficient to drive user engagement and business growth.",
    solution:
      "Developed a mobile app providing better interaction and convenience with a seamless shopping experience. Integrated analytics to gather insights from user behavior for informed decisions. Features include engaging UI/UX, integrated analytics, targeted marketing, and multiple payment gateways.",
    features: [
      "Engaging UI/UX design",
      "Integrated analytics dashboard",
      "User behavior tracking",
      "Targeted marketing features",
      "Simplified checkout flow",
      "Multiple payment gateways",
      "Push notification campaigns",
      "Personalized recommendations",
    ],
    results: [
      {
        value: "45%",
        label: "Conversion Increase",
        description: "Improvement in sales conversion",
      },
      {
        value: "60%",
        label: "Engagement Growth",
        description: "Increase in user engagement",
      },
      {
        value: "30%",
        label: "Cart Abandonment Drop",
        description: "Reduction in abandoned carts",
      },
      {
        value: "3x",
        label: "Mobile Revenue",
        description: "Growth in mobile channel sales",
      },
      {
        value: "28+",
        label: "Countries",
        description: "Launched globally across 4 continents",
      },
      {
        value: "10+",
        label: "Payment Gateways",
        description: "Integration of multiple payment gateways",
      },
    ],
    testimonial: {
      quote:
        "Excellent feedback from our company's senior leadership for a successful engagement as the quality of deliverables has been phenomenal.",
      author: "VP, Product Delivery",
      role: "Retail Company",
    },
    image:
      "https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?w=800&q=80",
    heroImage:
      "https://images.unsplash.com/photo-1472851294608-062f824d29cc?w=1920&q=80",
    featured: false,
  },
  {
    id: 16,
    slug: "ev-charger-management",
    title: "EV Charger Management System",
    tagline: "Smart Charging Infrastructure with IoT Integration",
    clientType: "Clean Energy Startup",
    industry: "Energy",
    industrySlug: "energy",
    services: [
      "IOT App Development",
      "Mobile App Development",
      "Web App Development",
    ],
    servicesSlugs: [
      "iot-app-development",
      "mobile-app-development",
      "web-app-development",
    ],
    timeline: "6 Months",
    technologies: [
      "React Native",
      "OCPP",
      "BLE",
      "eNode",
      "Stripe",
      "Node.js",
      "AWS IoT",
    ],
    challenge:
      "Build a charge management system that optimizes charge allocation by leveraging data from all vehicles/users who use the system. Inputs needed to be pulled through a third-party system (Enode), users' driving habits, and manual entry via the app.",
    solution:
      "Developed an integrated solution with charger hardware interface, backend configuration admin panel, and native Android and iOS mobile apps. Features include OCPP EV charging adaptor interface, BLE connectivity, eNode integration, Stripe payments, and SaaS-based CMS.",
    features: [
      "OCPP EV charging adaptor interface",
      "BLE to connect app with EV",
      "eNode third-party integration",
      "Stripe charges and subscriptions",
      "SaaS-based CMS",
      "OAuth authentication",
      "Charge optimization algorithms",
      "Usage analytics dashboard",
    ],
    results: [
      {
        value: "30%",
        label: "Charging Efficiency",
        description: "Improvement in charge allocation",
      },
      {
        value: "99.5%",
        label: "System Uptime",
        description: "Platform reliability rate",
      },
      {
        value: "2K+",
        label: "Active Chargers",
        description: "Chargers managed on platform",
      },
      {
        value: "40%",
        label: "Cost Savings",
        description: "User savings on charging costs",
      },
      {
        value: "Data",
        label: "Analytics",
        description: "Deep insight and 360 degree view",
      },
    ],
    testimonial: {
      quote:
        "Your hard work has positioned our organization at the forefront of smart charging solutions, and I am excited for what we will achieve next together!",
      author: "Head of Marketing",
      role: "Clean Energy Startup",
    },
    image:
      "https://images.unsplash.com/photo-1593941707882-a5bba14938c7?w=800&q=80",
    heroImage:
      "https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=1920&q=80",
    featured: false,
  },
  {
    id: 17,
    slug: "greenhouse-management-agritech",
    title: "Greenhouse Management Platform",
    tagline: "Modern Controlled Agriculture Software",
    clientType: "AgriTech Company",
    industry: "AgriTech",
    industrySlug: "agritech",
    services: ["Mobile App Development", "Web App Development"],
    servicesSlugs: ["mobile-app-development", "web-app-development"],
    timeline: "5 Months",
    technologies: [
      "React Native",
      "React",
      "Node.js",
      "PostgreSQL",
      "AWS",
      "QR Code SDK",
    ],
    challenge:
      "Build a mobile and web app that simplifies task management, crop tracking, maintenance, control and fertigation applications, scouting for greenhouse management users who are on the field and need a quick way for plants management.",
    solution:
      "Developed a modern, controlled agriculture software for web, Android and iOS using React Native, born from research at Ohio State. Features include task management, reservations, QR code tracking, scouting and pest management, multilingual support, and maps integration.",
    features: [
      "Task management system",
      "Crop reservations",
      "QR code tracking",
      "Scouting and pest management",
      "Multilingual support",
      "Interactive maps",
      "Fertigation control",
      "Weather integration",
    ],
    results: [
      {
        value: "45%",
        label: "Productivity Gain",
        description: "Improvement in farm operations",
      },
      {
        value: "30%",
        label: "Waste Reduction",
        description: "Decrease in crop losses",
      },
      {
        value: "60%",
        label: "Time Saved",
        description: "Reduction in manual tracking",
      },
      {
        value: "25%",
        label: "Yield Increase",
        description: "Improvement in crop output",
      },
    ],
    testimonial: {
      quote:
        "We have been using this modern, controlled agriculture software across its web, Android, and iOS platforms, and it has truly transformed the way we manage our agricultural operations.",
      author: "VP, Operations",
      role: "AgriTech Company",
    },
    image:
      "https://images.unsplash.com/photo-1416879595882-3373a0480b5b?w=800&q=80",
    heroImage:
      "https://images.unsplash.com/photo-1530836369250-ef72a3f5cda8?w=1920&q=80",
    featured: false,
  },
  {
    id: 18,
    slug: "peer-to-peer-payments-tipping",
    title: "Peer-to-Peer Payments Platform",
    tagline: "Digital Tipping and Tip Pool Management",
    clientType: "FinTech Startup",
    industry: "FinTech / Finance",
    industrySlug: "fintech-finance",
    services: ["Mobile App Development", "API Development"],
    servicesSlugs: ["mobile-app-development", "api-development"],
    timeline: "6 Months",
    technologies: [
      "React Native",
      "Node.js",
      "PostgreSQL",
      "Stripe",
      "Kount",
      "AWS",
    ],
    challenge:
      "Create a peer-to-peer payments solution in the tipping space, allowing organizations to track tips received by staff and pool/distribute them evenly across the staff. The department admin needed tools for distributing tips to each user's wallets.",
    solution:
      "Integrated various payment gateways and third-party services including Kount fraud control, KYC services, request signing, and encryption. Features include multi-vendor payment system, QR codes for scan-and-pay, custom branded QR codes, and organizations built as SaaS.",
    features: [
      "Kount fraud control",
      "KYC services integration",
      "Request signing & encryption",
      "Account blocking and reviews",
      "Multi-vendor payment system",
      "QR codes for scan-and-pay",
      "Custom branded QR codes",
      "SaaS organization management",
    ],
    results: [
      {
        value: "99.9%",
        label: "Transaction Success",
        description: "Payment processing reliability",
      },
      {
        value: "< 0.1%",
        label: "Fraud Rate",
        description: "Fraudulent transaction rate",
      },
      {
        value: "50K+",
        label: "Monthly Transactions",
        description: "Tips processed per month",
      },
      {
        value: "100%",
        label: "Automation",
        description: "Manual work to digital transformation",
      },
    ],
    testimonial: {
      quote:
        "We are extremely impressed with the peer-to-peer payment system implemented for managing tips. This innovative solution has streamlined the tipping process, making it incredibly easy for both customers and staff.",
      author: "Product Head",
      role: "FinTech Startup",
    },
    image:
      "https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?w=800&q=80",
    heroImage:
      "https://images.unsplash.com/photo-1563986768494-4dee2763ff3f?w=1920&q=80",
    featured: false,
  },
  {
    id: 19,
    slug: "clix-capital-digital-lending",
    title: "Digital Lending Platform",
    tagline: "Streamlined Customer Verification for NBFC",
    clientType: "FinTech / Digital Lending NBFC",
    industry: "FinTech / Finance",
    industrySlug: "fintech-finance",
    services: ["Mobile App Development", "API Development"],
    servicesSlugs: ["mobile-app-development", "api-development"],
    timeline: "4 Months",
    technologies: [
      "Android Native",
      "Kotlin",
      "REST APIs",
      "AWS",
      "Firebase",
      "Aadhaar API",
    ],
    challenge:
      "With an AUM (assets under management) worth about Rs 5,000 crore across over 3 million customers, Clix Capital required a mobile app for customer verification and data management with robust security and compliance.",
    solution:
      "Native Android mobile application development with integration of government ID verification, secure document uploads, OTP-based mobile authentication, and robust security compliance features.",
    features: [
      "Government ID verification",
      "Secure document upload",
      "OTP-based mobile authentication",
      "Robust security and compliance",
      "KYC verification workflow",
      "Real-time data validation",
      "Encrypted data transmission",
      "Audit trail logging",
    ],
    results: [
      {
        value: "Scalable",
        label: "Architecture",
        description: "Supports cloud based scaling",
      },
      {
        value: "$500 M",
        label: "AUM Managed",
        description: "Assets under management",
      },
      {
        value: "70%",
        label: "Faster Verification",
        description: "Reduction in verification time",
      },
      {
        value: "99.9%",
        label: "Security Score",
        description: "Compliance rating achieved",
      },
    ],
    testimonial: {
      quote:
        "The mobile app has transformed our customer onboarding process, making verification seamless while maintaining the highest security standards required in the NBFC sector.",
      author: "Head of Digital",
      role: "FinTech NBFC",
    },
    image:
      "https://images.unsplash.com/photo-1563013544-824ae1b704d3?w=800&q=80",
    heroImage:
      "https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=1920&q=80",
    featured: false,
  },
  {
    id: 20,
    slug: "giftpocket-digital-wallet",
    title: "Digital Gift Card Wallet",
    tagline: "Buy, Store, Send & Exchange Gift Cards",
    clientType: "E-Commerce / Lifestyle Company",
    industry: "Lifestyle",
    industrySlug: "lifestyle",
    services: ["Mobile App Development", "Experience Design"],
    servicesSlugs: ["mobile-app-development", "experience-design"],
    timeline: "5 Months",
    technologies: [
      "React Native",
      "Node.js",
      "Stripe",
      "Firebase",
      "REST APIs",
      "AWS",
    ],
    challenge:
      "Create a digital wallet app that allows users to buy, store, send, exchange, and use gift cards. Additionally, users should be able to check card balance, donate gift cards, and enjoy a gamified user experience.",
    solution:
      "Cross-platform mobile application using React Native with multiple API integrations for buying, storing, sending, and exchanging gift cards. Features fluid UI, gamification elements, and Stripe payment gateway integration.",
    features: [
      "Buy and store gift cards",
      "Send and exchange cards",
      "Check card balance",
      "Donate gift cards",
      "Gamification features",
      "Fluid UI navigation",
      "Stripe payment integration",
      "Real-time notifications",
    ],
    results: [
      {
        value: "5K+",
        label: "Active Users",
        description: "Monthly active app users",
      },
      {
        value: "85%",
        label: "User Retention",
        description: "30-day retention rate",
      },
      {
        value: "$50K+",
        label: "Gift Cards Processed",
        description: "Monthly transaction volume",
      },
      {
        value: "250+",
        label: "Brands",
        description: "Integration with gift cards providers APIs",
      },
    ],
    testimonial: {
      quote:
        "We are very glad with our experience using GiftPocket! This app has truly revolutionized how we manage and enjoy gift cards, offering unparalleled convenience and versatility.",
      author: "Chief Marketing Officer",
      role: "Lifestyle Company",
    },
    image:
      "https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?w=800&q=80",
    heroImage:
      "https://images.unsplash.com/photo-1607083206869-4c7672e72a8a?w=1920&q=80",
    featured: false,
  },
  {
    id: 21,
    slug: "peerro-jobs-platform",
    title: "Unified Jobs & Training Platform",
    tagline: "Empowering Workforce with Personalized Career Growth",
    clientType: "Jobs / Employment Platform",
    industry: "Jobs / HR Tech",
    industrySlug: "jobs-hr-tech",
    services: ["Web App Development", "Custom Integrations"],
    servicesSlugs: ["web-app-development", "custom-integrations"],
    timeline: "6 Months",
    technologies: [
      "React",
      "Node.js",
      "Indeed API",
      "ZipRecruiter API",
      "JobsPikr",
      "PostgreSQL",
      "AWS",
    ],
    challenge:
      "Organizations struggle with employee engagement, efficient job searches, and aligning skills with market demands. Need a unified platform enabling personalized job searches, targeted training, and skill analytics.",
    solution:
      "Developed a customized platform integrating Indeed, JobsPikr, and ZipRecruiter to streamline job search. Includes CMS for job posting, tools for analyzing job performance, training programs, assessments, and workforce management features.",
    features: [
      "Indeed & ZipRecruiter integration",
      "JobsPikr data aggregation",
      "CMS for job listings",
      "Training program management",
      "Skill assessments",
      "Job performance analytics",
      "Workforce management tools",
      "Personalized recommendations",
    ],
    results: [
      {
        value: "100K+",
        label: "Job Listings",
        description: "Aggregated from multiple sources",
      },
      {
        value: "40%",
        label: "Faster Hiring",
        description: "Reduction in time-to-hire",
      },
      {
        value: "60%",
        label: "Skill Match Rate",
        description: "Improved candidate matching",
      },
      {
        value: "2K+",
        label: "Users Trained",
        description: "Completed training programs",
      },
    ],
    testimonial: {
      quote:
        "This unified platform is delivering a seamless and highly personalized experience for both job seekers and professionals looking to upskill. Great job!",
      author: "Chief Executive Officer",
      role: "HR Tech Company",
    },
    image:
      "https://images.unsplash.com/photo-1521737711867-e3b97375f902?w=800&q=80",
    heroImage:
      "https://images.unsplash.com/photo-1522071820081-009f0129c71c?w=1920&q=80",
    featured: false,
  },
  {
    id: 22,
    slug: "ayana-therapy-mental-health",
    title: "Mental Health Services Platform",
    tagline: "Culturally Competent Mental Health Services",
    clientType: "Healthcare / Mental Health Services",
    industry: "Healthcare / Medical",
    industrySlug: "healthcare-medical",
    services: ["Mobile App Development", "Custom Integrations"],
    servicesSlugs: ["mobile-app-development", "custom-integrations"],
    timeline: "5 Months",
    technologies: [
      "React Native",
      "Node.js",
      "WebRTC",
      "Twilio",
      "AWS",
      "PostgreSQL",
    ],
    challenge:
      "Provide a digital solution for instant mental health services for marginalized and intersectional employees across the globe, erasing stigma and eliminating barriers to access.",
    solution:
      "Cross-platform development with third-party integrations for scheduling sessions, therapist search, chat and video call functionalities, session records, mental health progress review plans, and customized calendar.",
    features: [
      "Schedule therapy sessions",
      "Search therapists by specialty",
      "Chat and video calls",
      "Session record maintenance",
      "Mental health progress tracking",
      "Customized calendar",
      "Secure messaging",
      "Resource library",
    ],
    results: [
      {
        value: "1K+",
        label: "Sessions Completed",
        description: "Therapy sessions delivered",
      },
      {
        value: "70+",
        label: "Therapists",
        description: "Digitally Onboarded and Verified",
      },
      {
        value: "4.9/5",
        label: "User Satisfaction",
        description: "Client satisfaction rate",
      },
      {
        value: "50%",
        label: "Accessibility Increase",
        description: "Improvement in access to care",
      },
      {
        value: "4.9/5",
        label: "App Rating",
        description: "User experience score",
      },
    ],
    testimonial: {
      quote:
        "The ease of access, culturally competent resources, and immediate support have significantly improved employee morale and engagement resulting in a growing customer base.",
      author: "VP, Business Development and Partnerships",
      role: "Mental Health Platform",
    },
    image:
      "https://images.unsplash.com/photo-1573497019940-1c28c88b4f3e?w=800&q=80",
    heroImage:
      "https://images.unsplash.com/photo-1527689368864-3a821dbccc34?w=1920&q=80",
    featured: false,
  },
  {
    id: 23,
    slug: "secure-cup-ble-security",
    title: "BLE-Enabled Smart Cup",
    tagline: "IoT Security Solution for Beverage Protection",
    clientType: "Lifestyle / Security Company",
    industry: "Security",
    industrySlug: "security",
    services: ["IOT App Development", "Mobile App Development"],
    servicesSlugs: ["iot-app-development", "mobile-app-development"],
    timeline: "6 Months",
    technologies: [
      "Swift",
      "Kotlin",
      "BLE",
      "Firmware SDK",
      "Node.js",
      "AWS IoT",
    ],
    challenge:
      "Build a physical cup with security features to open the lid using a mobile app. The cup requires BLE-enabled firmware to interact with mobile apps for secure access.",
    solution:
      "Native Android and iOS mobile apps developed along with partnering for hardware and firmware. Features include BLE characteristics integration, secure pairing, biometric implementation, QR code scanning, multiple ownership modes, and chipset software upgrades.",
    features: [
      "BLE characteristics integration",
      "Secure cup pairing",
      "Biometric authentication",
      "QR code connection",
      "Multiple ownership modes",
      "Firmware upgrade capability",
      "Tamper detection",
      "Battery status monitoring",
    ],
    results: [
      {
        value: "99.9%",
        label: "Pairing Success",
        description: "BLE connection reliability",
      },
      {
        value: "< 2 sec",
        label: "Unlock Speed",
        description: "Average time to unlock",
      },
      {
        value: "100%",
        label: "Security Rate",
        description: "Zero unauthorized access",
      },
      {
        value: "5K+",
        label: "Units",
        description: "Ready to sell in the market",
      },
    ],
    testimonial: {
      quote:
        "We are absolutely thrilled with this innovative hardware cup with secured BLE connectivity using mobile app adding a whole new level of security targeted to party going users.",
      author: "Product Manager",
      role: "Security Company",
    },
    image:
      "https://images.unsplash.com/photo-1544787219-7f47ccb76574?w=800&q=80",
    heroImage:
      "https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=1920&q=80",
    featured: false,
  },
  {
    id: 24,
    slug: "surgicloud-surgical-coordination",
    title: "Surgical Coordination Platform",
    tagline: "Digital Transformation of Surgical Workflows",
    clientType: "Healthcare / Medical Technology",
    industry: "Healthcare / Medical",
    industrySlug: "healthcare-medical",
    services: ["Mobile App Development", "Web App Development"],
    servicesSlugs: ["mobile-app-development", "web-app-development"],
    timeline: "8 Months",
    technologies: [
      "Swift",
      "Kotlin",
      "React",
      "Node.js",
      "OCR",
      "AWS",
      "PostgreSQL",
    ],
    challenge:
      "Build an app that optimizes surgical coordination and product billing by replacing paper forms with a digital platform. Streamline case scheduling, inventory tracking, and billing between distributors, manufacturers, and hospitals.",
    solution:
      "Natively built mobile and tablet application for iOS and Android, designed to streamline surgical coordination, product billing, and inventory management with real-time case tracking, scheduling, digital signatures, and OCR capabilities.",
    features: [
      "Real-time case tracking",
      "Product inventory management",
      "Case scheduling",
      "Digital signature capture",
      "OCR document scanning",
      "Billing management",
      "Multi-party coordination",
      "Compliance reporting",
    ],
    results: [
      {
        value: "60%",
        label: "Time Saved",
        description: "Reduction in administrative work",
      },
      {
        value: "100%",
        label: "Paperless",
        description: "Digital documentation achieved",
      },
      {
        value: "40%",
        label: "Billing Accuracy",
        description: "Improvement in billing precision",
      },
      {
        value: "50+",
        label: "Hospitals",
        description: "Healthcare facilities onboarded",
      },
    ],
    testimonial: {
      quote:
        "Surgicloud makes the surgical workflow more efficient and lets teams focus on what is important.",
      author: "CEO",
      role: "Healthcare Technology Company",
    },
    image:
      "https://images.unsplash.com/photo-1551190822-a9333d879b1f?w=800&q=80",
    heroImage:
      "https://images.unsplash.com/photo-1579684385127-1ef15d508118?w=1920&q=80",
    featured: false,
  },
  {
    id: 25,
    slug: "promentor-sports-training",
    title: "Sports Training Platform",
    tagline: "Professional Coaching for Athletes",
    clientType: "Sports / Training Platform",
    industry: "Sports",
    industrySlug: "sports",
    services: ["Mobile App Development", "Experience Design"],
    servicesSlugs: ["mobile-app-development", "experience-design"],
    timeline: "6 Months",
    technologies: [
      "Swift",
      "iOS",
      "DocuSign",
      "WebRTC",
      "Stripe Connect",
      "Firebase",
    ],
    challenge:
      "Create an app that enables professional players and coaches to schedule personalized lessons for students, offering one-on-one and group sessions with live feedback on videos and posture.",
    solution:
      "Native iOS app with DocuSign integration, video call sessions, group sessions, screen sharing and video annotation, Apple subscriptions, social login, and Stripe Connect for payments.",
    features: [
      "DocuSign integration",
      "Video call sessions",
      "Group session support",
      "Screen sharing",
      "Video annotation",
      "Apple subscriptions",
      "Google/Facebook login",
      "Stripe Connect payments",
    ],
    results: [
      {
        value: "1K+",
        label: "Athletes Trained",
        description: "Students using the platform",
      },
      {
        value: "50+",
        label: "Pro Coaches",
        description: "Professional mentors onboarded",
      },
      {
        value: "92%",
        label: "Skill Improvement",
        description: "Students showing progress",
      },
      {
        value: "4.6/5",
        label: "App Rating",
        description: "User satisfaction score",
      },
    ],
    testimonial: {
      quote:
        "Promentor will enable us to implement a world-class onboarding and professional development program for our athletic and activities department coaches.",
      author: "Athletic and Activities Director",
      role: "Sports Training Organization",
    },
    image:
      "https://images.unsplash.com/photo-1526232761682-d26e03ac148e?w=800&q=80",
    heroImage:
      "https://images.unsplash.com/photo-1517649763962-0c623066013b?w=1920&q=80",
    featured: false,
  },
  {
    id: 26,
    slug: "optimize-sales-operations",
    title: "Sales Operations App",
    tagline: "Digital Transformation for Field Sales Teams",
    clientType: "IT Services / Operations",
    industry: "Sales & Marketing",
    industrySlug: "sales-marketing",
    services: ["Mobile App Development", "Web App Development"],
    servicesSlugs: ["mobile-app-development", "web-app-development"],
    timeline: "5 Months",
    technologies: [
      "Swift",
      "iOS",
      "PWA",
      "Node.js",
      "Barcode SDK",
      "SSO",
      "AWS",
    ],
    challenge:
      "Digital transformation to bring sales activities of on-ground sales staff to a mobile app with 360-degree view of coverage and activities. Remove dependency on carrying multiple devices and keep each team's data on separate servers.",
    solution:
      "iOS mobile app with role-based visibility, calendar-centric interface with cases as to-do lists. Features include PWA web version, barcode scanning, 100% offline capability, multi-server hopping, and SSO.",
    features: [
      "PWA web version",
      "Barcode scanning",
      "100% offline capability",
      "Multi-server hopping",
      "SSO authentication",
      "Role-based visibility",
      "Calendar-centric interface",
      "Real-time sync",
    ],
    results: [
      {
        value: "100%",
        label: "Offline Support",
        description: "Full functionality without internet",
      },
      {
        value: "50%",
        label: "Productivity Gain",
        description: "Improvement in sales efficiency",
      },
      {
        value: "30%",
        label: "Coverage Increase",
        description: "More territories covered",
      },
      {
        value: "Zero",
        label: "Data Loss",
        description: "Complete data integrity",
      },
    ],
    testimonial: {
      quote:
        "This mobile app has been an invaluable tool for driving our digital transformation. Kudos to the development team!",
      author: "Director, Sales Operations",
      role: "Enterprise Company",
    },
    image:
      "https://images.unsplash.com/photo-1552664730-d307ca884978?w=800&q=80",
    heroImage:
      "https://images.unsplash.com/photo-1556761175-4b46a572b786?w=1920&q=80",
    featured: false,
  },
  {
    id: 27,
    slug: "our-apps-food-truck",
    title: "White-Label Food Truck Platform",
    tagline: "Ready-Made Mobile Apps for Food Businesses",
    clientType: "E-Commerce / Food Service",
    industry: "e-Commerce / Gig-commerce",
    industrySlug: "ecommerce-gig-commerce",
    services: ["Mobile App Development", "Product Engineering"],
    servicesSlugs: ["mobile-app-development", "product-engineering"],
    timeline: "4 Months",
    technologies: [
      "React Native",
      "Node.js",
      "Firebase",
      "Stripe",
      "Google Maps API",
      "AWS",
    ],
    challenge:
      "Sell out-of-the-box ready-made white-labeled mobile apps to food trucks, enabling them to get started and on the app store within a day.",
    solution:
      "Base mobile app project with build flavors enabling rapid publishing of white-labeled versions. Over 50% of configuration moved to run-time execution for faster app publishing with server-driven data on login.",
    features: [
      "White-labeled product",
      "Automatic brand configuration",
      "Live location tracking",
      "Multi-vendor marketplace",
      "POS integrations",
      "Order management",
      "Push notifications",
      "Analytics dashboard",
    ],
    results: [
      {
        value: "< 24 hrs",
        label: "Launch Time",
        description: "From signup to app store",
      },
      {
        value: "100+",
        label: "Food Trucks",
        description: "Businesses using the platform",
      },
      {
        value: "50%",
        label: "Config Runtime",
        description: "Server-driven configuration",
      },
      { value: "99.9%", label: "Uptime", description: "Platform reliability" },
    ],
    testimonial: {
      quote:
        "The platform is truly a game-changer, offering an out-of-the-box, ready-made solution that allowed us to get our mobile app customized, branded, and live on the app store in less than a day!",
      author: "Head of Operations",
      role: "Food Service Company",
    },
    image:
      "https://images.unsplash.com/photo-1565123409695-7b5ef63a2efb?w=800&q=80",
    heroImage:
      "https://images.unsplash.com/photo-1567129937968-cdad8f07e2f8?w=1920&q=80",
    featured: false,
  },
  {
    id: 28,
    slug: "hear-me-read-education",
    title: "E-Book Learning Platform",
    tagline: "AI-Powered Reading Practice for Speech Therapy",
    clientType: "Education / Speech Therapy",
    industry: "EdTech / Education / Training",
    industrySlug: "edtech-education-training",
    services: ["Mobile App Development"],
    servicesSlugs: ["mobile-app-development"],
    timeline: "7 Months",
    technologies: [
      "React Native",
      "Python NLP",
      "Speech2Text",
      "TTS",
      "Node.js",
      "AWS",
    ],
    challenge:
      "Markup books with different parts of speech, custom videos and pronunciation audios, presented in a custom-built e-book reader. Build a practice-patient SaaS for tracking and assigning goals.",
    solution:
      "Mobile app with Python NLP microservice, karaoke-style highlight-as-media-plays feature, AI-generated TTS reading, SaaS platform for practitioners, and Speech2Text for practice assessment.",
    features: [
      "Python NLP processing",
      "Karaoke-style text highlighting",
      "AI-generated TTS",
      "Speech2Text assessment",
      "SaaS practitioner platform",
      "Goal assignment",
      "Progress tracking",
      "Custom video integration",
    ],
    results: [
      {
        value: "85%",
        label: "Reading Improvement",
        description: "Student progress rate",
      },
      {
        value: "1K+",
        label: "Students",
        description: "Active learners on platform",
      },
      {
        value: "100+",
        label: "Practitioners",
        description: "Speech therapists using SaaS",
      },
      {
        value: "50+ GB",
        label: "Content",
        description: "More options and user engagement",
      },
    ],
    testimonial: {
      quote:
        "The custom-built e-book reader, integrated with powerful meta features, is an absolute standout. Great work.",
      author: "Product Head",
      role: "EdTech Company",
    },
    image:
      "https://images.unsplash.com/photo-1503676260728-1c00da094a0b?w=800&q=80",
    heroImage:
      "https://images.unsplash.com/photo-1456513080510-7bf3a84b82f8?w=1920&q=80",
    featured: false,
  },
  {
    id: 29,
    slug: "hope-app-charity-donations",
    title: "Micro-Donation Platform",
    tagline: "Transform Everyday Spending into Charitable Giving",
    clientType: "Charity / Non-Profit",
    industry: "Charity / Funding",
    industrySlug: "charity-funding",
    services: ["Mobile App Development", "API Development"],
    servicesSlugs: ["mobile-app-development", "api-development"],
    timeline: "5 Months",
    technologies: [
      "Swift",
      "iOS",
      "Finicity",
      "Stripe",
      "Node.js",
      "AWS",
      "Firebase",
    ],
    challenge:
      "Create an app that empowers users to transform everyday spending into charitable giving through round-ups and micro-donations supporting nonprofit organizations.",
    solution:
      "Native iOS application allowing users to donate by rounding up purchases. Features white-labeled product, secure biometric login, Finicity integration for spare change tracking, Stripe payments, dynamic branding, and donation tracking.",
    features: [
      "Round-up donations",
      "White-labeled product",
      "Biometric login",
      "Finicity integration",
      "Stripe payments",
      "Dynamic organization branding",
      "Donation tracking",
      "Transaction history",
    ],
    results: [
      {
        value: "$100K+",
        label: "Donations",
        description: "Total funds raised",
      },
      {
        value: "25K+",
        label: "Active Donors",
        description: "Users making donations",
      },
      {
        value: "100+",
        label: "Non-Profits",
        description: "Organizations supported",
      },
      {
        value: "1M+",
        label: "Transactions",
        description: "Donations received",
      },
    ],
    testimonial: {
      quote:
        "The Hope App community is growing every single day! It is so awesome to think about how small everyday purchases can help bring about big, big change!",
      author: "Product Owner",
      role: "Charity Platform",
    },
    image:
      "https://images.unsplash.com/photo-1532629345422-7515f3d16bb6?w=800&q=80",
    heroImage:
      "https://images.unsplash.com/photo-1559027615-cd4628902d4a?w=1920&q=80",
    featured: false,
  },
  {
    id: 30,
    slug: "99p-labs-ar-exhibition",
    title: "AR Exhibition Guide",
    tagline: "Augmented Reality Smart City Experience",
    clientType: "Marketing / Exhibition",
    industry: "Sales & Marketing",
    industrySlug: "sales-marketing",
    services: ["Mobile App Development", "Experience Design"],
    servicesSlugs: ["mobile-app-development", "experience-design"],
    timeline: "3 Months",
    technologies: [
      "React Native",
      "ARKit",
      "ARCore",
      "Node.js",
      "Firebase",
      "AWS",
    ],
    challenge:
      "Build a guide app for visitors to an exhibition center showcasing smart city concepts, along with sign-up and tracking for a startup competition held by the university.",
    solution:
      "AR experiences developed for each topic surrounding the exhibition center. The startup program sign-up was built as SaaS, making it extensible for managing applications and startups from within the platform.",
    features: [
      "AR exhibition experiences",
      "Smart city topic guides",
      "Startup competition signup",
      "SaaS application management",
      "Interactive 3D models",
      "Visitor tracking",
      "Event notifications",
      "Analytics dashboard",
    ],
    results: [
      {
        value: "20K+",
        label: "Visitors",
        description: "Exhibition attendees using app",
      },
      // {
      //   value: "200+",
      //   label: "Startups",
      //   description: "Competition applications",
      // },
      {
        value: "95%",
        label: "Engagement",
        description: "AR feature usage rate",
      },
      {
        value: "200+",
        label: "Tradeshows",
        description: "Custom, modular, and portable",
      },
      {
        value: "95%",
        label: "Customers",
        description: "Repeat and Referral",
      },
    ],
    testimonial: {
      quote:
        "The AR experience brought our smart city exhibition to life in ways we never imagined. Visitors were truly engaged and immersed.",
      author: "Exhibition Director",
      role: "Marketing Agency",
    },
    image:
      "https://images.unsplash.com/photo-1633356122544-f134324a6cee?w=800&q=80",
    heroImage:
      "https://images.unsplash.com/photo-1519389950473-47ba0277781c?w=1920&q=80",
    featured: false,
  },
  {
    id: 31,
    slug: "dressit-wedding-shopping",
    title: "Wedding Dress Discovery Platform",
    tagline: "Pinterest-Style Shopping from Local Boutiques",
    clientType: "IT Services / Retail",
    industry: "Retail",
    industrySlug: "retail",
    services: ["Mobile App Development", "Web App Development"],
    servicesSlugs: ["mobile-app-development", "web-app-development"],
    timeline: "6 Months",
    technologies: [
      "React Native",
      "React",
      "Node.js",
      "PostgreSQL",
      "AWS",
      "Firebase",
    ],
    challenge:
      "Create curated selections for wedding dresses from local shops - Pinterest-style wedding dress shopping rooted in real pieces that can be found at local stores.",
    solution:
      "SaaS platform letting retailers upload catalogues and track item performance. Brides, friends, and family can shop, shortlist, share collections, setup store appointments, and book trials directly from the app.",
    features: [
      "SaaS retailer platform",
      "Catalogue management",
      "Performance analytics",
      "Shortlist and share",
      "Store appointment booking",
      "Trial scheduling",
      "Push notifications",
      "Fluid Pinterest-style UI",
    ],
    results: [
      {
        value: "50+",
        label: "Boutiques",
        description: "Retailers on platform",
      },
      {
        value: "1K+",
        label: "Dresses Listed",
        description: "Available selections",
      },
      // { value: "30K+", label: "Brides", description: "Active users monthly" },
      {
        value: "40%",
        label: "Conversion Rate",
        description: "Appointment to purchase",
      },
      {
        value: "8K",
        label: "Orders",
        description: "By users in a quarter",
      },
    ],
    testimonial: {
      quote:
        "From ideation to execution, the entire process has been smooth, and the end result is nothing short of outstanding.",
      author: "Director, Sales Operations",
      role: "Retail Platform",
    },
    image:
      "https://images.unsplash.com/photo-1519741497674-611481863552?w=800&q=80",
    heroImage:
      "https://images.unsplash.com/photo-1594552072238-b8a33785b261?w=1920&q=80",
    featured: false,
  },
  {
    id: 32,
    slug: "finaureus-mutual-funds",
    title: "Mutual Funds Investment App",
    tagline: "White-Label Investment Platform for Fund Houses",
    clientType: "FinTech / Investment",
    industry: "FinTech / Finance",
    industrySlug: "fintech-finance",
    services: ["Mobile App Development", "API Development"],
    servicesSlugs: ["mobile-app-development", "api-development"],
    timeline: "8 Months",
    technologies: [
      "React Native",
      "Node.js",
      "PostgreSQL",
      "AWS",
      "KYC APIs",
      "Payment Gateways",
    ],
    challenge:
      "Build a mutual-funds investment app capable of white-labeling for fund houses who can release it as a private product for their investors, requiring many third-party integrations.",
    solution:
      "Developed app with multiple integrations including white-labeling, account management, multi-vendor payment system, QR codes for transactions, KYC services, request signing, and end-to-end encryption.",
    features: [
      "White-labeled app",
      "Account blocking and reviews",
      "Multi-vendor payments",
      "QR code transactions",
      "Custom branded QR codes",
      "KYC services",
      "Request signing",
      "End-to-end encryption",
    ],
    results: [
      {
        value: "100%",
        label: "Digitization",
        description: "No manual paperwork",
      },
      {
        value: "6K+",
        label: "Investors",
        description: "Active users on platform",
      },
      {
        value: "8+",
        label: "Fund Houses",
        description: "White-label deployments",
      },
      {
        value: "99.99%",
        label: "Uptime",
        description: "Platform availability",
      },
    ],
    testimonial: {
      quote:
        "The team was able to successfully navigate the complexities of many third party integrations and built a robust, secure, and highly scalable platform for our customers.",
      author: "Head of Digital Initiatives",
      role: "Investment Platform",
    },
    image:
      "https://images.unsplash.com/photo-1611974789855-9c2a0a7236a3?w=800&q=80",
    heroImage:
      "https://images.unsplash.com/photo-1590283603385-17ffb3a7f29f?w=1920&q=80",
    featured: false,
  },
  {
    id: 33,
    slug: "ai-multi-agent-market-intelligence",
    title: "Multi-Agent Framework for Market Intelligence",
    tagline: "AI-Powered Competitive Analysis and Data Insights",
    clientType: "AI Consulting / Data Intelligence",
    industry: "Artificial Intelligence",
    industrySlug: "artificial-intelligence",
    services: ["AI Consulting", "API Development"],
    servicesSlugs: ["ai-consulting", "api-development"],
    timeline: "4 Months",
    technologies: [
      "OpenAI GPT-4",
      "Anthropic Claude",
      "Python",
      "FastAPI",
      "AWS",
      "Data Visualization",
    ],
    challenge:
      "The client struggled to generate rich, business-grade insights from fragmented data sources and APIs. Manual processes limited the team's ability to deliver fast, scalable, and precise market intelligence.",
    solution:
      "Built a robust multi-agent framework with planner agents from OpenAI and Anthropic. Integrated API orchestration, summarization, and web research into a single intelligent pipeline. Enabled natural language queries to access, compute, and visualize insights in real time.",
    features: [
      "Multi-agent AI framework",
      "OpenAI & Anthropic integration",
      "API orchestration pipeline",
      "Natural language querying",
      "Real-time data visualization",
      "Automated summarization",
      "Web research integration",
      "Scalable analytics engine",
    ],
    results: [
      {
        value: "90%",
        label: "Time Saved",
        description: "Report generation from days to minutes",
      },
      {
        value: "Real-time",
        label: "Insights",
        description: "Plots and analysis via single API",
      },
      {
        value: "100%",
        label: "Query Coverage",
        description: "Complex queries like market size analysis",
      },
      {
        value: "10x",
        label: "Scalability",
        description: "Autonomous analytics across verticals",
      },
    ],
    testimonial: {
      quote:
        "The multi-agent framework has transformed our market intelligence capabilities. What used to take days now takes minutes, with deeper insights than ever before.",
      author: "Chief Data Officer",
      role: "Data Intelligence Company",
    },
    image:
      "https://images.unsplash.com/photo-1677442136019-21780ecad995?w=800&q=80",
    heroImage:
      "https://images.unsplash.com/photo-1620712943543-bcc4688e7485?w=1920&q=80",
    featured: true,
  },
  {
    id: 34,
    slug: "ai-assistant-medtech-field-support",
    title: "Agentic Assistant for MedTech Field Support",
    tagline: "AI-Powered Technical Troubleshooting for Medical Devices",
    clientType: "Medical Devices / Field Engineering",
    industry: "Artificial Intelligence",
    industrySlug: "artificial-intelligence",
    services: ["AI Consulting", "Web App Development"],
    servicesSlugs: ["ai-consulting", "web-app-development"],
    timeline: "5 Months",
    technologies: [
      "GPT-4o",
      "Gemini",
      "Anthropic",
      "Python",
      "React",
      "AWS",
      "Vector DB",
    ],
    challenge:
      "Engineers were slowed by manual document searches, inconsistent troubleshooting approaches, and limited access to resolution history. Overreliance on senior staff and poor knowledge capture made support workflows inefficient and difficult to scale.",
    solution:
      "Built a cloud-based AI assistant that delivers real-time, contextual answers. Integrated manuals, SOPs, and service ticket history for intelligent response. Enabled conversation memory, editable knowledge base, content tagging, and support for multiple LLMs.",
    features: [
      "Real-time contextual answers",
      "Multi-LLM support (GPT-4o, Gemini, Anthropic)",
      "Manual & SOP integration",
      "Service ticket history access",
      "Conversation memory",
      "Editable knowledge base",
      "Content tagging system",
      "Document upload capability",
    ],
    results: [
      {
        value: "60%",
        label: "Time Reduction",
        description: "Faster access to technical information",
      },
      {
        value: "75%",
        label: "Fewer Escalations",
        description: "Reduced routine senior engineer calls",
      },
      {
        value: "95%",
        label: "Consistency",
        description: "Reliable troubleshooting accuracy",
      },
      {
        value: "Scalable",
        label: "Platform",
        description: "Supports future self-service routing",
      },
    ],
    testimonial: {
      quote:
        "The AI assistant has dramatically reduced our field engineers' time spent searching for information and has improved the consistency of our troubleshooting across all service teams.",
      author: "VP, Field Operations",
      role: "Medical Equipment Company",
    },
    image:
      "https://images.unsplash.com/photo-1576091160550-2173dba999ef?w=800&q=80",
    heroImage:
      "https://images.unsplash.com/photo-1559757175-5700dde675bc?w=1920&q=80",
    featured: false,
  },
  {
    id: 35,
    slug: "ai-assistant-legal-tax-compliance",
    title: "AI Assistant for Legal & Tax Compliance",
    tagline: "Citation-Backed Legal Research and Document Automation",
    clientType: "LegalTech / Regulatory Technology",
    industry: "Artificial Intelligence",
    industrySlug: "artificial-intelligence",
    services: ["AI Consulting", "Web App Development"],
    servicesSlugs: ["ai-consulting", "web-app-development"],
    timeline: "6 Months",
    technologies: [
      "GPT-4",
      "Python",
      "React",
      "Node.js",
      "Vector DB",
      "AWS",
      "Legal APIs",
    ],
    challenge:
      "Legal professionals faced fragmented knowledge sources, slow and manual research workflows, and heavy reliance on senior experts for nuanced queries. Existing tools failed to interpret context correctly, leading to inefficiencies and inconsistent drafting quality.",
    solution:
      "Built a full-stack AI assistant for answering plain-English legal and tax queries with verified, contextual responses. Integrated internal and external knowledge sources with features like document drafting, summarization, and research support, achieving 90%+ accuracy.",
    features: [
      "Plain-English legal queries",
      "Citation-backed responses",
      "Document drafting automation",
      "Legal summarization",
      "Research support tools",
      "Ongoing conversation memory",
      "Editable knowledge base",
      "API integration for internal tools",
    ],
    results: [
      {
        value: "90%+",
        label: "Accuracy",
        description: "Validated by legal team",
      },
      {
        value: "70%",
        label: "Time Saved",
        description: "Reduced research and drafting time",
      },
      {
        value: "50%",
        label: "Load Reduction",
        description: "Fewer queries to senior experts",
      },
      {
        value: "Scalable",
        label: "Foundation",
        description: "AI-driven compliance innovation",
      },
    ],
    testimonial: {
      quote:
        "The AI assistant has empowered our junior staff to handle complex queries independently, significantly reducing the load on senior experts while improving our client delivery timelines.",
      author: "Chief Legal Officer",
      role: "LegalTech Startup",
    },
    image:
      "https://images.unsplash.com/photo-1589829545856-d10d557cf95f?w=800&q=80",
    heroImage:
      "https://images.unsplash.com/photo-1450101499163-c8848c66ca85?w=1920&q=80",
    featured: false,
  },
];

export const TESTIMONIALS = [
  {
    id: 1,
    name: "Sarah Johnson",
    role: "CTO",
    company: "TechVentures Inc.",
    content:
      "Infotech.Works delivered an exceptional AI solution that transformed our business operations. Their expertise and dedication exceeded our expectations.",
    avatar:
      "https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=150&q=80",
  },
  {
    id: 2,
    name: "Michael Chen",
    role: "CEO",
    company: "StartupHub",
    content:
      "Working with Infotech.Works was a game-changer for our startup. They helped us build and launch our MVP in record time with outstanding quality.",
    avatar:
      "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=150&q=80",
  },
  {
    id: 3,
    name: "Emily Roberts",
    role: "Product Director",
    company: "GlobalRetail Corp",
    content:
      "The team's technical expertise and commitment to quality made our e-commerce platform a huge success. Highly recommend their services.",
    avatar:
      "https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=150&q=80",
  },
];

export const LEADERSHIP_TEAM = [
  {
    id: 1,
    name: "Nitesh Thacker",
    role: "Co-Founder, CEO",
    image: TEAM_IMAGES.nitesh,
    linkedin: "https://www.linkedin.com/in/niteshthacker/",
    bio: "A seasoned sales and delivery leader with 18+ years in banking technology, driving global growth, closing multi-million deals, and delivering innovative solutions for financial institutions worldwide.",
  },
  {
    id: 2,
    name: "Rohini Thacker",
    role: "Co-Founder, Director",
    image: TEAM_IMAGES.rohini,
    linkedin: "https://www.linkedin.com/in/rohini-thacker-b5aa1121/",
    bio: "Strategic leader driving organizational growth through administrative agility, HR excellence, and transformative L&D. Architecting high-performance operational efficiency for a sustainable business success.",
  },

  {
    id: 3,
    name: "Ankit Choudhury",
    role: "Chief Technology Officer",
    image: TEAM_IMAGES.ankit,
    linkedin: "https://www.linkedin.com/in/ankitchoudhury/",
    deviantart: "https://www.deviantart.com/ankit56",
    chain: "https://ankitchoudhury.com/",
    bio: "A dynamic business and growth professional with experience in fintech and marketing, known for strategic thinking, execution excellence, and driving innovative, scalable business outcomes.",
  },

  {
    id: 4,
    name: "Dilip Maddala",
    role: "VP, Technology",
    image: TEAM_IMAGES.dilip,
    linkedin: "https://www.linkedin.com/in/dilip-maddala-96302a99/",
    bio: "A results-driven professional with experience in technology and business, focused on delivering scalable solutions, driving growth, and creating value through innovation and execution excellence.",
  },

  {
    id: 5,
    name: "Dinesh Bisht",
    role: "VP, Delivery",
    image: TEAM_IMAGES.dinesh,
    linkedin: "https://www.linkedin.com/in/dinesh-singh-bisht/",
    bio: "A growth-focused technology and business leader, driving digital transformation, strategic partnerships, and offshore development initiatives to deliver scalable solutions for global clients.",
  },
  {
    id: 6,
    name: "Pankaj Pagar",
    role: "VP, Operations",
    image: TEAM_IMAGES.pankaj,
    linkedin: "https://www.linkedin.com/in/pankaj-pagar-63869239/",
    bio: "A performance-oriented business professional with expertise in market expansion, client engagement, and revenue acceleration, known for delivering consistent outcomes through strategic planning and execution.",
  },
  {
    id: 7,
    name: "Vikas Aggarwal",
    role: "Mentor & Advisor",
    image: TEAM_IMAGES.vikash,
    linkedin: "https://www.linkedin.com/in/vikasaggarwalatlinkedin/",
    bio: "A trusted advisor with 28+ years of experience in product management and global delivery, known for driving operational excellence, strategic clarity, and impactful business outcomes across industries.",
  },
  {
    id: 8,
    name: "Anjanikumar Shukla",
    role: "Director of Key Initiatives",
    image: TEAM_IMAGES.anjani,
    linkedin: "https://www.linkedin.com/in/anjanikumarshukla/",
    bio: "A strategic leader with 19+ years of experience, driving sales excellence, key account growth, and team alignment while empowering individuals through mentorship and impactful training initiatives.",
  },

  {
    id: 9,
    name: "Tushar Kulkarni",
    role: "Strategic Advisor",
    image: TEAM_IMAGES.tushar,
    linkedin: "https://www.linkedin.com/in/thetushar/",
    chain: "https://bigkittylabs.com/",
    bio: "A visionary entrepreneur and strategic partner leading in building innovative digital solutions, partnering with startups and enterprises to transform ideas into scalable, impactful products.",
  },
];

export const STATS = [
  { value: "100+", label: "Engineers & Specialists" },
  { value: "200+", label: "Products Shipped" },
  { value: "25+", label: "AI-Powered Products" },
  { value: "180+", label: "Mobile Apps Live" },
];

export const JOBS = [
  // {
  //   id: 1,
  //   title: "Technical Project Manager",
  //   department: "Engineering",
  //   location: "On-site",
  //   type: "Full-time",
  //   fullDescription:
  //     "We are looking for a highly skilled and experienced Technical Project Manager to oversee complex, client-facing technical projects. The ideal candidate will bring a strong background in programming, project management, and client interaction.",
  //   responsibilitiesHeading: "Role & Responsibilities:",
  //   responsibilities: [
  //     "Managing end-to-end project lifecycles, including planning, execution, and delivery.",
  //     "Collaborating with clients to define requirements, manage expectations, secure approvals, and oversee milestone invoicing.",
  //     "Providing technical leadership with hands-on expertise in LAMP, ROR, or MEAN stacks.",
  //     "Designing and producing data models, ERDs, and API documentation before development.",
  //     "Setting up and troubleshooting AWS infrastructure, including EC2, RDS, S3, ACM, SES, SNS, IAM, and CI/CD pipelines with containerization.",
  //     "Managing mobile application lifecycles and interacting with Apple and Google app stores for reviews, approvals, and privacy declarations.",
  //     "Overseeing Jira-based project workflows, including epics, timelines, user stories, sprints, backlog, bugs, and sprint velocity estimation.",
  //     "Ensuring seamless partner integrations with platforms such as Stripe, Twilio, Firebase, Google APIs, OAuth/SSO logins, and in-app purchase systems.",
  //     "Leading technical teams, mentoring members, and performing architect-level duties when needed.",
  //     "Managing client expectations and ensuring timely project delivery using existing team resources.",
  //     "Extracting sign-offs, clearing milestone invoices, and maintaining effective client relationships.",
  //   ],
  //   skillsHeading: "Required Skills and Experience:",
  //   skills: [
  //     {
  //       type: "list",
  //       items: [
  //         "Minimum 8 years of hands-on programming experience.",
  //         "At least 4 years in project management roles.",
  //         "At least 4 years in client-facing roles.",
  //       ],
  //     },
  //     {
  //       type: "section",
  //       heading: "Technical Skills:",
  //       items: [
  //         "Expertise in LAMP, ROR, or MEAN stack programming.",
  //         "Hands-on experience with AWS DevOps and infrastructure management.",
  //         "Deep knowledge of mobile app project lifecycles, including IoT integration (preferred).",
  //       ],
  //     },
  //     {
  //       type: "section",
  //       heading: "Project Management Skills:",
  //       items: [
  //         "Proficient in Jira with extensive knowledge of epics, timelines, stories, tasks, and sprint management.",
  //         "Strong grasp of project estimation, velocity, and sprint planning concepts.",
  //       ],
  //     },
  //     {
  //       type: "section",
  //       heading: "Desirable Skills:",
  //       items: [
  //         "Familiarity with third-party integrations like Stripe, Twilio, Firebase, Chart API, and Google API platforms.",
  //         "Experience interacting with app store support teams and filling out privacy declarations.",
  //       ],
  //     },
  //     {
  //       type: "section",
  //       heading: "Soft Skills:",
  //       items: [
  //         "Strong client management capabilities.",
  //         "Excellent communication and leadership skills.",
  //         "Problem-solving mindset with the ability to work within time and resource constraints.",
  //       ],
  //     },
  //   ],

  //   perksHeading: "Perks and Benefits:",
  //   perks: [
  //     "Opportunity to work on cutting-edge projects with high visibility.",
  //     "Collaborative and dynamic work environment that values innovation.",
  //     "Competitive compensation tailored to expertise and experience.",
  //     "Exposure to diverse technologies and industries, including IoT and cloud computing.",
  //     "Hands-on experience with global app store processes and compliance.",
  //     "Professional growth opportunities through challenging technical and managerial responsibilities.",
  //   ],
  // },
  {
    id: 1,
    title: "Chief Business Officer (CBO)",
    department: "Leadership",
    location: "On-site",
    type: "Full-time",
    fullDescription:
      "We are looking for a high-impact Chief Business Officer (CBO) to join us as a core member of our leadership team. This is not a maintenance role — we're seeking a true hunter with a strong international network and a consistent track record of driving multimillion-dollar revenues in product engineering, digital, or AI-led technology services. This is a rare opportunity to co-build, scale, and shape a global growth engine from the ground up.",
    jobDetails: [
      { label: "Job Type", value: "Full-time" },
      { label: "Job Location", value: "Pune (Onsite)" },
      { label: "Experience", value: "10–15 years" },
    ],
    responsibilitiesHeading: "Key Responsibilities:",
    responsibilities: [
      "Drive end-to-end revenue growth across global markets (US, Europe, GCC, etc.)",
      "Leverage your existing network to open doors and close high-value deals.",
      "Own and execute the Go-To-Market (GTM) strategy from 0 → 1 → 10.",
      "Build and expand strategic client relationships into multi-million dollar accounts.",
      "Lead complex deal cycles and negotiate mid-to-large, multi-year contracts.",
      "Collaborate closely with founders and delivery teams to align growth with execution.",
      "Establish a scalable and predictable revenue pipeline.",
    ],
    skillsHeading: "What We're Looking For:",
    skills: [
      {
        type: "list",
        items: [
          "10–15 years of experience in business development/sales within IT services.",
          "Proven success in selling $3M–$10M+ annually.",
          "Strong experience closing deals in the range of $250K–$1M+.",
          "Demonstrated ability to grow accounts into long-term, high-value partnerships.",
          "Background in agile services environments (100–500 employees) where individual ownership drives results.",
          "A well-established international Rolodex and client network.",
          "Entrepreneurial mindset with the ability to build, execute, and scale.",
        ],
      },
    ],
    perksHeading: "Why Join Us?",
    perks: [
      "C-level leadership role with direct founder collaboration.",
      "Opportunity to co-build and shape the company's global growth journey.",
      "Equity participation aligned with long-term success.",
      "Exposure to international markets and high-impact deals.",
      "A culture that values ownership, speed, and execution.",
    ],
    applyInfo: {
      email: "cbo@infotech.works",
      instructions:
        "Please include your updated CV, current CTC (fixed + variable), expected compensation range, notice period, and highlights of recent deal achievements (value, geography, and type — no confidential details required).",
    },
  },
  {
    id: 2,
    title: "Technical Pre-Sales Consultant (EST Shift)",
    department: "Pre-Sales",
    location: "Remote / Hybrid",
    type: "Full-time",
    fullDescription:
      "At Infotech.Works, we move fast—from idea to MVP in weeks, not months. We’re looking for a highly technical pre-sales consultant who can lead client conversations, break down complex requirements in real time, and translate them into practical, scalable solutions—often within a single call. This is a hands-on, thinking-on-your-feet role, not a traditional BA or documentation-heavy position. ",
    jobDetails: [
      { label: "Job Location", value: "Pune (Remote/Hybrid)" },
      { label: "Timing", value: "6:00 PM – 3:00 AM IST (US EST overlap)" },
      { label: "Experience", value: "5–10 years" },
      { label: "Job Type", value: "Full-time" },
    ],
    responsibilitiesHeading: "What You'll Do:",
    responsibilities: [
      "Lead discovery calls with US clients and drive technical conversations.",
      "Translate vague ideas into clear system architecture and solution flows live.",
      "Create mind maps / solution outlines during or immediately after calls.",
      "Recommend tech stacks, APIs, integrations, and data models.",
      "Identify risks, edge cases, and trade-offs early.",
      "Turn discussions into proposals or solution summaries within 24 hours.",
      "Act as a bridge between client expectations and engineering execution.",
    ],
    skillsHeading: "What We're Looking For:",
    skills: [
      {
        type: "list",
        items: [
          "Strong fundamentals in system design, database design, and APIs.",
          "Ability to think structurally and respond in real time.",
          "Comfortable discussing end-to-end architecture (frontend, backend, integrations, cloud).",
          "Strong communication — can simplify complex ideas for clients.",
          "Hands-on mindset — can sketch flows, schemas, and solutions quickly.",
        ],
      },
      {
        type: "section",
        heading: "Ideal Background:",
        items: [
          "5–10 years of experience.",
          "Started as a developer, now operating as a solution architect / consultant.",
          "Experience in startups, product companies, or tech consulting.",
          "Exposure to AI/LLMs, APIs, and modern stacks is a plus.",
        ],
      },
    ],
    perksHeading: "One-Line Expectation:",
    perks: [
      "You should be able to run a client call independently and walk out with a solution we can propose the next day.",
    ],
    notForYouHeading: "This Role Is Not For You If:",
    notForYou: [
      "You need time after calls to “figure things out",
      "You prefer deep coding over client-facing problem solving ",
      "You rely heavily on documentation before solutioning ",
    ],
  },
  {
    id: 3,
    title: "Technical Project Manager - AI First Engineering",
    department: "Engineering",
    location: "On-site",
    type: "Full-time",
    fullDescription:
      "We are looking for a Technical Project Manager who lives and breathes AI-assisted engineering. At our company, we don’t just manage projects — we build, architect, and ship solutions fast using modern AI development tools. If you are someone who uses Claude, Cursor, Copilot, or Codex daily to prototype, architect, debug, and accelerate engineering work, this role will feel natural to you. We believe the future of engineering is AI-accelerated delivery where solutions are designed in hours instead of days. ",
    jobDetails: [
      { label: "Experience", value: "4+ years TPM, 4+ years client-facing" },
      { label: "Job Type", value: "Full-time" },
      { label: "Job Location", value: "On-site" },
    ],
    responsibilitiesHeading: "What You Will Do:",
    responsibilities: [
      "Own end-to-end delivery of complex client projects.",
      "Work directly with clients to define requirements and architecture.",
      "Design system architecture including ERDs, APIs, and integrations.",
      "Lead teams building applications using LAMP, Ruby on Rails, or MEAN/MERN stacks.",
      "Guide engineering teams using AI-assisted development workflows.",
      "Oversee infrastructure on AWS (EC2, RDS, S3, IAM, CI/CD).",
      "Manage third-party integrations such as Stripe, Twilio, Firebase, and Google APIs.",
      "Oversee mobile app releases and manage Apple / Google app store approvals.",
      "Run delivery processes using Jira, Agile, and sprint planning.",
    ],
    skillsHeading: "We Are Looking For Someone Who:",
    skills: [
      {
        type: "list",
        items: [
          "Has 4+ years of Technical Project Management experience.",
          "Has 4+ years of client-facing delivery experience.",
          "Is comfortable architecting systems and solving technical problems hands-on.",
          "Uses AI tools regularly to accelerate development and testing.",
          " Understands AWS infrastructure and modern DevOps workflows.",
          "Can lead engineering teams while maintaining technical depth.",
        ],
      },
      {
        type: "section",
        heading: "Technologies You Should Be Comfortable With:",
        items: [
          "LAMP / Ruby on Rails / MEAN / MERN",
          "AWS (EC2, RDS, S3, IAM, SES/SNS)",
          "CI/CD pipelines and Docker (preferred)",
          " Integrations: Stripe, Twilio, Firebase, OAuth/SSO ",
        ],
      },
    ],
    perksHeading: "Why This Role Is Different:",
    perks: [
      "AI-first engineering environment where teams deliver software 3-5x faster without compromising quality.",
      "You will experiment, automate, and constantly push engineering productivity forward.",
      "Work on cutting-edge projects with real architectural ownership.",
    ],
    applyNote:
      "When applying, please mention in your CV the AI tools you use in your daily workflow.",
  },

  {
    id: 4,
    title: "Technical Lead - AI-First Approach",
    department: "Engineering",
    location: "On-site",
    type: "Full-time",
    fullDescription:
      "At Infotech.Works, we partner with startups and innovators to turn raw ideas into working products—fast. Our projects typically move from idea to deployment in 2–6 months, starting with rapid discovery and moving quickly into execution. We’re looking for a Technical Lead who combines deep engineering fundamentals with AI-powered speed—someone who can design solid systems, make the right architectural trade-offs, and still ship fast. This role is for someone who can think deeply and execute quickly.",
    jobDetails: [
      { label: "Experience", value: "7-10 years" },
      { label: "Job Type", value: "Full-time" },
      { label: "Job Location", value: "On-site" },
    ],
    responsibilitiesHeading: "What You'll Do:",
    responsibilities: [
      "Translate business ideas into robust technical architectures and system designs.",
      "Lead discovery sessions and create structured solution blueprints.",
      "Design and build MVPs that are fast to launch but don't collapse at scale.",
      "Make key decisions on data models, system boundaries, API contracts, and performance trade-offs.",
      "Guide developers through clean implementation, code quality, and design thinking.",
      "Introduce reusable patterns and standardized architecture approaches.",
      "Use AI tools to accelerate development — without compromising engineering quality.",
    ],
    skillsHeading: "Core Requirements:",
    skills: [
      {
        type: "section",
        heading: "Database Design:",
        items: [
          "Schema design (normalized vs denormalized).",
          "Indexing strategies and query optimization.",
          "Handling large datasets and performance bottlenecks.",
          "Designing for reporting, analytics, and scalability.",
          "Writing efficient SQL and understanding execution plans.",
        ],
      },
      {
        type: "section",
        heading: "System Design & Architecture:",
        items: [
          "Designing scalable, modular, and maintainable systems.",
          "Clear understanding of monolith vs microservices (and when NOT to over-engineer).",
          "API design and contract-first thinking.",
          "Caching strategies, queues, background jobs.",
          "Ability to design systems that evolve from MVP → scale without rework.",
        ],
      },

      {
        type: "section",
        heading: "Problem Solving :",
        items: [
          "Ability to break down ambiguous problems into structured solutions.",
          "Strong debugging and performance optimization skills.",
        ],
      },
      {
        type: "section",
        heading: "AI-First Mindset:",
        items: [
          "Daily use of tools like ChatGPT, GitHub Copilot, Cursor.",
          "Ability to learn new stacks quickly using AI-assisted workflows.",
          "Comfortable integrating APIs like OpenAI API into products.",
          "Use AI for rapid prototyping, debugging, code generation, and architecture exploration.",
        ],
      },
      {
        type: "section",
        heading: "Technology Expectations:",
        items: [
          "Backend: Laravel (PHP), Node.js (Express/Nest), or Python (Django/FastAPI).",
          "Frontend: React / Next.js / Vue.",
          "Cloud & DevOps: AWS (EC2, RDS, S3, IAM, SES, CloudWatch), Docker, CI/CD, Git.",
          "Databases: MySQL / PostgreSQL (must-have); NoSQL exposure is a plus.",
        ],
      },
    ],
    perksHeading: "Who You Are?",
    perks: [
      "You understand why things work, not just how to use them .",
      "You can design systems from scratch, not just assemble components .",
      "You are comfortable with ambiguity but bring structure quickly.",
      "You move fast—but never compromise on core design quality.",
      "You use AI as a multiplier, not a crutch .",
    ],
  },
  {
    id: 5,
    title: "PHP (Laravel) Developer",
    department: "Engineering",
    location: "On-site",
    type: "Full-time",

    fullDescription:
      "We seek an experienced, full stack Laravel developer with strong PHP development skills to lead the development and maintenance of our applications. This role involves working closely with other front-end developers, UX/UI designers, and key stakeholders to ensure application security, scalability, and performance. ",

    jobDetails: [
      { label: "Joining", value: "Immediate" },
      { label: "Job Type", value: "Full-time" },
      { label: "Job Location", value: "Balewadi, Pune" },
      { label: "Experience", value: "3+ years" },
    ],
    skillsHeading: "Must-Have Skills:",

    skills: [
      {
        type: "list",
        items: [
          "Experience: 3 or 3+ years of PHP development with a focus on the Laravel framework and MySQL.",
          "Proficiency: Advanced understanding of HTML5, CSS3, Bootstrap 5, JQuery, and AJAX.",
          "Tools: Strong experience with Git for version control, JIRA for project management, and AWS SDK for cloud integration.",
          "Payment Integration: Proven experience with at least one payment gateway (e.g., Stripe, Braintree, Plaid, Razorpay).",
          "Third-Party APIs: Familiarity with multiple third-party integrations, including Firebase, Google Maps, and email/SMS services.",
        ],
      },
    ],
    desirableHeading: "Desirable:",
    desirable: [
      "More than one Databases (MySQL, Postgres, MongoDB)",
      "Knowledge of AWS basic services (S3, EC2, RDS, SES)",
      "Rest API with the ability to create documentation in Apiary",
      "Working with encryption, hashing, and encoding (SES, SHA, RSA standards)",
      "Angular JS OR Node JS will be an added advantage",
      "Stripe Payment gateway integration",
    ],
  },
];

export const EVENTS = [
  {
    id: 1,
    title: "AI Innovation Summit 2025",
    date: "September 15, 2025",
    location: "San Francisco, CA",
    description:
      "Join us for our annual AI summit featuring industry leaders and cutting-edge demos",
    image:
      "https://images.unsplash.com/photo-1540575467063-178a50c2df87?w=400&q=80",
  },
  {
    id: 2,
    title: "Tech Talks: Future of Mobile",
    date: "October 5, 2025",
    location: "Virtual Event",
    description:
      "Monthly tech talk series exploring mobile development trends and best practices",
    image:
      "https://images.unsplash.com/photo-1591115765373-5207764f72e7?w=400&q=80",
  },
  {
    id: 3,
    title: "Hackathon: Build for Good",
    date: "November 20-22, 2025",
    location: "New York, NY",
    description:
      "48-hour hackathon focused on social impact projects with amazing prizes",
    image:
      "https://images.unsplash.com/photo-1504384308090-c894fdcc538d?w=400&q=80",
  },
];

export const BLOGS = [
  {
    id: 1,
    slug: "we-just-hosted-our-first-all-hands-heres-why-it-matters",
    title: "We Just Hosted Our First All-Hands — Here's Why It Matters",
    excerpt:
      "We're proud of this moment. And even more excited for what it sets in motion. Here's to many more all-hands—and to building something meaningful, together.",
    author: "Infotech.Works Team",
    date: "February 18, 2026",
    category: "Events",
    image:
      "https://images.unsplash.com/photo-1552664730-d307ca884978?w=800&q=80",
    readTime: "4 min read",
    content: `
      <p>At Infotech.Works, we believe that building a great company starts with building a great team. That's why we recently hosted our very first All-Hands meeting—a milestone moment that brought together every member of our organization, from developers to designers, from project managers to leadership.</p>
      
      <h2>Why All-Hands Meetings Matter</h2>
      <p>In a world where remote work and distributed teams are becoming the norm, creating moments of genuine connection has never been more important. An All-Hands meeting isn't just about sharing updates or presenting metrics—it's about reinforcing our shared mission and celebrating our collective achievements.</p>
      
      <h2>What We Covered</h2>
      <p>During our first All-Hands, we discussed our company vision, celebrated recent project wins, recognized outstanding team members, and outlined our roadmap for the coming quarters. But more importantly, we created space for open dialogue, questions, and genuine connection.</p>
      
      <h2>Looking Ahead</h2>
      <p>This first All-Hands was just the beginning. We're committed to making these gatherings a regular part of our culture—because when everyone understands where we're headed and why, we move faster and stronger together.</p>
      
      <p>Here's to many more all-hands—and to building something meaningful, together.</p>
    `,
  },
  {
    id: 2,
    slug: "how-fintech-companies-are-revolutionizing-mobile-app-development",
    title: "How Fintech Companies Are Revolutionizing Mobile App Development",
    excerpt:
      "We specialize in building secure, high-performance mobile applications tailored for fast-moving industries like Fintech.",
    author: "Infotech.Works Team",
    date: "January 9, 2026",
    category: "Resources",
    image:
      "https://images.unsplash.com/photo-1563986768609-322da13575f3?w=800&q=80",
    readTime: "6 min read",
    content: `
      <p>The financial technology sector is experiencing unprecedented growth, and at the heart of this revolution is mobile app development. Today's consumers expect seamless, secure, and intuitive mobile experiences for managing their finances.</p>
      
      <h2>The Fintech Mobile Revolution</h2>
      <p>From digital banking to cryptocurrency trading, peer-to-peer payments to investment platforms, fintech mobile apps are transforming how people interact with money. These apps must deliver enterprise-grade security while maintaining consumer-friendly usability.</p>
      
      <h2>Key Challenges in Fintech App Development</h2>
      <ul>
        <li><strong>Security First:</strong> Implementing multi-layer authentication, encryption, and fraud detection</li>
        <li><strong>Regulatory Compliance:</strong> Navigating complex financial regulations across different markets</li>
        <li><strong>Real-Time Processing:</strong> Handling high-volume transactions with minimal latency</li>
        <li><strong>User Experience:</strong> Making complex financial operations simple and intuitive</li>
      </ul>
      
      <h2>Our Approach</h2>
      <p>At Infotech.Works, we specialize in building secure, high-performance mobile applications tailored for fast-moving industries like Fintech. Our team combines deep technical expertise with financial domain knowledge to deliver solutions that meet the highest standards of security and performance.</p>
      
      <p>Whether you're a startup disrupting traditional banking or an established institution modernizing your digital offerings, we're here to help you build the future of finance.</p>
    `,
  },
  {
    id: 3,
    slug: "developing-e-commerce-mobile-and-web-applications",
    title: "Developing E-Commerce Mobile and Web Applications",
    excerpt:
      "At infotech.works, we don't just build apps—we build experiences that convert. Whether you're just starting out or looking to modernize your current platform, we're here to bring your e-commerce vision to life.",
    author: "Infotech.Works Team",
    date: "December 22, 2025",
    category: "Resources",
    image:
      "https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?w=800&q=80",
    readTime: "7 min read",
    content: `
      <p>E-commerce has evolved far beyond simple online catalogs. Today's successful platforms deliver personalized, omnichannel experiences that meet customers wherever they are—on web, mobile, or in-store.</p>
      
      <h2>The Modern E-Commerce Landscape</h2>
      <p>With mobile commerce accounting for over 70% of e-commerce traffic, having a robust mobile strategy is no longer optional. Consumers expect fast load times, intuitive navigation, seamless checkout, and personalized recommendations.</p>
      
      <h2>Essential Features for E-Commerce Success</h2>
      <ul>
        <li><strong>Mobile-First Design:</strong> Responsive layouts that prioritize the mobile experience</li>
        <li><strong>Smart Search & Filters:</strong> AI-powered product discovery and recommendations</li>
        <li><strong>Streamlined Checkout:</strong> One-click purchasing and multiple payment options</li>
        <li><strong>Inventory Management:</strong> Real-time stock updates across all channels</li>
        <li><strong>Analytics & Insights:</strong> Data-driven decision making for optimization</li>
      </ul>
      
      <h2>Why Choose Infotech.Works</h2>
      <p>At Infotech.Works, we don't just build apps—we build experiences that convert. Our team has delivered e-commerce solutions for businesses of all sizes, from boutique startups to enterprise retailers.</p>
      
      <p>Whether you're just starting out or looking to modernize your current platform, we're here to bring your e-commerce vision to life.</p>
    `,
  },
  {
    id: 4,
    slug: "partner-with-infotech-works-for-endless-opportunities",
    title: "Partner with Infotech.Works for Endless Opportunities",
    excerpt:
      "At Infotech.Works, we pride ourselves on being more than just a software development company—we are your innovation partner, product enabler, and growth accelerator.",
    author: "Infotech.Works Team",
    date: "November 11, 2025",
    category: "News",
    image:
      "https://images.unsplash.com/photo-1600880292203-757bb62b4baf?w=800&q=80",
    readTime: "5 min read",
    content: `
      <p>In today's rapidly evolving digital landscape, finding the right technology partner can make the difference between market leadership and playing catch-up. At Infotech.Works, we pride ourselves on being more than just a software development company.</p>
      
      <h2>Your Innovation Partner</h2>
      <p>We work alongside your team to identify opportunities, solve complex challenges, and bring innovative ideas to life. Our collaborative approach ensures that every solution we build is aligned with your business goals and market needs.</p>
      
      <h2>Your Product Enabler</h2>
      <p>From initial concept to market launch, we provide end-to-end product development services. Our expertise spans mobile apps, web platforms, cloud infrastructure, and emerging technologies like AI and blockchain.</p>
      
      <h2>Your Growth Accelerator</h2>
      <p>We understand that speed matters. Our agile methodologies and experienced teams help you move faster without sacrificing quality. Whether you need to scale quickly or pivot strategically, we're ready to support your growth journey.</p>
      
      <h2>Partnership Models</h2>
      <p>We offer flexible engagement models to suit your needs—from dedicated development teams to project-based engagements to technology consulting. Let's explore how we can work together to unlock endless opportunities for your business.</p>
    `,
  },
  {
    id: 5,
    slug: "how-infotech-works-is-helping-global-clients-embrace-the-mobile-future",
    title:
      "How Infotech.Works is Helping Global Clients Embrace the Mobile Future",
    excerpt:
      "At Infotech.Works, we believe that the right technology can transform businesses. Partner with us and drive tomorrow's innovation today!",
    author: "Infotech.Works Team",
    date: "October 3, 2025",
    category: "Resources",
    image:
      "https://images.unsplash.com/photo-1512941937669-90a1b58e7e9c?w=800&q=80",
    readTime: "6 min read",
    content: `
      <p>The mobile revolution is reshaping every industry, and businesses worldwide are racing to deliver exceptional mobile experiences. At Infotech.Works, we're proud to be at the forefront of this transformation, helping clients across the globe build mobile solutions that drive real business results.</p>
      
      <h2>Global Reach, Local Expertise</h2>
      <p>Our team works with clients across North America, Europe, Asia, and beyond. We combine global best practices with deep understanding of local markets to deliver solutions that resonate with users everywhere.</p>
      
      <h2>Industries We Serve</h2>
      <ul>
        <li><strong>Healthcare:</strong> Patient engagement apps, telemedicine platforms, health tracking solutions</li>
        <li><strong>Finance:</strong> Mobile banking, investment platforms, payment solutions</li>
        <li><strong>Retail:</strong> E-commerce apps, loyalty programs, in-store experiences</li>
        <li><strong>Logistics:</strong> Fleet management, delivery tracking, warehouse optimization</li>
      </ul>
      
      <h2>Our Mobile Technology Stack</h2>
      <p>We leverage cutting-edge technologies including React Native, Flutter, Swift, and Kotlin to build cross-platform and native applications that deliver exceptional performance and user experience.</p>
      
      <p>At Infotech.Works, we believe that the right technology can transform businesses. Partner with us and drive tomorrow's innovation today!</p>
    `,
  },
  {
    id: 6,
    slug: "react-native-revolutionizing-multi-platform-mobile-app-development",
    title:
      "React Native: Revolutionizing Multi-Platform Mobile App Development",
    excerpt:
      "At Infotech.Works, we specialize in helping startups, small, medium and big enterprises leverage React Native to create exceptional cross-platform applications.",
    author: "Infotech.Works Team",
    date: "September 17, 2025",
    category: "Resources",
    image:
      "https://images.unsplash.com/photo-1555066931-4365d14bab8c?w=800&q=80",
    readTime: "8 min read",
    content: `
      <p>React Native has emerged as one of the most powerful frameworks for building mobile applications that work seamlessly across iOS and Android platforms. By enabling code sharing while maintaining native performance, React Native is revolutionizing how companies approach mobile development.</p>
      
      <h2>Why React Native?</h2>
      <ul>
        <li><strong>Code Reusability:</strong> Write once, deploy on both iOS and Android with up to 90% code sharing</li>
        <li><strong>Native Performance:</strong> React Native compiles to native components, ensuring smooth performance</li>
        <li><strong>Faster Development:</strong> Hot reloading and a vast ecosystem of libraries accelerate development</li>
        <li><strong>Cost Efficiency:</strong> Single codebase means reduced development and maintenance costs</li>
        <li><strong>Strong Community:</strong> Backed by Facebook and a thriving open-source community</li>
      </ul>
      
      <h2>Success Stories</h2>
      <p>Major companies like Facebook, Instagram, Airbnb, and Uber Eats have leveraged React Native to deliver world-class mobile experiences. The framework continues to evolve with new features and performance improvements.</p>
      
      <h2>Our React Native Expertise</h2>
      <p>At Infotech.Works, we specialize in helping startups, small, medium and big enterprises leverage React Native to create exceptional cross-platform applications. Our experienced team has delivered dozens of React Native projects across various industries.</p>
      
      <p>Ready to build your next mobile app with React Native? Let's talk about how we can bring your vision to life.</p>
    `,
  },
  {
    id: 7,
    slug: "infotech-works-announces-mobile-dedicated-development-centre",
    title: "Infotech.Works Announces A Mobile Dedicated Development Centre",
    excerpt:
      "We are excited to announce the launch of our new Mobile Dedicated Development Centre—a specialized facility focused entirely on mobile application development and innovation.",
    author: "Infotech.Works Team",
    date: "August 6, 2025",
    category: "News",
    image:
      "https://images.unsplash.com/photo-1497215842964-222b430dc094?w=800&q=80",
    readTime: "4 min read",
    content: `
      <p>We are excited to announce the launch of our new Mobile Dedicated Development Centre (MDC)—a specialized facility focused entirely on mobile application development and innovation.</p>
      
      <h2>What is the Mobile Dedicated Development Centre?</h2>
      <p>Our MDC brings together the best mobile development talent, cutting-edge tools, and proven methodologies under one roof. This dedicated center allows us to deliver even higher quality mobile solutions with faster turnaround times.</p>
      
      <h2>Key Capabilities</h2>
      <ul>
        <li><strong>Cross-Platform Development:</strong> Expert teams in React Native and Flutter</li>
        <li><strong>Native Development:</strong> Specialized iOS (Swift) and Android (Kotlin) developers</li>
        <li><strong>UI/UX Design:</strong> Mobile-first design specialists</li>
        <li><strong>Quality Assurance:</strong> Dedicated mobile testing and QA teams</li>
        <li><strong>DevOps:</strong> CI/CD pipelines optimized for mobile deployment</li>
      </ul>
      
      <h2>Benefits for Our Clients</h2>
      <p>The MDC enables us to offer dedicated mobile development teams that can scale with your needs. Whether you need a small team for a startup MVP or a large team for enterprise-scale projects, our MDC model provides flexibility and expertise.</p>
      
      <p>Contact us to learn more about how our Mobile Dedicated Development Centre can accelerate your mobile strategy.</p>
    `,
  },
  {
    id: 8,
    slug: "infotech-works-unveils-new-state-of-the-art-office-in-pune-india",
    title:
      "Infotech.Works Unveils Its New State-of-the-Art Office in Pune, India",
    excerpt:
      "We are thrilled to announce the grand opening of our new office in Pune, India!",
    author: "Infotech.Works Team",
    date: "July 25, 2025",
    category: "News",
    image:
      "https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?w=800&q=80",
    readTime: "3 min read",
    content: `
      <p>We are thrilled to announce the grand opening of our new state-of-the-art office in Pune, India! This milestone marks an exciting new chapter in our company's growth and our commitment to delivering world-class technology solutions.</p>
      
      <h2>A Space Designed for Innovation</h2>
      <p>Our new Pune office has been thoughtfully designed to foster creativity, collaboration, and innovation. The modern workspace features open floor plans, dedicated collaboration zones, quiet focus areas, and cutting-edge technology infrastructure.</p>
      
      <h2>Facility Highlights</h2>
      <ul>
        <li><strong>Modern Workspaces:</strong> Ergonomic furniture and natural lighting throughout</li>
        <li><strong>Collaboration Zones:</strong> Meeting rooms and breakout spaces for team discussions</li>
        <li><strong>Tech Infrastructure:</strong> High-speed connectivity and state-of-the-art equipment</li>
        <li><strong>Employee Amenities:</strong> Cafeteria, recreation areas, and wellness facilities</li>
        <li><strong>Green Design:</strong> Sustainable building practices and energy-efficient systems</li>
      </ul>
      
      <h2>Growing Our Team</h2>
      <p>This new facility positions us to significantly expand our team in Pune. We're actively hiring talented engineers, designers, and project managers who want to work on challenging projects for global clients.</p>
      
      <p>We invite our clients, partners, and the Pune tech community to visit our new office. Here's to new beginnings and continued growth!</p>
    `,
  },
];

// AI Consulting Page Data
export const AI_CONSULTING_DATA = {
  hero: {
    title: "AI Consulting",
    subtitle: "Find Where AI Moves the Needle — Then Build It.",
    features: [
      "AI Strategy & Roadmap Development",
      "Technology Assessment & Selection",
      "Implementation Planning & Execution",
      "ROI Analysis & Optimization",
      "Risk Assessment & Mitigation",
    ],
  },
  stats: [
    { value: "30+", label: "AI Engagements" },
    { value: "9+", label: "Years Engineering" },
    { value: "5+", label: "Industries Served" },
    { value: "12+", label: "AI Consulting Leaders" },
  ],
  overview: {
    title: "Transform Your Business with AI",
    description:
      "The hardest part of AI isn't the technology — it's knowing which problems are actually worth solving with it. We've helped businesses automate workflows that took skilled specialists hours to complete, add reliable intelligence to existing products, and build AI systems that hold up in production. We bring that real-world experience to your strategy — so you invest where it pays off, not where it looks good in a presentation.",
    image:
      "https://images.unsplash.com/photo-1552664730-d307ca884978?w=800&q=80",
  },
  serviceTypes: [
    {
      title: "AI Strategy Development",
      description:
        "We map your workflows, find where AI creates genuine leverage, and build a prioritised roadmap — so you invest in AI that pays off, not AI that sounds impressive.",
      icon: "Target",
      features: [
        "Business Goal Alignment",
        "Technology Roadmap",
        "Resource Planning",
        "Timeline Development",
      ],
    },
    {
      title: "Technology Assessment",
      description:
        "We evaluate LLMs, APIs, and platforms — OpenAI, LangChain, AWS AI, Azure OpenAI, and more — and recommend what genuinely fits your stack, budget, and delivery timeline.",
      icon: "Search",
      features: [
        "Platform Evaluation",
        "Vendor Analysis",
        "Cost-Benefit Analysis",
        "Integration Assessment",
      ],
    },
    {
      title: "Data Strategy",
      description:
        "AI is only as good as the data feeding it. We audit what you have, identify gaps, and design the pipelines and governance that make AI outputs reliable.",
      icon: "Database",
      features: [
        "Data Audit",
        "Quality Assessment",
        "Governance Framework",
        "Pipeline Design",
      ],
    },
    {
      title: "Implementation Planning",
      description:
        "We scope, sequence, and de-risk your AI rollout — clear milestones, realistic timelines, and contingency planning baked in from day one.",
      icon: "ClipboardList",
      features: [
        "Project Scoping",
        "Resource Allocation",
        "Risk Mitigation",
        "Change Management",
      ],
    },
    {
      title: "AI Governance",
      description:
        "We build responsible AI practices into every engagement — guardrails, bias detection, explainability, and compliance frameworks so your AI earns user trust and withstands scrutiny.",
      icon: "Shield",
      features: [
        "Ethics Guidelines",
        "Compliance Framework",
        "Bias Detection",
        "Transparency Standards",
      ],
    },
    {
      title: "Training & Enablement",
      description:
        "We don't just hand over a system and leave. We make sure your team understands it, trusts it, and can evolve it — from executive briefings to developer handoffs.",
      icon: "GraduationCap",
      features: [
        "Executive Workshops",
        "Technical Training",
        "Best Practices",
        "Ongoing Support",
      ],
    },
  ],
  clientGrowth: {
    title: "Accelerate Your Growth with AI",
    stats: [
      {
        value: "60%",
        label: "Faster Time to Market",
        description:
          "Reduce product development cycles with AI-powered automation",
      },
      {
        value: "45%",
        label: "Operational Efficiency",
        description: "Streamline processes and reduce manual workload",
      },
      {
        value: "3.5x",
        label: "Revenue Growth",
        description: "Unlock new revenue streams with AI-driven insights",
      },
      {
        value: "50%",
        label: "Cost Savings",
        description: "Optimize resources and reduce operational costs",
      },
    ],
  },
  expertise: [
    {
      title: "LLM Integration & Orchestration",
      image:
        "https://images.unsplash.com/photo-1645839057098-5ea8761a6b09?w=500&auto=format&fit=crop&q=60&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxzZWFyY2h8M3x8bGFyZ2UlMjBsYW5ndWFnZSUyMG1vZGVsc3xlbnwwfHwwfHx8MA%3D%3D",
    },
    {
      title: "Agentic Workflow Design",
      image:
        "https://images.unsplash.com/photo-1620712943543-bcc4688e7485?w=500&auto=format&fit=crop&q=60&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxzZWFyY2h8Mnx8QWdlbnRpYyUyMEFJfGVufDB8fDB8fHww",
    },
    {
      title: "Model Context Protocol (MCP)",
      image:
        "https://images.unsplash.com/photo-1664527184293-8ab968c4aa25?w=500&auto=format&fit=crop&q=60&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxzZWFyY2h8MTF8fE1vZGVsJTIwQ29udGV4dCUyMFByb3RvY29sJTIwKE1DUCl8ZW58MHx8MHx8fDA%3D",
    },
    {
      title: "AI Guardrails & Anti-Hallucination",
      image:
        "https://images.unsplash.com/photo-1526374965328-7f61d4dc18c5?w=300&q=80",
    },
    {
      title: "RAG Architectures",
      image:
        "https://images.unsplash.com/photo-1664526936886-67d4e7ff743c?w=500&auto=format&fit=crop&q=60&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxzZWFyY2h8M3x8UmV0cmlldmFsJTIwQXVnbWVudGVkJTIwR2VuZXJhdGlvbiUyMEFyY2hpdGVjdHVyZXxlbnwwfHwwfHx8MA%3D%3D",
    },

    {
      title: "Natural Language Processing",
      image:
        "https://images.unsplash.com/photo-1677442135136-760c813028c0?w=500&auto=format&fit=crop&q=60&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxzZWFyY2h8MTh8fE5hdHVyYWwlMjBMYW5ndWFnZSUyMFByb2Nlc3Npbmd8ZW58MHx8MHx8fDA%3D",
    },

    {
      title: "Prompt Engineering",
      image:
        "https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=300&q=80",
    },
  ],
  technologies: [
    {
      name: "TensorFlow",
      icon: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/tensorflow/tensorflow-original.svg",
    },
    {
      name: "PyTorch",
      icon: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/pytorch/pytorch-original.svg",
    },
    {
      name: "Python",
      icon: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/python/python-original.svg",
    },
    {
      name: "AWS",
      icon: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/amazonwebservices/amazonwebservices-original-wordmark.svg",
    },
    {
      name: "Google Cloud",
      icon: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/googlecloud/googlecloud-original.svg",
    },
    {
      name: "Azure",
      icon: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/azure/azure-original.svg",
    },
    {
      name: "Docker",
      icon: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/docker/docker-original.svg",
    },
    {
      name: "Kubernetes",
      icon: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/kubernetes/kubernetes-plain.svg",
    },
    {
      name: "MongoDB",
      icon: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/mongodb/mongodb-original.svg",
    },
    {
      name: "PostgreSQL",
      icon: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/postgresql/postgresql-original.svg",
    },
  ],
  whyChooseUs: [
    {
      title: "Proven Expertise",
      description: "150+ successful AI projects across diverse industries",
      icon: "Award",
    },
    {
      title: "End-to-End Support",
      description: "From strategy to implementation and beyond",
      icon: "Layers",
    },
    {
      title: "Industry Knowledge",
      description: "Deep understanding of 25+ industry verticals",
      icon: "Building",
    },
    {
      title: "Agile Approach",
      description: "Flexible methodology that adapts to your needs",
      icon: "Zap",
    },
    {
      title: "Dedicated Teams",
      description: "Expert consultants committed to your success",
      icon: "Users",
    },
    {
      title: "Measurable Results",
      description: "Data-driven approach with clear KPIs",
      icon: "BarChart",
    },
  ],
  engagementModels: [
    {
      title: "Discovery Workshop",
      description:
        "Intensive 2-week engagement to assess AI opportunities and create an actionable roadmap",
      duration: "2 Weeks",
      image:
        "https://images.unsplash.com/photo-1552664730-d307ca884978?w=400&q=80",
    },
    {
      title: "Strategic Advisory",
      description:
        "Ongoing consulting engagement with dedicated AI advisors for continuous guidance",
      duration: "3-6 Months",
      image:
        "https://images.unsplash.com/photo-1600880292203-757bb62b4baf?w=400&q=80",
    },
    {
      title: "Implementation Partner",
      description:
        "Full-scale partnership from strategy through deployment and optimization",
      duration: "6-12 Months",
      image:
        "https://images.unsplash.com/photo-1522071820081-009f0129c71c?w=400&q=80",
    },
  ],
  agileProcess: {
    title: "Our Agile Consulting Process",
    description:
      "We follow a structured yet flexible approach to deliver maximum value at every stage",
    steps: [
      {
        phase: "Discovery",
        description:
          "Understand your business, challenges, and AI opportunities",
      },
      {
        phase: "Assessment",
        description:
          "Evaluate data readiness, technology landscape, and team capabilities",
      },
      {
        phase: "Strategy",
        description: "Develop comprehensive AI roadmap with clear milestones",
      },
      {
        phase: "Planning",
        description:
          "Create detailed implementation plans and resource allocation",
      },
      {
        phase: "Execution",
        description:
          "Guide implementation with hands-on support and best practices",
      },
      {
        phase: "Optimization",
        description: "Continuous improvement and performance monitoring",
      },
    ],
    image:
      "https://images.unsplash.com/photo-1531403009284-440f080d1e12?w=600&q=80",
  },
  faq: [
    {
      question: "How long does a typical AI consulting engagement take?",
      answer:
        "The duration varies based on scope and complexity. Discovery workshops typically take 2 weeks, while comprehensive strategy engagements range from 3-6 months. Full implementation partnerships can extend to 12 months or more.",
    },
    {
      question: "Do we need existing AI expertise to work with you?",
      answer:
        "Not at all. We work with organizations at all stages of AI maturity. Our consultants will assess your current capabilities and help build the knowledge and infrastructure needed for successful AI adoption.",
    },
    {
      question: "How do you ensure AI solutions align with our business goals?",
      answer:
        "We start every engagement with a thorough understanding of your business objectives. Our strategy development process ensures AI initiatives are directly tied to measurable business outcomes and KPIs.",
    },
    {
      question: "What industries do you have experience in?",
      answer:
        "We've delivered AI solutions across 25+ industries including healthcare, finance, retail, manufacturing, logistics, and more. Our consultants bring deep domain expertise to every engagement.",
    },
    {
      question: "How do you handle data privacy and security?",
      answer:
        "Data security is paramount in all our engagements. We follow industry best practices, comply with regulations like GDPR and HIPAA, and implement robust security measures throughout the consulting process.",
    },
    {
      question: "What's the typical ROI from AI consulting?",
      answer:
        "Our clients typically see 3-5x ROI within the first year of implementation. This includes cost savings from automation, revenue growth from new capabilities, and efficiency gains across operations.",
    },
  ],
};

// AGENTIC AI DATA
export const AGENTIC_AI_DATA = {
  hero: {
    title: "Agentic AI",
    subtitle: "AI That Acts, Not Just Answers.",
    features: [
      "Multi-Agent System Architecture",
      "Autonomous Decision Making",
      "Tool & API Integration",
      "Goal-Oriented Task Execution",
      "Real-Time Adaptive Learning",
    ],
  },
  stats: [
    { value: "30+", label: "AI Engagements" },
    { value: "9+", label: "Years Engineering" },
    { value: "5+", label: "Industries Served" },
    { value: "25+", label: "AI Deployments" },
  ],
  overview: {
    title: "Build Autonomous AI That Works for You",
    description:
      "Most AI tools respond when you ask them something. Agentic AI understands a goal, breaks it into steps, and executes — across your APIs, databases, and tools — without you having to manage every move. We design and build agentic systems that work reliably in production, with the guardrails and human-in-the-loop controls that real deployments demand.",
    image:
      "https://images.unsplash.com/photo-1485827404703-89b55fcc595e?w=800&q=80",
  },
  serviceTypes: [
    {
      title: "Multi-Agent Systems",
      description:
        "When one agent isn't enough. We design coordinated agent networks where specialised agents collaborate, delegate, and check each other's work to solve complex, multi-stage problems.",
      icon: "Users",
      features: [
        "Agent Orchestration",
        "Task Distribution",
        "Inter-Agent Communication",
        "Collective Intelligence",
      ],
    },
    {
      title: "Tool Integration",
      description:
        "Agents are only as powerful as the tools they can reach. We connect your agents to your real systems — CRMs, databases, APIs, internal tools — so they take meaningful action, not just generate text.",
      icon: "Plug",
      features: [
        "API Connectivity",
        "Database Operations",
        "Third-Party Services",
        "Custom Tool Development",
      ],
    },
    {
      title: "Autonomous Reasoning",
      description:
        "We build agents that don't just pattern-match — they break down problems, evaluate options, and work through multi-step reasoning to arrive at the right next action.",
      icon: "Brain",
      features: [
        "Chain-of-Thought",
        "Problem Decomposition",
        "Decision Trees",
        "Contextual Analysis",
      ],
    },
    {
      title: "Task Planning",
      description:
        "A goal without a plan is just a wish. Our agents decompose high-level objectives into concrete, sequenced steps — and adapt the plan when something unexpected happens.",
      icon: "ClipboardList",
      features: [
        "Goal Setting",
        "Step Planning",
        "Resource Allocation",
        "Priority Management",
      ],
    },
    {
      title: "Memory & Context",
      description:
        "Agents that remember what matters. We implement long-term memory, session context, and knowledge retrieval so your agents get smarter with every interaction — without retraining.",
      icon: "Database",
      features: [
        "Long-Term Memory",
        "Session Context",
        "Knowledge Retrieval",
        "Conversation History & Context Retrieval",
      ],
    },
    {
      title: "Safety & Guardrails",
      description:
        "Autonomous doesn't mean unchecked. Every agentic system we build includes output validation, boundary enforcement, audit logging, and human escalation paths — because production AI needs to be trustworthy, not just clever.",
      icon: "Shield",
      features: [
        "Action Validation",
        "Boundary Enforcement",
        "Human-in-the-Loop",
        "Audit Logging",
      ],
    },
  ],
  useCases: [
    {
      title: "Clinical Documentation Automation",
      description:
        "We built an agentic system for a healthcare provider that reads clinical video recordings and consultation notes, then autonomously generates structured patient treatment plans. Work that took specialists 45+ minutes — done in seconds, with built-in validation before it reaches the clinician.",
      icon: "FileText",
    },
    {
      title: "AI-Native Product Builder",
      description:
        "Our team is currently powering an 8-person dedicated build of a Lovable-style platform — where users describe what they want to build in plain English and the system generates working, deployable applications. Multi-agent architecture, code generation, test writing, and validation pipelines, all orchestrated end-to-end.",
      icon: "Code",
    },
    {
      title: "Anti-Hallucination Guardrail System",
      description:
        "We designed and built a guardrail layer for a production AI product that validates, filters, and constrains LLM outputs before they reach users — making it safe to deploy conversational AI in environments where a wrong answer has real consequences.",
      icon: "ShieldCheck",
    },

    {
      title: "Customer Service Agents",
      description:
        "Autonomous agents handling inquiries, resolving issues, and escalating when needed",
      icon: "Headphones",
    },
    {
      title: "Research Assistants",
      description:
        "Agents that gather, analyze, and synthesize information from multiple sources",
      icon: "Search",
    },
    {
      title: "Process Automation",
      description:
        "Intelligent automation of complex workflows across multiple systems",
      icon: "Settings",
    },
    {
      title: "Personal Assistants",
      description:
        "AI agents managing schedules, communications, and task prioritization",
      icon: "Calendar",
    },
    {
      title: "Data Analysis Agents",
      description:
        "Agents that connect to your live data, run analysis autonomously, and surface the insights that matter — without waiting for someone to build a report.",
      icon: "BarChart",
    },
    {
      title: "Code Generation",
      description:
        "AI agents that write, review, and iterate on code — accelerating development cycles and letting your engineers focus on architecture and product, not boilerplate.",
      icon: "Code",
    },
  ],
  technologies: [
    {
      name: "LangChain",
      icon: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/python/python-original.svg",
    },
    {
      name: "OpenAI GPT",
      icon: TEAM_IMAGES.openai,
    },
    {
      name: "AutoGPT",
      icon: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/python/python-original.svg",
    },
    {
      name: "CrewAI",
      icon: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/python/python-original.svg",
    },
    {
      name: "Python",
      icon: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/python/python-original.svg",
    },
    {
      name: "FastAPI",
      icon: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/fastapi/fastapi-original.svg",
    },
    {
      name: "Redis",
      icon: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/redis/redis-original.svg",
    },
    {
      name: "PostgreSQL",
      icon: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/postgresql/postgresql-original.svg",
    },
    {
      name: "Docker",
      icon: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/docker/docker-original.svg",
    },
    {
      name: "AWS",
      icon: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/amazonwebservices/amazonwebservices-original-wordmark.svg",
    },
  ],
  faq: [
    {
      question: "What makes Agentic AI different from traditional automation?",
      answer:
        "Unlike rule-based automation, agentic AI can reason about problems, plan multi-step solutions, adapt to changing circumstances, and learn from outcomes. Agents can handle novel situations without explicit programming for every scenario.",
    },
    {
      question: "How do you ensure agent safety and reliability?",
      answer:
        "We implement comprehensive guardrails including action validation, boundary enforcement, human-in-the-loop approval for critical decisions, extensive logging, and fail-safe mechanisms. Agents are designed with safety as a core principle.",
    },
    {
      question: "Can agents integrate with our existing systems?",
      answer:
        "Absolutely. Our agents are designed to work with any system that has an API. We create custom tool integrations for databases, CRMs, ERPs, communication platforms, and any other business systems you use.",
    },
    {
      question: "What's the typical timeline for deploying an AI agent?",
      answer:
        "Simple single-purpose agents can be deployed in 4-6 weeks. Complex multi-agent systems with extensive integrations typically take 3-6 months. We follow an iterative approach, delivering value incrementally.",
    },
  ],
};

// AI PRODUCTS DATA
export const AI_PRODUCTS_DATA = {
  hero: {
    title: "AI Products",
    subtitle: "From Strong Idea to Shipped Product — We Go the Full Distance.",
    features: [
      "Product Strategy & Ideation",
      "MVP Development & Validation",
      "Full Product Engineering",
      "Market Launch & Scaling",
      "Continuous Improvement",
    ],
  },
  stats: [
    { value: "6+", label: "Dedicated Teams" },
    { value: "2-4", label: "Weeks to MVP" },
    { value: "9+", label: "Years Engineering" },
    { value: "25+", label: "AI Deployments" },
  ],
  overview: {
    title: "Transform Ideas into Market-Ready AI Products",
    description:
      "Building an AI product isn't just an API call and a nice UI. It takes product thinking, deep engineering, and real experience with how AI behaves when actual users hit it at scale. We’ve seen firsthand how edge cases, latency, and model drift can make or break user trust if not handled early. That’s why we focus on building resilient, production-ready systems from day one—not just prototypes.",
    image:
      "https://images.unsplash.com/photo-1559028012-481c04fa702d?w=800&q=80",
  },
  serviceTypes: [
    {
      title: "Product Strategy",
      description:
        "We help you define what to build, who it's for, and how it wins. Market research, competitive positioning, value proposition, and revenue model — before a line of code is written.",
      icon: "Target",
      features: [
        "Market Research",
        "Competitive Analysis",
        "Value Proposition",
        "Revenue Model Design",
      ],
    },
    {
      title: "MVP Development",
      description:
        "Get to market fast with a focused, production-quality MVP. We build the core experience, get it in front of real users, and iterate on what we learn — typically in 6-8 weeks.",
      icon: "Rocket",
      features: [
        "Core Feature Set",
        "Rapid Prototyping",
        "User Testing",
        "Feedback Integration",
      ],
    },
    {
      title: "Product Engineering",
      description:
        "Once validated, we scale. Full-stack development, LLM orchestration pipelines, API layers, DevOps — production-grade architecture built to grow with your user base.",
      icon: "Cog",
      features: [
        "Scalable Architecture",
        "LLM Orchestration Pipeline",
        "API Development",
        "DevOps Integration",
      ],
    },
    {
      title: "UX/UI Design",
      description:
        "AI is only as good as the experience around it. We design interfaces that make complex AI behaviour feel simple, intuitive, and trustworthy to end users.",
      icon: "Palette",
      features: [
        "User Research",
        "Interface Design",
        "Prototyping",
        "Usability Testing",
      ],
    },
    {
      title: "Quality Assurance",
      description:
        "We test the full stack — functional testing, performance under load, security audits, and AI-specific validation to catch hallucinations and edge cases before they reach users.",
      icon: "CheckCircle",
      features: [
        "Automated Testing",
        "Performance Testing",
        "Security Audits",
        "AI Output Validation",
      ],
    },
    {
      title: "Launch & Scale",
      description:
        "Launch isn't the finish line — it's the starting gun. We support go-live, monitor production, onboard early users, and optimise based on real usage data.",
      icon: "TrendingUp",
      features: [
        "Deployment Strategy",
        "Performance Monitoring",
        "User Onboarding",
        "Growth Optimization",
      ],
    },
  ],
  process: [
    {
      phase: "Discovery",
      description: "Understand your vision, market opportunity, and user needs",
      duration: "1-2 Weeks",
    },
    {
      phase: "Strategy",
      description: "Define product roadmap, features, and success metrics",
      duration: "2-3 Weeks",
    },
    {
      phase: "MVP Build",
      description: "Rapidly develop core features for market validation",
      duration: "6-8 Weeks",
    },
    {
      phase: "Validation",
      description: "Test with real users and iterate based on feedback",
      duration: "2-4 Weeks",
    },
    {
      phase: "Full Development",
      description: "Build production-ready product with all features",
      duration: "3-6 Months",
    },
    {
      phase: "Launch & Scale",
      description: "Deploy, monitor, and optimize for growth",
      duration: "Ongoing",
    },
  ],
  technologies: [
    {
      name: "Python",
      icon: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/python/python-original.svg",
    },
    {
      name: "TensorFlow",
      icon: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/tensorflow/tensorflow-original.svg",
    },
    {
      name: "PyTorch",
      icon: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/pytorch/pytorch-original.svg",
    },
    {
      name: "React",
      icon: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/react/react-original.svg",
    },
    {
      name: "Node.js",
      icon: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/nodejs/nodejs-original.svg",
    },
    {
      name: "AWS",
      icon: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/amazonwebservices/amazonwebservices-original-wordmark.svg",
    },
    {
      name: "GCP",
      icon: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/googlecloud/googlecloud-original.svg",
    },
    {
      name: "Docker",
      icon: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/docker/docker-original.svg",
    },
    {
      name: "Kubernetes",
      icon: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/kubernetes/kubernetes-plain.svg",
    },
    {
      name: "MongoDB",
      icon: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/mongodb/mongodb-original.svg",
    },
  ],
  faq: [
    {
      question: "How do you approach AI product development differently?",
      answer:
        "We combine deep AI/ML expertise with product thinking. Unlike pure engineering firms, we focus on user value, market fit, and business outcomes—not just technical implementation. Every decision is guided by what will make the product successful in the market.",
    },
    {
      question: "What if we only have a rough idea for an AI product?",
      answer:
        "That's the perfect starting point. Our discovery phase helps refine rough concepts into concrete product visions. We'll work with you to validate the idea, identify the right AI approach, and create a clear roadmap to bring it to life.",
    },
    {
      question: "How do you ensure the AI product will find market fit?",
      answer:
        "We build validation into every phase. Starting with user research, we test assumptions early with MVPs, gather continuous feedback, and iterate rapidly. This lean approach minimizes risk and ensures we're building something users actually want.",
    },
    {
      question: "What ongoing support do you provide after launch?",
      answer:
        "Product launch is just the beginning. We offer comprehensive post-launch support including monitoring, optimization, feature updates, model retraining, and scaling assistance. Many clients continue partnering with us for ongoing product evolution.",
    },
  ],
};

// SMART AI CHATBOT DATA
export const SMART_CHATBOT_DATA = {
  hero: {
    title: "Smart AI Chatbot",
    subtitle: "Let Your Users Talk to Their Data.",
    features: [
      "Natural Language Understanding",
      "Multi-Channel Deployment",
      "Seamless System Integration",
      "Continuous Learning",
      "24/7 Availability",
    ],
  },
  stats: [
    { value: "3+", label: "Chatbot Products" },
    { value: "Realtime", label: "Live Data Connected" },
    { value: "Guardrails", label: "Built-in" },
    { value: "RAG", label: "Vector Database" },
  ],
  overview: {
    title: "Transform Customer Engagement with AI Chatbots",
    description:
      "Forget bots that answer FAQs from a static list. We build MCP-powered chatbots that connect to your live business data — CRMs, databases, APIs, internal tools — and give users real, contextual answers in real time. With anti-hallucination guardrails built in, you get the power of conversational AI without the production risk.",
    image:
      "https://images.unsplash.com/photo-1531746790731-6c087fecd65a?w=800&q=80",
  },
  serviceTypes: [
    {
      title: "MCP-Connected Data Chatbots",
      description:
        "Our flagship chatbot capability. Built on Model Context Protocol (MCP), these bots connect directly to your databases, APIs, and internal systems. Users ask questions in plain language and get answers from your actual live data — inventory levels, customer records, analytics, documents, anything",
      icon: "Database",
      features: [
        "Live Database Queries",
        "API Tool Calling",
        "Dynamic Data Responses",
        "Structured Output Formatting",
      ],
    },

    {
      title: "Customer Support Bots",
      description: "Handle inquiries, resolve issues, and escalate seamlessly",
      icon: "Headphones",
      features: [
        "Intent Recognition",
        "Issue Resolution",
        "Smart Escalation",
        "Ticket Integration",
      ],
    },
    {
      title: "Sales & Lead Gen",
      description:
        "Qualify leads, answer questions, and guide purchase decisions",
      icon: "TrendingUp",
      features: [
        "Lead Qualification",
        "Product Recommendations",
        "Appointment Booking",
        "CRM Integration",
      ],
    },
    {
      title: "Internal Assistants",
      description:
        "Give your team a single interface to query internal systems, HR docs, IT resources, and business data — in plain language, with accurate answers from live sources",
      icon: "Briefcase",
      features: [
        "Knowledge Base",
        "HR Queries",
        "IT Support",
        "Process Guidance",
      ],
    },
    {
      title: "E-commerce Bots",
      description: "Enhance shopping with personalized assistance",
      icon: "ShoppingCart",
      features: [
        "Product Search",
        "Order Tracking",
        "Returns Processing",
        "Recommendations",
      ],
    },
    {
      title: "Healthcare Bots",
      description: "Support patients with information and scheduling",
      icon: "Heart",
      features: [
        "Symptom Checker",
        "Appointment Booking",
        "Medication Reminders",
        "HIPAA Compliant",
      ],
    },
    {
      title: "Multi-Language Support",
      description: "Engage global audiences in their preferred language",
      icon: "Globe",
      features: [
        "50+ Languages",
        "Real-Time Translation",
        "Cultural Adaptation",
        "Regional Variants",
      ],
    },
  ],
  channels: [
    { name: "Website", icon: "Globe" },
    { name: "WhatsApp", icon: "MessageCircle" },
    { name: "Facebook Messenger", icon: "Facebook" },
    { name: "Slack", icon: "Slack" },
    { name: "Microsoft Teams", icon: "Briefcase" },
    { name: "Mobile Apps", icon: "Smartphone" },
  ],
  technologies: [
    {
      name: "OpenAI GPT",
      icon: TEAM_IMAGES.openai,
    },
    {
      name: "Dialogflow",
      icon: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/googlecloud/googlecloud-original.svg",
    },
    {
      name: "Python",
      icon: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/python/python-original.svg",
    },
    {
      name: "Node.js",
      icon: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/nodejs/nodejs-original.svg",
    },
    {
      name: "React",
      icon: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/react/react-original.svg",
    },
    {
      name: "MongoDB",
      icon: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/mongodb/mongodb-original.svg",
    },
    {
      name: "Redis",
      icon: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/redis/redis-original.svg",
    },
    {
      name: "AWS",
      icon: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/amazonwebservices/amazonwebservices-original-wordmark.svg",
    },
    {
      name: "Socket.io",
      icon: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/socketio/socketio-original.svg",
    },
    {
      name: "Docker",
      icon: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/docker/docker-original.svg",
    },
  ],
  faq: [
    {
      question:
        "How intelligent are your AI chatbots compared to rule-based bots?",
      answer:
        "Our chatbots use advanced LLMs and NLP to understand intent, context, and nuance. They can handle variations in phrasing, remember conversation history, and provide intelligent responses—far beyond the rigid scripted flows of traditional rule-based bots.",
    },
    {
      question: "Can the chatbot integrate with our existing systems?",
      answer:
        "Yes, seamless integration is a core capability. We connect chatbots to CRMs (Salesforce, HubSpot), helpdesks (Zendesk, Freshdesk), ERPs, databases, and any system with an API. The bot can pull data, create records, and trigger workflows.",
    },
    {
      question: "How do you handle queries the bot can't answer?",
      answer:
        "We design intelligent escalation flows. The bot recognizes when it can't help and smoothly hands off to human agents with full conversation context. We also continuously improve the bot by learning from escalated queries.",
    },
    {
      question: "What analytics and insights do you provide?",
      answer:
        "Our chatbots come with comprehensive analytics dashboards showing conversation volumes, resolution rates, common topics, user satisfaction, and AI performance metrics. These insights help optimize both the bot and your overall customer experience.",
    },
  ],
};

// AI STUDIO DATA
export const AI_STUDIO_DATA = {
  hero: {
    title: "AI Studio",
    subtitle:
      "Experiment, prototype, and validate AI concepts — before you commit to full-scale development.",
    features: [
      "Pre-Built AI Templates",
      "Collaborative Workspace",
      "Model Training & Tuning",
      "Visualization Tools",
      "Expert Guidance",
    ],
  },
  stats: [
    { value: "10+", label: "Pre-Built Templates" },
    { value: "10x", label: "Faster Prototyping" },
    { value: "25+", label: "Supported Models" },
    { value: "Priority", label: "Expert Support" },
  ],
  overview: {
    title: "Accelerate AI Innovation with Our Studio",
    description:
      "AI Studio is our hands-on environment where organizations move from AI curiosity to validated concepts — fast. Access pre-built templates, collaborate with our AI team, train custom models, and visualize results, all in one place. Whether you're exploring what's possible or accelerating a proof of concept, AI Studio shortens the path from idea to evidence.",
    image:
      "https://images.unsplash.com/photo-1518770660439-4636190af475?w=800&q=80",
  },
  features: [
    {
      title: "Data Integration",
      description:
        "Seamlessly connect and manage structured and unstructured data from sources",
      icon: "Code",
    },
    {
      title: "Pre-Built Templates",
      description:
        "Ready-to-use templates for common AI use cases across industries",
      icon: "Copy",
    },
    {
      title: "Real-time Experimentation",
      description:
        "Test ideas instantly with rapid iterations and immediate performance feedback loops",
      icon: "Cpu",
    },
    {
      title: "Visualization Tools",
      description:
        "Interactive dashboards and charts to understand your data and models",
      icon: "BarChart",
    },
    {
      title: "Collaboration",
      description:
        "Work together with your team and our experts in shared workspaces",
      icon: "Users",
    },
    {
      title: "Version Control",
      description:
        "Track experiments, compare results, and manage model versions",
      icon: "GitBranch",
    },
  ],
  useCases: [
    "AI Proof of Concepts",
    "Rapid Prototyping",
    "Data Exploration",
    "3rd Party Integration",
    "Team Training",
    "Hackathons",
  ],
  technologies: [
    {
      name: "Jupyter",
      icon: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/jupyter/jupyter-original.svg",
    },
    {
      name: "Python",
      icon: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/python/python-original.svg",
    },
    {
      name: "TensorFlow",
      icon: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/tensorflow/tensorflow-original.svg",
    },
    {
      name: "PyTorch",
      icon: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/pytorch/pytorch-original.svg",
    },
    {
      name: "Pandas",
      icon: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/pandas/pandas-original.svg",
    },
    {
      name: "NumPy",
      icon: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/numpy/numpy-original.svg",
    },
    {
      name: "scikit-learn",
      icon: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/python/python-original.svg",
    },
    {
      name: "Plotly",
      icon: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/plotly/plotly-original.svg",
    },
  ],
};

// DEDICATED CAPABILITY CENTRE DATA
export const CAPABILITY_CENTRE_DATA = {
  hero: {
    title: "Dedicated Capability Centre",
    subtitle:
      "A dedicated engineering team that works like yours — without the cost of building one.",
    features: [
      "Dedicated Resources",
      "Full Knowledge Transfer",
      "Scalable Team Size",
      "Direct Communication",
      "Long-Term Partnership",
    ],
  },
  stats: [
    { value: "3+", label: "Active Centres" },
    { value: "100+", label: "Engineers Deployed" },
    { value: "98%", label: "Client Retention" },
    { value: "5+", label: "Avg Years Partnership" },
  ],
  overview: {
    title: "An Extension of Your Team",
    description:
      "Our Dedicated Capability Centre gives you a fully committed engineering team — working exclusively on your project, integrated into your workflows, and aligned with your goals. From day one, the team operates as a seamless extension of your organization, adapting to your processes and culture. Unlike traditional outsourcing, there's no hand-off disconnect. You get the focus and continuity of an in-house team, at a fraction of the cost.",
    image:
      "https://images.unsplash.com/photo-1497366216548-37526070297c?w=800&q=80",
  },
  benefits: [
    {
      title: "Dedicated Team",
      description:
        "Handpicked engineers and specialists working exclusively for you",
      icon: "Users",
    },
    {
      title: "Cost Efficiency",
      description:
        "Reduce engineering costs by 40-60% compared to local hiring — without compromising on quality or delivery speed.",
      icon: "DollarSign",
    },
    {
      title: "Scalability",
      description: "Quickly scale team size up or down based on project needs",
      icon: "TrendingUp",
    },
    {
      title: "Knowledge Continuity",
      description:
        "Deep domain expertise preserved through long-term engagement",
      icon: "Brain",
    },
    {
      title: "Direct Control",
      description:
        "You manage priorities and workflows as if team were in-house",
      icon: "Settings",
    },
    {
      title: "24/7 Operations",
      description:
        "Leverage time zone differences for round-the-clock productivity",
      icon: "Clock",
    },
  ],
  teamComposition: [
    { role: "Project Managers", count: "1-2" },
    { role: "Tech Leads", count: "1-2" },
    { role: "Senior Developers", count: "3-5" },
    { role: "Mid-Level Developers", count: "5-10" },
    { role: "QA Engineers", count: "2-4" },
    { role: "DevOps Engineers", count: "1-2" },
    { role: "UI/UX Designers", count: "1-2" },
  ],
};

// STARTUP LAB DATA
export const STARTUP_LAB_DATA = {
  hero: {
    title: "Startup Lab",
    subtitle: "Where Founders Build, Launch, and Scale — Faster.",
    features: [
      "Technical Mentorship",
      "MVP Development",
      "Funding Guidance",
      "Go-to-Market Strategy",
      "Network Access",
    ],
  },
  stats: [
    { value: "175+", label: "Startups Supported" },
    { value: "75%", label: "Success Rate" },
    { value: "15-30", label: "Days to MVP" },
    { value: "350+", label: "Idea Validations" },
  ],
  overview: {
    title: "Where Startup Dreams Become Reality",
    description:
      "Startup Lab is our dedicated program for founders who need more than a dev shop. We're co-builders — helping you validate your idea, ship an MVP, prepare for investors, and scale when you're ready. We bring product thinking, technical expertise, and startup experience to navigate early-stage challenges effectively. From first sprint to Series A, we're with you every step.",
    image:
      "https://images.unsplash.com/photo-1559136555-9303baea8ebd?w=800&q=80",
  },
  services: [
    {
      title: "Idea Validation",
      description:
        "Test and refine your concept with market research and user feedback",
      icon: "Lightbulb",
    },
    {
      title: "MVP Development",
      description: "Rapid build of core product to validate market fit",
      icon: "Rocket",
    },
    {
      title: "Technical Architecture",
      description: "Design scalable systems that grow with your startup",
      icon: "Layers",
    },
    {
      title: "Fundraising Support",
      description: "Pitch deck preparation and investor introductions",
      icon: "DollarSign",
    },
    {
      title: "Growth Strategy",
      description: "Go-to-market planning and growth hacking techniques",
      icon: "TrendingUp",
    },
    {
      title: "Mentorship",
      description: "Access to experienced founders and technical advisors",
      icon: "Users",
    },
  ],
  programs: [
    {
      name: "Sprint Program",
      duration: "4-6 Weeks",
      description: "Intensive MVP development for early-stage startups",
      includes: ["Idea Refinement", "MVP Build", "User Testing", "Pitch Prep"],
    },
    {
      name: "Scale Program",
      duration: "3-6 Months",
      description: "Full product development with growth support",
      includes: [
        "Product Engineering",
        "Team Extension",
        "Fundraising Support",
        "Growth Strategy",
      ],
    },
    {
      name: "Partnership",
      duration: "Ongoing",
      description:
        "Long-term technology partnership with flexible commercial models, including revenue-share options",
      includes: [
        "Dedicated Team",
        "CTO-as-a-Service",
        "Strategic Advisory",
        "Network Access",
      ],
    },
  ],
};

// PRODUCT ENGINEERING PAGE DATA
export const PRODUCT_ENGINEERING_DATA = {
  hero: {
    title: "Product Engineering",
    subtitle:
      "We don't just write code — we build products people use, at a scale that grows with your business.",
    features: [
      "Full-Stack Development",
      "Cloud-Native Architecture",
      "Agile Methodology",
      "DevOps & CI/CD",
      "Scalable Solutions",
    ],
  },
  stats: [
    { value: "200+", label: "Products Built" },
    { value: "99.9%", label: "Uptime SLA" },
    { value: "50%", label: "Faster Delivery" },
    { value: "100+", label: "Engineers" },
  ],
  overview: {
    title: "Build Products That Scale",
    description:
      "We combine engineering depth with product thinking to build software that actually works in the market — not just in staging. We prioritize scalability, performance, and real-world usability to ensure long-term success across evolving business needs. From MVP to enterprise platforms, we own the full engineering lifecycle: architecture, development, cloud, DevOps, security, and data.",
    image:
      "https://images.unsplash.com/photo-1522071820081-009f0129c71c?w=800&q=80",
  },
  services: [
    {
      title: "Architecture Design",
      description: "Scalable, maintainable system architecture",
      icon: "Layers",
      features: [
        "Microservices",
        "Event-Driven",
        "Cloud-Native",
        "High Availability",
      ],
    },
    {
      title: "Full-Stack Development",
      description: "Frontend and backend excellence",
      icon: "Code",
      features: [
        "React/Vue/Angular",
        "Node.js/Python/Java",
        "REST/GraphQL",
        "Real-time Apps",
      ],
    },
    {
      title: "Cloud Engineering",
      description: "Deploy and scale on any cloud",
      icon: "Cloud",
      features: [
        "AWS/Azure/GCP",
        "Kubernetes",
        "Serverless",
        "Infrastructure as Code",
      ],
    },
    {
      title: "DevOps & Automation",
      description: "Streamline delivery and operations",
      icon: "Settings",
      features: [
        "CI/CD Pipelines",
        "Container Orchestration",
        "Monitoring",
        "Auto-Scaling",
      ],
    },
    {
      title: "Database Engineering",
      description: "Data storage that performs",
      icon: "Database",
      features: ["SQL/NoSQL", "Data Modeling", "Optimization", "Migration"],
    },
    {
      title: "Security Engineering",
      description: "Build security into every layer",
      icon: "Shield",
      features: [
        "Secure Code",
        "Penetration Testing",
        "Compliance",
        "Encryption",
      ],
    },
  ],
  technologies: [
    {
      name: "React",
      icon: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/react/react-original.svg",
    },
    {
      name: "Node.js",
      icon: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/nodejs/nodejs-original.svg",
    },
    {
      name: "Python",
      icon: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/python/python-original.svg",
    },
    {
      name: "TypeScript",
      icon: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/typescript/typescript-original.svg",
    },
    {
      name: "AWS",
      icon: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/amazonwebservices/amazonwebservices-original-wordmark.svg",
    },
    {
      name: "Docker",
      icon: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/docker/docker-original.svg",
    },
    {
      name: "Kubernetes",
      icon: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/kubernetes/kubernetes-plain.svg",
    },
    {
      name: "PostgreSQL",
      icon: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/postgresql/postgresql-original.svg",
    },
  ],
  faq: [
    {
      question: "What development methodologies do you use?",
      answer:
        "We primarily use Agile/Scrum with 2-week sprints, but we adapt to your preferred methodology. Our teams are experienced in Kanban, SAFe, and hybrid approaches depending on project needs.",
    },
    {
      question: "How do you ensure code quality?",
      answer:
        "We maintain high standards through code reviews, automated testing (unit, integration, e2e), static analysis, and CI/CD pipelines. Every commit goes through our quality gates before merging.",
    },
    {
      question: "Can you work with our existing codebase?",
      answer:
        "Absolutely. We regularly take over existing projects, modernize legacy systems, and integrate with existing architectures. We start with a thorough code audit and create a roadmap for improvements.",
    },
    {
      question: "What about post-launch support?",
      answer:
        "We offer flexible support packages including 24/7 monitoring, bug fixes, feature development, and infrastructure management. Many clients transition to our managed services for ongoing support.",
    },
  ],
};

// WEB APP DEVELOPMENT PAGE DATA
export const WEB_APP_DATA = {
  hero: {
    title: "Web App Development",
    subtitle:
      "Fast, secure web applications that convert visitors into customers — and scale when they do.",
    features: [
      "Responsive Design",
      "Progressive Web Apps",
      "Single Page Applications",
      "E-commerce Platforms",
      "CMS Integration",
    ],
  },
  stats: [
    { value: "200+", label: "Web Apps Built" },
    { value: "100+", label: "Integrations" },
    { value: "99%", label: "Client Satisfaction" },
    { value: "20M+", label: "Users Served" },
  ],
  overview: {
    title: "Web App Development",
    description:
      "Whether you need a high-conversion marketing site, a data-heavy SaaS platform, or a complex enterprise web app, we build with performance, security, and scale in mind — using modern frameworks and production-grade best practices. Our approach emphasizes clean architecture, modular design, and seamless integrations to ensure flexibility as your product evolves.",
    image:
      "https://images.unsplash.com/photo-1547658719-da2b51169166?w=800&q=80",
  },
  services: [
    {
      title: "Frontend Development",
      description: "Beautiful, responsive interfaces",
      icon: "Layout",
      features: [
        "React/Vue/Angular",
        "Responsive Design",
        "Accessibility",
        "Animations",
      ],
    },
    {
      title: "Backend Development",
      description: "Powerful server-side solutions",
      icon: "Server",
      features: [
        "PHP/RoR",
        "Node.js/Python/Java",
        "REST/GraphQL APIs",
        "Authentication",
        "Caching",
      ],
    },
    {
      title: "E-commerce",
      description: "Online stores that sell",
      icon: "ShoppingCart",
      features: [
        "Custom Platforms",
        "Shopify/WooCommerce",
        "Payment Integration",
        "Inventory",
      ],
    },
    {
      title: "CMS Solutions",
      description: "Content management made easy",
      icon: "FileText",
      features: [
        "Headless CMS",
        "WordPress/Drupal",
        "Custom CMS",
        "Multi-language",
      ],
    },
    {
      title: "Progressive Web Apps",
      description: "App-like web experiences",
      icon: "Smartphone",
      features: [
        "Offline Support",
        "Push Notifications",
        "App Install",
        "Fast Loading",
      ],
    },
    {
      title: "Web Optimization",
      description: "Speed and performance tuning",
      icon: "Zap",
      features: ["Core Web Vitals", "SEO", "CDN Setup", "Image Optimization"],
    },
  ],
  technologies: [
    {
      name: "React",
      icon: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/react/react-original.svg",
    },
    {
      name: "Vue.js",
      icon: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/vuejs/vuejs-original.svg",
    },
    {
      name: "Next.js",
      icon: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/nextjs/nextjs-original.svg",
    },
    {
      name: "Node.js",
      icon: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/nodejs/nodejs-original.svg",
    },
    {
      name: "TypeScript",
      icon: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/typescript/typescript-original.svg",
    },
    {
      name: "Tailwind CSS",
      icon: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/tailwindcss/tailwindcss-original.svg",
    },
    {
      name: "MongoDB",
      icon: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/mongodb/mongodb-original.svg",
    },
    {
      name: "PostgreSQL",
      icon: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/postgresql/postgresql-original.svg",
    },
  ],
  faq: [
    {
      question: "How long does it take to build a web application?",
      answer:
        "Timeline depends on complexity. A simple website takes 4-6 weeks, while complex web applications can take 3-6 months. We provide detailed estimates after understanding your requirements.",
    },
    {
      question: "Do you provide hosting and maintenance?",
      answer:
        "Yes, we offer complete hosting solutions on AWS, Azure, or GCP with managed maintenance packages that include updates, security patches, backups, and 24/7 monitoring.",
    },
    {
      question: "Can you redesign our existing website?",
      answer:
        "Absolutely. We handle complete redesigns, from UX research and new designs to implementation. We can also do incremental improvements while keeping your current site running.",
    },
    {
      question: "How do you handle SEO?",
      answer:
        "SEO is built into our development process. We implement technical SEO best practices, optimize for Core Web Vitals, ensure proper meta tags, and can integrate with your content strategy.",
    },
  ],
};

// API DEVELOPMENT PAGE DATA
export const API_DEVELOPMENT_DATA = {
  hero: {
    title: "API Development",
    subtitle: "Robust APIs That Power Your Digital Ecosystem.",
    features: [
      "RESTful APIs",
      "GraphQL Services",
      "API Gateway Setup",
      "Third-Party Integrations",
      "Comprehensive Documentation",
    ],
  },
  stats: [
    { value: "500+", label: "APIs Built" },
    { value: "99.99%", label: "Uptime" },
    { value: "10ms", label: "Avg Response" },
    { value: "1B+", label: "Requests/Month" },
  ],
  overview: {
    title: "APIs That Enable Growth",
    description:
      "We design and build APIs that developers actually enjoy using — secure, well-documented, versioned, and built to handle real production loads. We ensure consistency, reliability, and clear standards so teams can integrate and scale with confidence. Whether it's internal microservices, partner integrations, or a public developer platform, we get the fundamentals right.",
    image:
      "https://images.unsplash.com/photo-1516321318423-f06f85e504b3?w=800&q=80",
  },
  services: [
    {
      title: "REST API Development",
      description: "Clean, intuitive RESTful services",
      icon: "Server",
      features: ["Resource Design", "Versioning", "Pagination", "Filtering"],
    },
    {
      title: "GraphQL Services",
      description: "Flexible queries, efficient data fetching",
      icon: "GitBranch",
      features: ["Schema Design", "Resolvers", "Subscriptions", "Federation"],
    },
    {
      title: "API Gateway",
      description: "Centralized API management",
      icon: "Shield",
      features: [
        "Rate Limiting",
        "Authentication",
        "Load Balancing",
        "Caching",
      ],
    },
    {
      title: "Microservices",
      description: "Distributed service architecture",
      icon: "Boxes",
      features: [
        "Service Mesh",
        "Event-Driven",
        "Container-Ready",
        "Auto-Scaling",
      ],
    },
    {
      title: "Integration APIs",
      description: "Connect to third-party services",
      icon: "Link",
      features: ["OAuth/OIDC", "Webhooks", "Data Sync", "Error Handling"],
    },
    {
      title: "API Documentation",
      description: "Developer-friendly docs",
      icon: "FileText",
      features: ["OpenAPI/Swagger", "Interactive Docs", "Code Samples", "SDKs"],
    },
  ],
  technologies: [
    {
      name: "Node.js",
      icon: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/nodejs/nodejs-original.svg",
    },
    {
      name: "Python",
      icon: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/python/python-original.svg",
    },
    {
      name: "GraphQL",
      icon: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/graphql/graphql-plain.svg",
    },
    {
      name: "Redis",
      icon: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/redis/redis-original.svg",
    },
    {
      name: "Kong",
      icon: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/linux/linux-original.svg",
    },
    {
      name: "Docker",
      icon: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/docker/docker-original.svg",
    },
    {
      name: "AWS",
      icon: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/amazonwebservices/amazonwebservices-original-wordmark.svg",
    },
    {
      name: "PostgreSQL",
      icon: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/postgresql/postgresql-original.svg",
    },
  ],
  faq: [
    {
      question: "REST or GraphQL - which should we choose?",
      answer:
        "It depends on your use case. REST is great for simple CRUD operations and caching. GraphQL excels when clients need flexible queries or you have multiple frontends with different data needs. We often recommend both for different scenarios.",
    },
    {
      question: "How do you handle API security?",
      answer:
        "Security is built-in from day one. We implement OAuth 2.0/JWT authentication, rate limiting, input validation, encryption in transit and at rest, and comprehensive audit logging. We also conduct security reviews and penetration testing.",
    },
    {
      question: "Can you help with API versioning strategy?",
      answer:
        "Yes, we design APIs with versioning in mind. We typically use URL versioning (v1, v2) for major changes and header-based versioning for minor updates, ensuring backward compatibility and smooth migrations.",
    },
    {
      question: "Do you provide API monitoring?",
      answer:
        "We set up comprehensive monitoring including uptime checks, response time tracking, error rate alerts, and usage analytics. We integrate with tools like DataDog, New Relic, or can build custom dashboards.",
    },
  ],
};

// IOT APP DEVELOPMENT PAGE DATA
export const IOT_APP_DATA = {
  hero: {
    title: "IoT App Development",
    subtitle:
      "From device firmware to cloud dashboards — full-stack IoT solutions for the physical world.",
    features: [
      "Device Connectivity",
      "Edge Computing",
      "Cloud Platforms",
      "Real-Time Dashboards",
      "Predictive Analytics",
    ],
  },
  stats: [
    { value: "100K+", label: "Devices Connected" },
    { value: "15+", label: "IoT Projects" },
    { value: "99.9%", label: "Data Accuracy" },
    { value: "5ms", label: "Latency" },
  ],
  overview: {
    title: "Bridge the Physical and Digital",
    description:
      "We build IoT systems that bridge physical hardware and digital intelligence — covering everything from device firmware and edge computing to cloud platforms and real-time analytics. We ensure seamless communication, scalability, and reliability across distributed environments and connected ecosystems. Whether it's industrial sensors, smart infrastructure, or consumer connected devices, we handle the full stack.",
    image:
      "https://images.unsplash.com/photo-1518770660439-4636190af475?w=800&q=80",
  },
  services: [
    {
      title: "Device Integration",
      description: "Connect any device to your platform",
      icon: "Cpu",
      features: [
        "Protocol Support",
        "Firmware Development",
        "Hardware Abstraction",
        "Secure Boot",
      ],
    },
    {
      title: "Edge Computing",
      description: "Process data at the edge",
      icon: "Server",
      features: [
        "Local Processing",
        "Data Filtering",
        "Offline Capability",
        "Edge AI",
      ],
    },
    {
      title: "IoT Platforms",
      description: "Cloud infrastructure for IoT",
      icon: "Cloud",
      features: [
        "AWS IoT/Azure IoT",
        "Device Management",
        "Data Ingestion",
        "Rules Engine",
      ],
    },
    {
      title: "Real-Time Dashboards",
      description: "Visualize device data instantly",
      icon: "BarChart",
      features: [
        "Live Monitoring",
        "Custom Widgets",
        "Alerts",
        "Mobile Access",
      ],
    },
    {
      title: "Predictive Maintenance",
      description: "AI-powered failure prediction",
      icon: "Settings",
      features: [
        "Anomaly Detection",
        "Health Scoring",
        "Maintenance Scheduling",
        "Cost Optimization",
      ],
    },
    {
      title: "Industrial IoT",
      description: "Manufacturing and industry solutions",
      icon: "Factory",
      features: [
        "OPC-UA/MQTT",
        "SCADA Integration",
        "Asset Tracking",
        "Quality Control",
      ],
    },
  ],
  technologies: [
    {
      name: "AWS IoT",
      icon: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/amazonwebservices/amazonwebservices-original-wordmark.svg",
    },
    {
      name: "Azure IoT",
      icon: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/azure/azure-original.svg",
    },
    {
      name: "Python",
      icon: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/python/python-original.svg",
    },
    {
      name: "Node.js",
      icon: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/nodejs/nodejs-original.svg",
    },
    {
      name: "Arduino",
      icon: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/arduino/arduino-original.svg",
    },
    {
      name: "Raspberry Pi",
      icon: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/raspberrypi/raspberrypi-original.svg",
    },
    {
      name: "InfluxDB",
      icon: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/influxdb/influxdb-original.svg",
    },
    {
      name: "Grafana",
      icon: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/grafana/grafana-original.svg",
    },
  ],
  faq: [
    {
      question: "Which IoT protocols do you support?",
      answer:
        "We support all major protocols including MQTT, CoAP, HTTP/REST, WebSocket, Modbus, OPC-UA, BLE, Zigbee, and LoRaWAN. We select protocols based on your specific requirements for bandwidth, power, and latency.",
    },
    {
      question: "How do you handle IoT security?",
      answer:
        "IoT security is paramount. We implement secure boot, encrypted communications (TLS), device authentication, firmware signing, and secure OTA updates. We follow IoT security frameworks like NIST and OWASP IoT guidelines.",
    },
    {
      question: "Can you work with our existing hardware?",
      answer:
        "Yes, we can integrate with virtually any connected device. We assess your hardware capabilities and develop appropriate firmware or gateway solutions to connect legacy and new devices to your platform.",
    },
    {
      question: "How do you handle large-scale deployments?",
      answer:
        "We design for scale from day one using cloud-native architectures. Our solutions handle millions of devices through auto-scaling infrastructure, efficient data pipelines, and distributed processing at the edge.",
    },
  ],
};

// CUSTOM INTEGRATIONS PAGE DATA
export const CUSTOM_INTEGRATIONS_DATA = {
  hero: {
    title: "Custom Integrations",
    subtitle: "Connect Your Systems, Unlock Your Data.",
    features: [
      "API Integrations",
      "ETL Pipelines",
      "Real-Time Sync",
      "Legacy System Integration",
      "Data Migration",
    ],
  },
  stats: [
    { value: "180+", label: "Systems Integrated" },
    { value: "1K+", label: "APIs Connected" },
    { value: "1TB+", label: "Data Migrated" },
    { value: "99.9%", label: "Sync Accuracy" },
  ],
  overview: {
    title: "Break Down Data Silos",
    description:
      "Most businesses run on 5-15 disconnected systems. CRMs that don't talk to ERPs. Marketing tools that don't sync with databases. We fix that — building reliable, secure integrations that eliminate manual work, reduce errors, and give you one source of truth. Our approach ensures real-time data flow, system interoperability, and long-term scalability without disrupting your existing operations or workflows.",
    image:
      "https://images.unsplash.com/photo-1558494949-ef010cbdcc31?w=800&q=80",
  },
  services: [
    {
      title: "API Integration",
      description: "Connect any application via API",
      icon: "Link",
      features: [
        "REST/GraphQL",
        "OAuth Setup",
        "Error Handling",
        "Rate Limiting",

        "CIBIL",
        "Hunter",
        "Cersai ",
      ],
    },
    {
      title: "ETL Pipelines",
      description: "Extract, transform, load data reliably",
      icon: "Database",
      features: [
        "Data Extraction",
        "Transformation",
        "Scheduled Jobs",
        "Monitoring",
      ],
    },
    {
      title: "Real-Time Sync",
      description: "Keep systems in sync instantly",
      icon: "RefreshCw",
      features: [
        "Event-Driven",
        "Webhooks",
        "Change Tracking",
        "Conflict Resolution",
      ],
    },
    {
      title: "Legacy Integration",
      description: "Connect modern apps to legacy systems",
      icon: "Layers",
      features: ["SOAP/XML", "File-Based", "Screen Scraping", "Middleware"],
    },
    {
      title: "iPaaS Solutions",
      description: "Integration platform implementation",
      icon: "Cloud",
      features: ["MuleSoft", "Workato", "Zapier", "Custom Platforms"],
    },
    {
      title: "Data Migration",
      description: "Move data safely between systems",
      icon: "ArrowRight",
      features: [
        "Data Mapping",
        "Validation",
        "Rollback Plans",
        "Zero Downtime",
      ],
    },
  ],
  technologies: [
    {
      name: "MuleSoft",
      icon: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/salesforce/salesforce-original.svg",
    },
    {
      name: "Apache Kafka",
      icon: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/apachekafka/apachekafka-original.svg",
    },
    {
      name: "Python",
      icon: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/python/python-original.svg",
    },
    {
      name: "Node.js",
      icon: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/nodejs/nodejs-original.svg",
    },
    {
      name: "PostgreSQL",
      icon: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/postgresql/postgresql-original.svg",
    },
    {
      name: "Redis",
      icon: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/redis/redis-original.svg",
    },
    {
      name: "AWS",
      icon: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/amazonwebservices/amazonwebservices-original-wordmark.svg",
    },
    {
      name: "Docker",
      icon: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/docker/docker-original.svg",
    },
  ],
  faq: [
    {
      question: "Which systems can you integrate?",
      answer:
        "We can integrate virtually any system with an API or data export capability. Common integrations include Salesforce, SAP, Oracle, Microsoft Dynamics, HubSpot, Shopify, custom databases, and hundreds more.",
    },
    {
      question: "How do you handle data security during integration?",
      answer:
        "Security is paramount. We use encrypted connections, secure credential storage, implement least-privilege access, and maintain detailed audit logs. For sensitive data, we can implement tokenization and data masking.",
    },
    {
      question: "What if our system has no API?",
      answer:
        "We have strategies for API-less systems: database direct connections, file-based integration, screen scraping for legacy apps, or building custom APIs. We assess each situation and recommend the best approach.",
    },
    {
      question: "How long do integration projects take?",
      answer:
        "Simple API integrations can be done in 1-2 weeks. Complex enterprise integrations with multiple systems and data transformations typically take 2-4 months. We provide detailed timelines after assessing your requirements.",
    },
  ],
};

// IT SUPPORT SERVICES PAGE DATA
export const IT_SUPPORT_DATA = {
  hero: {
    title: "IT Support Services",
    subtitle: "24/7 Support to Keep Your Business Running.",
    features: [
      "24/7 Helpdesk",
      "Remote & On-Site Support",
      "Infrastructure Management",
      "Security Monitoring",
      "Disaster Recovery",
    ],
  },
  stats: [
    { value: "15min", label: "Avg Response Time" },
    { value: "95%", label: "First-Call Resolution" },
    { value: "99.9%", label: "Uptime Maintained" },
    { value: "24/7", label: "Availability" },
  ],
  overview: {
    title: "IT Support You Can Rely On",
    description:
      "We take IT off your plate — from helpdesk tickets to infrastructure management and security monitoring. Our team integrates seamlessly with your IT department, handles day-to-day operations, and proactively identifies and resolves issues before they impact business performance. We also ensure compliance, system reliability, and continuous optimization, keeping your systems healthy 24/7, so your team can stay focused on the work that matters.",
    image:
      "https://images.unsplash.com/photo-1553877522-43269d4ea984?w=800&q=80",
  },
  services: [
    {
      title: "Helpdesk Support",
      description: "Responsive user support",
      icon: "Headphones",
      features: [
        "Ticket Management",
        "Multi-Channel",
        "Knowledge Base",
        "SLA Tracking",
      ],
    },
    {
      title: "Infrastructure Management",
      description: "Keep systems healthy",
      icon: "Server",
      features: [
        "Server Management",
        "Network Admin",
        "Cloud Management",
        "Patching",
      ],
    },
    {
      title: "Security Operations",
      description: "Protect your assets 24/7",
      icon: "Shield",
      features: [
        "Threat Monitoring",
        "Incident Response",
        "Vulnerability Scans",
        "Compliance",
      ],
    },
    {
      title: "Application Support",
      description: "Keep apps running smoothly",
      icon: "Settings",
      features: ["Bug Fixes", "Performance Tuning", "Updates", "User Training"],
    },
    {
      title: "Backup & Recovery",
      description: "Protect your data",
      icon: "Database",
      features: [
        "Automated Backups",
        "Disaster Recovery",
        "Testing",
        "Fast Restore",
      ],
    },
    {
      title: "Vendor Management",
      description: "Handle third-party relationships",
      icon: "Users",
      features: [
        "License Management",
        "Contract Negotiation",
        "Escalation",
        "Coordination",
      ],
    },
  ],
  supportTiers: [
    {
      tier: "Essential",
      features: [
        "8x5 Support",
        "Email & Phone",
        "4hr Response",
        "Basic Monitoring",
      ],
      price: "Starting $2k/mo",
    },
    {
      tier: "Professional",
      features: [
        "24x7 Support",
        "All Channels",
        "1hr Response",
        "Advanced Monitoring",
      ],
      price: "Starting $5k/mo",
    },
    {
      tier: "Enterprise",
      features: [
        "24x7 Priority",
        "Dedicated Team",
        "15min Response",
        "Full Management",
      ],
      price: "Custom Pricing",
    },
  ],
  faq: [
    {
      question: "What are your support hours?",
      answer:
        "We offer flexible support hours from 8x5 business hours to full 24x7x365 coverage. Our Enterprise tier includes after-hours emergency support with guaranteed 15-minute response times.",
    },
    {
      question: "How do you handle on-site support?",
      answer:
        "For issues requiring physical presence, we dispatch certified technicians to your location. Response times and coverage areas are defined in your SLA. We also partner with local providers for extended geographic coverage.",
    },
    {
      question: "Can you support our custom applications?",
      answer:
        "Yes, we can support custom applications once we complete a knowledge transfer. We document your systems, create runbooks, and train our team. We can also coordinate with your development team for complex issues.",
    },
    {
      question: "How do you ensure service quality?",
      answer:
        "We track comprehensive KPIs including response time, resolution time, customer satisfaction, and SLA compliance. Monthly reports keep you informed, and we continuously improve based on metrics and feedback.",
    },
  ],
};

// PRODUCT MANAGEMENT ADVISORY DATA
export const PRODUCT_MANAGEMENT_DATA = {
  hero: {
    title: "Product Management Advisory",
    subtitle:
      "Expert product leadership — without the full-time CPO price tag.",
    features: [
      "Product Strategy",
      "Roadmap Planning",
      "Market Analysis",
      "Feature Prioritization",
      "Stakeholder Alignment",
    ],
  },
  stats: [
    { value: "150+", label: "Products Advised" },
    { value: "3x", label: "Avg Revenue Growth" },
    { value: "50%", label: "Faster Time-to-Market" },
    { value: "15+ Years", label: "Leadership Experience" },
  ],
  overview: {
    title: "Product Leadership When You Need It",
    description:
      "Not every company needs a full-time CPO — but every product needs direction. Our product advisors bring the strategic clarity, prioritization discipline, and stakeholder alignment that turns a good idea into a product that wins in market. We also help define metrics, validate assumptions, and ensure consistent execution across teams and product stages. Interim leadership or ongoing advisory, we adapt to what you need.",
    image:
      "https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=800&q=80",
  },
  services: [
    {
      title: "Product Strategy",
      description: "Define your product vision and direction",
      icon: "Target",
      features: [
        "Vision & Mission",
        "Positioning",
        "Competitive Analysis",
        "Pricing Strategy",
      ],
    },
    {
      title: "Roadmap Development",
      description: "Plan your product evolution",
      icon: "Map",
      features: [
        "Feature Planning",
        "Release Strategy",
        "Dependencies",
        "Timeline",
      ],
    },
    {
      title: "Market Research",
      description: "Understand your market deeply",
      icon: "Search",
      features: [
        "User Research",
        "Competitor Analysis",
        "Market Sizing",
        "Trend Analysis",
      ],
    },
    {
      title: "Feature Prioritization",
      description: "Focus on what matters most",
      icon: "ListOrdered",
      features: [
        "Impact Analysis",
        "RICE/ICE Scoring",
        "Trade-off Decisions",
        "Backlog Management",
      ],
    },
    {
      title: "Stakeholder Management",
      description: "Align everyone on product goals",
      icon: "Users",
      features: [
        "Executive Alignment",
        "Team Communication",
        "Customer Feedback",
        "Board Reporting",
      ],
    },
    {
      title: "Metrics & Analytics",
      description: "Measure what drives success",
      icon: "BarChart",
      features: [
        "KPI Definition",
        "Dashboard Setup",
        "A/B Testing",
        "Growth Metrics",
      ],
    },
  ],
  engagementModels: [
    {
      model: "Advisory Retainer",
      description: "Ongoing strategic guidance",
      hours: "10-20 hrs/month",
      ideal: "Established products needing strategic input",
    },
    {
      model: "Interim CPO",
      description: "Full-time product leadership",
      hours: "Full-time",
      ideal: "Leadership gaps or transitions",
    },
    {
      model: "Project-Based",
      description: "Specific deliverables",
      hours: "Varies",
      ideal: "Strategy documents, roadmaps, launches",
    },
  ],
  faq: [
    {
      question: "What experience do your advisors have?",
      answer:
        "Our product advisors have 10-20+ years of experience leading products at startups and Fortune 500 companies. They have launched successful products across B2B, B2C, SaaS, and marketplace models.",
    },
    {
      question: "How do you work with existing product teams?",
      answer:
        "We complement your team, not replace it. We work alongside PMs to provide mentorship, frameworks, and strategic perspective. Our goal is to elevate your team capabilities while delivering immediate value.",
    },
    {
      question: "Can you help with product launches?",
      answer:
        "Absolutely. We have extensive experience with go-to-market planning, launch coordination, beta programs, and post-launch optimization. We can advise or take hands-on roles depending on your needs.",
    },
    {
      question: "How do you measure advisory success?",
      answer:
        "We define success metrics upfront based on your goals—revenue growth, user adoption, NPS improvement, or time-to-market reduction. We track progress monthly and adjust our approach to maximize impact.",
    },
  ],
};

// EXPERIENCE DESIGN DATA
export const EXPERIENCE_DESIGN_DATA = {
  hero: {
    title: "Experience Design",
    subtitle: "User-Centered Design That Delights and Converts.",
    features: [
      "User Research",
      "UI/UX Design",
      "Prototyping & Testing",
      "Design Systems",
      "Accessibility",
    ],
  },
  stats: [
    { value: "200+", label: "Products Designed" },
    { value: "40%", label: "Avg Conversion Lift" },
    { value: "4.8/5", label: "User Satisfaction" },
    { value: "100%", label: "Design Consistency" },
  ],
  overview: {
    title: "Design That Drives Results",
    description:
      "Great design isn't just how it looks — it's how it works and how it converts. Our UX/UI team designs with data and user research at the core, crafting interfaces that are intuitive, accessible, and built to drive measurable engagement and conversion. We continuously test, iterate, and refine experiences based on real user behavior to maximize usability, retention, and overall business outcomes.",
    image:
      "https://images.unsplash.com/photo-1561070791-2526d30994b5?w=800&q=80",
  },
  services: [
    {
      title: "User Research",
      description: "Understand your users deeply",
      icon: "Search",
      features: [
        "Interviews",
        "Surveys",
        "Analytics Review",
        "Persona Development",
      ],
    },
    {
      title: "UX Design",
      description: "Craft seamless user journeys",
      icon: "Layout",
      features: [
        "Information Architecture",
        "User Flows",
        "Wireframes",
        "Interaction Design",
      ],
    },
    {
      title: "UI Design",
      description: "Beautiful, functional interfaces",
      icon: "Palette",
      features: [
        "Visual Design",
        "Responsive Layouts",
        "Micro-interactions",
        "Iconography",
      ],
    },
    {
      title: "Prototyping",
      description: "Test ideas before building",
      icon: "Smartphone",
      features: [
        "Interactive Prototypes",
        "Click-through Demos",
        "Animation Specs",
        "Developer Handoff",
      ],
    },
    {
      title: "Usability Testing",
      description: "Validate with real users",
      icon: "Users",
      features: [
        "Moderated Tests",
        "A/B Testing",
        "Heatmaps",
        "Session Recording",
      ],
    },
    {
      title: "Design Systems",
      description: "Scale design consistently",
      icon: "Grid",
      features: [
        "Component Library",
        "Style Guide",
        "Documentation",
        "Governance",
      ],
    },
  ],
  process: [
    { phase: "Discover", description: "Research users, business, and market" },
    { phase: "Define", description: "Synthesize insights into opportunities" },
    { phase: "Design", description: "Create solutions through iteration" },
    { phase: "Deliver", description: "Hand off with complete specifications" },
  ],
  technologies: [
    {
      name: "Figma",
      icon: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/figma/figma-original.svg",
    },
    {
      name: "Sketch",
      icon: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/sketch/sketch-original.svg",
    },
    {
      name: "Adobe XD",
      icon: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/xd/xd-plain.svg",
    },
    {
      name: "InVision",
      icon: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/figma/figma-original.svg",
    },
    {
      name: "Framer",
      icon: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/framer/framer-original.svg",
    },
    {
      name: "Miro",
      icon: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/figma/figma-original.svg",
    },
  ],
  faq: [
    {
      question: "How do you approach a new design project?",
      answer:
        "We start with discovery—understanding your users, business goals, and constraints. Then we move through definition (personas, journey maps), design (wireframes to high-fidelity), and delivery (specs, assets, support).",
    },
    {
      question: "Do you only design or also implement?",
      answer:
        "We offer both. Our design team creates the experience while our development team can implement it. We also work with your developers, providing detailed specs and supporting during implementation.",
    },
    {
      question: "How do you ensure designs are accessible?",
      answer:
        "Accessibility is built into our process. We design for WCAG 2.1 AA compliance, conduct accessibility audits, and test with assistive technologies. We believe good design is inclusive design.",
    },
    {
      question: "What deliverables do we receive?",
      answer:
        "Typical deliverables include research reports, personas, journey maps, wireframes, high-fidelity designs in Figma, interactive prototypes, design system documentation, and developer handoff specs.",
    },
  ],
};

export const INDUSTRY_ICONS = {
  energy: Zap,
  "healthcare-medical": Stethoscope,
  "fintech-finance": Banknote,
  retail: ShoppingBag,
  transport: Car,
  "artificial-intelligence": Brain,
  "ecommerce-gig-commerce": ShoppingCart,
  utility: Wrench,
  logistics: Truck,
  "sales-marketing": TrendingUp,
  security: Shield,
  "edtech-education-training": GraduationCap,
  agritech: Leaf,
  "jobs-hr-tech": Briefcase,
  legal: Scale,
  "networking-business": Network,
  sports: Trophy,
  lifestyle: Sun,
  fitness: Dumbbell,
  "charity-funding": Heart,
};

// QUALITY ASSURANCE TESTING DATA
export const QA_TESTING_DATA = {
  hero: {
    title: "Quality Assurance & Testing",
    subtitle:
      "Ship with confidence — comprehensive QA and testing that catches issues before your users do.",
    features: [
      "Manual Testing",
      "Automated Testing",
      "Performance Testing",
      "Security Testing",
      "Mobile Testing",
    ],
  },
  stats: [
    { value: "99.5%", label: "Bug Detection Rate" },
    { value: "60%", label: "Cost Reduction" },
    { value: "200+", label: "Projects Tested" },
    { value: "24hr", label: "Regression Cycles" },
  ],
  overview: {
    title: "Quality You Can Trust",
    description:
      "QA isn't a final checkpoint — it's built into every sprint. Our testers work alongside your developers from day one, running automated and manual test suites across functional, performance, security, and mobile testing to ship software you can stand behind. We focus on early defect detection, continuous integration, and comprehensive coverage to reduce risk, improve release confidence, and ensure consistent product quality across every deployment cycle.",
    image:
      "https://images.unsplash.com/photo-1576091160550-2173dba999ef?w=800&q=80",
  },
  services: [
    {
      title: "Manual Testing",
      description: "Expert exploratory and scripted testing",
      icon: "Search",
      features: [
        "Exploratory Testing",
        "Test Case Execution",
        "Bug Reporting",
        "Regression Testing",
      ],
    },
    {
      title: "Test Automation",
      description: "Scalable automated test suites",
      icon: "Code",
      features: [
        "UI Automation",
        "API Testing",
        "CI/CD Integration",
        "Framework Development",
      ],
    },
    {
      title: "Performance Testing",
      description: "Ensure speed and stability",
      icon: "Zap",
      features: [
        "Load Testing",
        "Stress Testing",
        "Scalability Analysis",
        "Bottleneck Identification",
      ],
    },
    {
      title: "Security Testing",
      description: "Find vulnerabilities before attackers",
      icon: "Shield",
      features: [
        "Penetration Testing",
        "OWASP Top 10",
        "Code Review",
        "Compliance Testing",
      ],
    },
    {
      title: "Mobile Testing",
      description: "Quality across all devices",
      icon: "Smartphone",
      features: [
        "iOS/Android Testing",
        "Device Lab",
        "App Store Compliance",
        "Cross-Platform",
      ],
    },
    {
      title: "API Testing",
      description: "Verify your integrations work",
      icon: "Link",
      features: [
        "Functional Testing",
        "Contract Testing",
        "Load Testing",
        "Documentation Review",
      ],
    },
  ],
  technologies: [
    {
      name: "Selenium",
      icon: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/selenium/selenium-original.svg",
    },
    {
      name: "Playwright",
      icon: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/playwright/playwright-original.svg",
    },
    {
      name: "Cypress",
      icon: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/cypressio/cypressio-original.svg",
    },
    {
      name: "JMeter",
      icon: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/apache/apache-original.svg",
    },
    {
      name: "Postman",
      icon: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/postman/postman-original.svg",
    },
    {
      name: "Jest",
      icon: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/jest/jest-plain.svg",
    },
    {
      name: "Appium",
      icon: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/java/java-original.svg",
    },
    {
      name: "Jenkins",
      icon: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/jenkins/jenkins-original.svg",
    },
  ],
  faq: [
    {
      question: "Should we do manual or automated testing?",
      answer:
        "Both have their place. We recommend automation for regression, smoke tests, and repetitive scenarios. Manual testing is better for exploratory testing, UX validation, and complex business scenarios. Most projects benefit from a hybrid approach.",
    },
    {
      question: "How do you integrate with our CI/CD pipeline?",
      answer:
        "We design tests to integrate seamlessly with Jenkins, GitHub Actions, GitLab CI, or any CI/CD tool. Automated tests run on every commit or PR, providing immediate feedback to developers.",
    },
    {
      question: "What testing metrics do you track?",
      answer:
        "We track test coverage, defect detection rate, test execution time, automation ROI, defect density, and escape rate. Monthly reports show trends and help identify improvement opportunities.",
    },
    {
      question: "Can you test our mobile app?",
      answer:
        "Yes, we test native iOS, Android, and cross-platform apps. We have device labs with real devices and use cloud services for extended coverage. We test functionality, performance, security, and app store compliance.",
    },
  ],
};
// Combined services for backward compatibility
export const PRODUCT_ENGINEERING_SERVICES = [
  ...SERVICE_BUNDLES,
  ...INDIVIDUAL_SERVICES,
];
